/* Shared domain types.
 *
 * These mirror the API's DTOs, which are audience-specific by design: a field a
 * role may not see is ABSENT from its DTO, never nulled (AD-6). That is why
 * suppressible fields below are optional plus an explicit `hidden` marker —
 * `undefined` means "not in this audience's shape", and the marker lets the UI
 * render "Агент скрыт" rather than an empty cell, so suppression is never
 * misread as missing data.
 *
 * Money always arrives as a decimal STRING (AD-9). Never widen these to number.
 */

/* The one import this file has, and it points at a VOCABULARY rather than at a
   shape: lib/fileKinds owns the list of document kinds and the rule for
   narrowing an unknown one, and re-declaring the union here would be a second
   copy of a list that already has three. */
import type { FileKind } from '@/lib/fileKinds'

/** A role key.
 *
 * Roles are RUNTIME-DEFINABLE since migration 0021, which superseded AD-7's
 * fixed five. The union below is not a closed set any more — it names the five
 * roles the product ships with, which are the ones that have translations and
 * the ones the access matrix documents. `(string & {})` keeps those five as
 * editor autocomplete while accepting a key a founder invented this morning.
 *
 * Never switch on this to decide what to render. A custom role matches none of
 * the five arms, so a switch silently falls through to the narrowest branch —
 * which is why the client renders from `capabilities` instead. */
export type SeededRole = 'sales' | 'pricing' | 'operations' | 'finance' | 'super-admin'
export type Role = SeededRole | (string & {})

/** A capability key — one thing a caller may do, as the server spells it.
 *
 * Dotted and lowercase (`quote.create`, `case.close.financial`). The full list
 * is served by GET /api/admin/capabilities rather than declared here, because
 * the server's registry is the only authority on which ones exist. */
export type Capability = string

export interface CurrentUser {
  id: string
  name: string
  email: string
  role: Role
  /** Directory-ownership grants (FR-44).
   *
   *  DERIVED on the server from the capability set since migration 0021, and
   *  kept for the directory screens that already read it. New code should test
   *  `capabilities` with the `can()` helper instead. */
  grants: {
    manageClients?: boolean
    manageParticipants?: boolean
    manageTransport?: boolean
  }
  /** Everything this account may do, resolved server-side on this request.
   *
   *  This is what the interface renders from — not the role name. A founder can
   *  create a role the client has never heard of, so "is this user Finance" is
   *  not a question with a useful answer any more; "may this user issue an
   *  invoice" is.
   *
   *  It is an AFFORDANCE, never an authorization. The server re-checks every
   *  one of these on the request that acts on it, so hiding a button is a
   *  courtesy and never a control (AD-21, FR-56). */
  capabilities: Capability[]
  /** True when this account holds the superuser role, which passes every check
   *  including capabilities this build has never heard of. */
  isSuperuser: boolean
  language: 'en' | 'ru'
  /** A short-lived signed link to the profile photo (FR-58, AD-18), absent when
   *  the account has none — which is the ordinary state, and why the interface
   *  falls back to initials rather than treating this as missing data.
   *
   *  The URL, never the object key: a key is the input to a signature and the
   *  server never serves one. It expires in five minutes and is re-minted on
   *  every session read, so it is never persisted anywhere on this side. */
  avatarUrl?: string
  /** True while the account still holds a credential somebody else generated —
   *  a create, an admin reset, or the seed. Always sent.
   *
   *  An ENFORCEMENT switch on the server, not a hint: while it is true every
   *  authenticated route except `GET /api/session` and
   *  `PUT /api/session/password` answers 403 `password-change-required`.
   *  `RequireSession` reads it to send the reader to `/change-password` before
   *  the shell can fire a request that would be refused. */
  mustChangePassword: boolean
  /** Whether this account is shown counter-agent identity — a carrier, a
   *  driver, a driver's phone (S-10). Always sent; computed server-side by
   *  `suppress.SeesCounterparties(perms)` — whether the account holds
   *  `counterparty.read` (S-22c) — the same call that suppresses those fields
   *  in every DTO this account reads (AD-6).
   *
   *  The leg page reads it to decide whether a carrier, driver or phone COLUMN
   *  exists at all — a column of «скрыто» markers is still a column advertising
   *  what is withheld. Anything but `true` hides them. It follows the grant, not
   *  the role name: a custom role granted `counterparty.read` reads `true`, a
   *  seeded role whose grant was removed reads `false`. Never a role name
   *  compared here (roles are runtime records). The wire name is historical. */
  seesAgentIdentity: boolean
  /** The scopable surfaces this account's role REACHES — any level at all —
   *  sorted (#129, N-08). Names only, never a level. Always sent by the server
   *  (`[]` for a role at «Нет доступа» on every one); the superuser is served
   *  all of them.
   *
   *  Read through `reaches()` (`lib/can.ts`) and nowhere else: the menu leaves
   *  out a section the role does not reach, and its page refuses a deep link.
   *  Optional here only so a hand-built session without it type-checks — and
   *  such a session reaches nothing, which fails closed. An affordance, never
   *  an authorization: the server scopes every read regardless. */
  surfaces?: string[]
}

/** A money value carries its currency; aggregates are USD at booked rates. */
export interface Money {
  amount: string
  currency: string
}

/** A field the viewer's role may not see. Rendered with an explicit marker so
 *  missing data is never mistaken for unentered data (AD-6, FR-29). */
export interface Suppressed {
  hidden: true
  reason: 'role'
}

/** Either a value, or an explicit marker that the role may not see it. */
export type Maybe<T> = T | Suppressed

export function isSuppressed<T>(value: Maybe<T> | undefined): value is Suppressed {
  return typeof value === 'object' && value !== null && 'hidden' in value
}

export type TransportMode = 'sea' | 'rail' | 'road' | 'air' | 'multimodal'

/* ── Quote ────────────────────────────────────────────────────────────────── */

/** The wire's own vocabulary (domain.go Status + the computed DisplayExpired).
 *
 * `rejected`, never the showcase-era `declined`, and no `awaiting-client` —
 * the server has no such state; "waiting on the client" IS `sent`. `expired` is
 * display-only: computed from the validity date at read time, never stored, so
 * nothing can ever be moved INTO it (AD-24).
 *
 * THERE IS NO `approved`. The client's yes creates the case (FR-19), so
 * `converted` is what an accepted offer reads as — a quote cannot be accepted
 * and caseless at the same time. The state existed until migration 0050 and
 * meant exactly that impossible thing. */
export type QuoteStatus =
  | 'draft'
  | 'sent'
  | 'rejected'
  | 'expired'
  | 'converted'

/** Where the request for a price stands on a quote (S-2a, owner decision O1)
 *  — `quote/pricing.go` PricingState. Stored, never computed:
 *  - `none`: nobody has asked, or the request was withdrawn;
 *  - `requested`: a colleague is pricing it, or a price-relevant edit reopened
 *    a ready price;
 *  - `ready`: the price is ready to send. */
export type PricingState = 'none' | 'requested' | 'ready'

/** The `pricing` block on EVERY QuoteDTO, list rows included, and the whole
 *  response of the three pricing acts (`quote/pricing_dto.go` PricingDTO).
 *
 *  `state` is always served; every other key is OMITTED when unset, never null
 *  — `{"state":"none"}` is a quote nobody asked about. Names, not ids, for who
 *  asked and who priced: the server serves no id it has no filter for. The
 *  assignee's id IS served, because the «Мои задачи» page (S-2b) filters the
 *  list on it. Read-only: nothing here travels back on the PUT. */
export interface QuotePricing {
  state: PricingState
  /** Who is pricing it — the colleague asked, or whoever marked it ready
   *  unasked. */
  assigneeId?: string
  assigneeName?: string
  /** RFC 3339 UTC; formatted for the reader through `useFormat`.
   *  When `reopenCauses` is present, `requestedAt` / `requestedByName` are the
   *  RESET's when and who — the colleague whose edit sent the price back — not
   *  somebody who asked. */
  requestedAt?: string
  requestedByName?: string
  readyAt?: string
  readyByName?: string
  /** Why a ready price went back to `requested` (N-11, migration 0083): the
   *  `PricingReopenCause` codes of the edit, in the server's order. Present
   *  only on a requested quote reached by a reset; absent on a request.
   *  A string list rather than the closed union, because a later server may
   *  send a code this client does not know — it renders as «другое». */
  reopenCauses?: string[]
  /** True when a price-relevant edit by THIS reader would send a ready price
   *  back to `requested` — computed per reader on the server
   *  (`quote.ReopensPricing`), so the id of who priced it never has to be
   *  served. Absent when false: on none and requested, and for the pricer. */
  reopensOnEdit?: boolean
}

/** The kinds of edit that reset a ready price (N-11) —
 *  `quote/pricing.go` ReopenCause, stored in `quotes.pricing_reopen_causes`. */
export type PricingReopenCause =
  | 'cargo'
  | 'route-option'
  | 'transport-mode'
  | 'pickup-location'
  | 'incoterm'
  | 'transit-days'
  | 'measures'

/** One contact at the quote's own client, as the header's contact picker
 *  offers it — `quote.ClientContactDTO`, `GET /api/quotes/:id/client-contacts`.
 *
 *  Served through the QUOTE's row scope rather than the directory's: a quote's
 *  owner is not always the client's (a client reassigned since), and the
 *  directory's scoped detail read answers that reader 404. Every key is always
 *  served, in the directory's order (primary first). No address on purpose —
 *  the picker names a person, and the send resolves the address server-side.
 *  `isDefault` marks the one an offer with no contact named is addressed to
 *  (quote.firstAddressable): at most one is true, and none when no contact
 *  carries an address. */
export interface QuoteClientContact {
  id: string
  label: string
  isPrimary: boolean
  isDefault: boolean
}

export interface QuoteSummary {
  id: string
  number: string
  clientName: string
  status: QuoteStatus
  /** The directory record the quote is for (FR-39). */
  clientId: string
  /** The pricing state (S-2a, L9). REQUIRED: the server serves it on every
   *  QuoteDTO — the detail, the list rows and every write's response. */
  pricing: QuotePricing

  /* The lane, and the money it carries.
   *
   * Optional because the quote HEADER does not hold either. A route is a route
   * option (FR-3, Story 2.4) and the totals come off cost lines (FR-2, Story
   * 2.3); until those tables exist the server serves a header and these are
   * absent. Optional rather than defaulted to "" or 0: an empty origin is a
   * lane nobody has entered, and a sell of zero is a price. */
  origin?: string
  destination?: string
  mode?: TransportMode
  sell?: Money
  margin?: Money
  marginPercent?: string

  validUntil?: string
  quoteDate?: string

  /** The latest issued revision's letter ("A", "B") — the "Rev A" the card
   *  cites beside the quote date. List-only like `cargo`, and absent until the
   *  first send: the detail serves the revisions themselves (Story 2.6), and a
   *  never-sent quote has no version to cite. */
  revisionLabel?: string

  /** The SELECTED route option's mode as a directory code (Story 2.4),
   *  list-only beside origin/destination. The seam's withMode prefers it over
   *  the header's transportModeCode: the header says what the client asked
   *  for, the option says what is being offered. */
  routeModeCode?: string

  /** The owner's account id — `quote.QuoteDTO.ownerId`, always served.
   *
   *  The twin of `CaseSummary.ownerId`, read by `canSeeRecordChat` for the same
   *  reason: a quote's owner is seeded onto `quote_team` at create but can be
   *  unassigned afterwards, and the server still serves them the conversation. */
  ownerId?: string
  ownerName?: string
  /** The issuing entity the quote is offered under — `quote.QuoteDTO.entityId`,
   *  always served. Conversion registers the case under THIS entity (P0-2), so
   *  the confirmation names it; the name is resolved from `/settings/entities`
   *  rather than served here. Optional only for a stale cache or an older
   *  server. */
  entityId?: string
  /** False once sent: an issued quote changes by revision, never in place (FR-7). */
  editable?: boolean

  /* The rest of the FR-4 header, which the server has always served on QuoteDTO
   * and this type did not declare.
   *
   * Declared now because PUT /quotes/:id takes the WHOLE record: an edit that
   * cannot read these cannot resubmit them, and the handler reads an absent key
   * as "clear it" — so a header form built against the old type would have wiped
   * every field below on its first save. They were invisible rather than absent,
   * which is the worse of the two: nothing failed, the data simply left.
   *
   * All optional and all `omitempty` on the wire, so absent means the quote has
   * none rather than "not served here". */
  clientContactId?: string
  cargoOwner?: string
  cargoDescription?: string
  /** The directory reference (FR-43). `mode` above is the resolved code the
   *  card renders; this is the id an edit has to send back. */
  transportModeId?: string
  transportModeCode?: string
  pickupLocation?: string
  /** FR-23's range, stored as two numbers and rendered "20–25 days". */
  transitDaysMin?: number
  transitDaysMax?: number
  /** FR-4: informational only; assigns no vendor to any cost line. */
  carrierDisplay?: string
  /** FR-11's deal terms. */
  demurrageTerms?: string
  deliveryTerms?: string
  /** FR-59, printed only when filled. */
  incoterm?: string

  /** What the quote is shipping, as the LIST serves it (dto.go CargoSummaryDTO).
   *
   *  Absent on the detail response, where the positions themselves arrive and
   *  this rounder answer would be a second version of the same fact. Absent is
   *  therefore "not computed here", not "no cargo" — the counts inside say the
   *  second, and a card must not read the two as the same thing. */
  cargo?: QuoteCargoSummary

  /* Three facts the LIST already carries, because `QuoteDetailDTO` embeds
   * `QuoteDTO` and the list handler serves that same shape (`ToQuoteDTOs`).
   * They sit on the summary rather than the detail for that reason: a type
   * that put them on the detail alone would describe a narrower response than
   * the server actually sends, and the case form reads `paymentTerms` off a
   * picked list row with no detail fetch. */

  /** The agreed terms as free text (`quote/dto.go` PaymentTerms). */
  paymentTerms?: string
  /** The credit term's day count (S-12, migration 0071), 1–365. Served only
   *  on a quote whose terms are `CREDIT` and only when one was given — a
   *  legacy CREDIT quote has none, and absent is "not stated", never zero.
   *  The conversion carries it onto the case as `credit` with these days. */
  paymentTermsCreditDays?: number

  /** The named person at the client, and where an offer would go (FR-71).
   *  Absent when the client has no contact carrying an email — which the send
   *  surface renders as a refusal rather than a blank address, because the fix
   *  is in the directory and not on that screen. */
  clientContactName?: string
  clientContactEmail?: string
}

/** The cargo aggregate a pipeline card renders (FR-3, FR-12). */
export interface QuoteCargoSummary {
  /** Zero on a header-only quote — which is NOT readiness to send: an empty
   *  quote passes every completeness rule vacuously (backend Sendable). */
  positions: number
  packages: number
  /** Set only when every position shares one type, so "40'HC × 2" never
   *  describes a third of a mixed quote. */
  packageType?: string
  /** How many positions carry an unmet requirement. A count: the card says the
   *  send is blocked, the quote says what would fix it. */
  blocked: number
}

/** What creating or amending a quote header sends (FR-4, FR-11, FR-23, FR-59). */
export interface QuotePayload {
  clientId: string
  clientContactId?: string
  cargoOwner?: string
  cargoDescription?: string
  transportModeId?: string
  pickupLocation?: string
  /** FR-23: a range stored as numbers, never as "20-25 days" text. */
  transitDaysMin?: number
  transitDaysMax?: number
  paymentTerms?: string
  /** S-12: tri-state on the header PUT — absent keeps the stored days, `null`
   *  clears them, a number sets them. Sent as the number while the terms are
   *  `CREDIT` and as `null` otherwise (the server clears them anyway when the
   *  terms move away). The server answers 422 `paymentTermsCreditDays`
   *  `required` / `invalid`. */
  paymentTermsCreditDays?: number | null
  /** FR-4: informational only; assigns no vendor to any cost line. */
  carrierDisplay?: string
  demurrageTerms?: string
  deliveryTerms?: string
  incoterm?: string
  /** The FR-13 header measures, as strings for the AD-9 reason the cargo
   *  measures are. An EMPTY STRING clears an override and returns the header
   *  to the computed sum — the ordinary way a user undoes a correction. */
  grossWeightKgOverride?: string
  volumeCbmOverride?: string
  chargeableWeightKg?: string
  /** FR-12: the client sent the final packing list, so the volumes are theirs
   *  rather than our estimate. Follows the quote onto its case. */
  finalPackingListReceived?: boolean
  /** Date-only, "YYYY-MM-DD" (AD-19). */
  quoteDate: string
  validUntil?: string
  /** The INTERNAL colleagues to staff, as user ids (migration 0028).
   *
   *  Ids only: the server resolves each name and role from the account, and
   *  sending them would let a caller record a colleague under a role they do
   *  not hold.
   *
   *  Read by the CREATE only: an absent list on a header edit cannot mean both
   *  "nobody" and "unchanged". The owner need not be listed — the server seeds
   *  them onto their own team in the create transaction.
   *
   *  Staff accounts only. A quote has no list of external parties since S-22a:
   *  the `participants` body field was retired with the quote's participants
   *  table (migration 0075), and a counterparty appears where it acts. */
  team?: string[]
}

/** One cargo position, with the FR-12/FR-13 completeness checklist. */
export interface CargoPosition {
  id: string
  description: string
  hsCode?: string
  /** Who ships THIS position (FR-12). Absent on the ordinary single-shipper
   *  quote; it earns its place on the consolidated load. */
  shipperName?: string
  packages: number
  packageType: string
  weightKg?: string
  volumeCbm?: string
  /** What the carrier bills when that is not the gross weight (FR-13).
   *  Entered, never derived: the volumetric divisor is the carrier's. */
  chargeableWeightKg?: string
  lengthCm?: string
  widthCm?: string
  heightCm?: string
  /** Chemical cargo adds MSDS and transport certificate to the checklist. */
  isChemical: boolean
  hasMsds?: boolean
  hasTransportCertificate?: boolean
}

/** What creating or amending a cargo position sends (FR-12, FR-13).
 *
 *  The measures are STRINGS, matching CargoPosition and for the same reason
 *  (AD-9): a volume through a JSON number is a float, and 22.4 comes back as
 *  22.399999999999999 on some clients. An empty string means "not measured" —
 *  the incomplete state the checklist reports, not an error. */
export interface CargoPayload {
  description: string
  hsCode?: string
  shipperName?: string
  packages: number
  packageType?: string
  weightKg?: string
  volumeCbm?: string
  chargeableWeightKg?: string
  lengthCm?: string
  widthCm?: string
  heightCm?: string
  isChemical: boolean
  hasMsds?: boolean
  hasTransportCertificate?: boolean
}

/** What conversion answers (Story 3.1, FR-19, FR-21).
 *
 *  The case's id and number, so the screen can send the user straight to the
 *  shipment it just created — FR-21's "reachable in one click" is answered by
 *  this response rather than by a second fetch. */
export interface ConversionResult {
  caseId: string
  caseNumber: string
  quoteId: string
  /** How many cost lines became planned expense lines. Zero is legitimate: an
   *  approved quote nobody priced still becomes a case. */
  plannedLines: number
  /** False when the quote carried no costing, so the case exists and needs a
   *  rate. Reported rather than hidden — a shipment that silently earns
   *  nothing is worse than one that says it has no price yet. */
  rateSet: boolean
  /** False when the selected route option carried no transport mode, so the
   *  case was created without its planned leg. */
  legCreated: boolean
}

/** The header's cargo measures (FR-13).
 *
 *  Both halves arrive: the figure that COUNTS (effective*) and the sum it may
 *  disagree with. An override the user cannot check against the arithmetic is
 *  a number with no provenance, so the screen shows both.
 *
 *  Nothing here is recomputed on the client. The server sums on every read,
 *  which is what makes the absent "recalculate" control honest (UX-DR-5) —
 *  there is never a stale total to refresh. */
export interface CargoTotals {
  /** The sums over the positions. Absent means NO position carries the
   *  measure, which is unknown rather than zero. */
  grossWeightKg?: string
  volumeCbm?: string
  chargeableWeightKg?: string
  /** The override where one was entered, the sum otherwise. */
  effectiveGrossWeightKg?: string
  effectiveVolumeCbm?: string
  effectiveChargeableWeightKg?: string
  /** Whether a human overrode the figure, so it reads as a correction. */
  grossWeightOverridden: boolean
  volumeOverridden: boolean
  /** The overrides AS ENTERED, which is what an EDIT has to resubmit.
   *
   *  PUT /quotes/:id takes the whole record and reads an empty measure as
   *  "clear the override", so a header form that could not prefill these would
   *  discard a human's correction on every save. They cannot all be recovered
   *  from the two fields above: weight and volume have an `overridden` flag, but
   *  the header chargeable weight is ENTERED rather than overriding a sum and
   *  has none, so `effectiveChargeableWeightKg` cannot be told apart from the
   *  positions' total. Absent means no override. */
  grossWeightKgOverride?: string
  volumeCbmOverride?: string
  chargeableWeightKgOverride?: string
  /** Whether the sum covers only some positions — real but incomplete. */
  weightPartial: boolean
  volumePartial: boolean
  /** The volumes are the client's rather than our estimate (FR-12). */
  finalPackingListReceived: boolean
}

/** What is missing. A closed set, so a locale missing a line fails to compile
 *  rather than rendering a raw code to a salesperson. */
export type ChecklistIssueCode =
  | 'dimensionsMissing'
  | 'volumeMissing'
  | 'msdsMissing'
  | 'certificateMissing'

/** What the gap prevents. Chemical cargo is held to a stricter rule, and the
 *  banner says so (FR-13). */
export type ChecklistBlockCode = 'send' | 'sendChemical'

/** A single unmet requirement blocking the offer from being sent.
 *
 *  Codes rather than sentences: the server decides WHICH issues exist and what
 *  each blocks, and this layer renders that into the reader's language. */
export interface ChecklistIssue {
  id: string
  /** What is missing, in the trade's words once rendered. */
  code: ChecklistIssueCode
  /** What it prevents. Stated in the banner, not at the moment of failure. */
  blocks: ChecklistBlockCode
  /** The number the user counts by. Absent when the row has no number yet. */
  position?: number
  positionId?: string
}

export interface CostLine {
  id: string
  description: string
  /** Buy side. Vendor is absent for Sales users (FR-29, AD-6). */
  buy: Money
  vendorName?: Maybe<string>
  /** The directory counterparty the vendor was picked from (migration 0044),
   *  or the empty string when the name was typed rather than chosen.
   *
   *  A `Maybe` for the same reason `vendorName` is, and suppressed by the same
   *  rule: the participants directory hides only AGENT rows from Sales, so an
   *  id they could resolve would hand back the name the line withholds. An
   *  unlinked line is suppressed too — see the server's CostLineDTO.VendorID —
   *  so this field never reveals whether a vendor is in the directory. */
  vendorId?: Maybe<string>
  sell: Money
  quantity: number
  /** The extended figures — unit price × quantity — computed by the SERVER.
   *  Never multiplied here: two surfaces that each do the arithmetic will
   *  eventually disagree by a rounding rule, and this is the number a price is
   *  decided on (FR-1). */
  buyTotal?: Money
  sellTotal?: Money
  /** The markup this line is priced at, over its cost, as a plain decimal
   *  string (S-5, L9) — computed by the SERVER, never here (L16).
   *
   *  The quote's stored markup when `atQuoteMarkup`; otherwise the line's own
   *  effective markup at two places. ABSENT (never null) on a line whose cost
   *  is zero and whose price is not, where no percentage of nothing exists. */
  markupPercent?: string
  /** Whether this line's price is exactly its cost at the quote's markup. Not
   *  omitempty on the wire: `false` is the flag that a line is priced
   *  differently — only a legacy draft can hold one (L10). */
  atQuoteMarkup: boolean
  /** The FR-17 rate memory: what this lane cost last time, and where. */
  rateMemory?: {
    amount: Money
    sourceCaseNumber: string
    month: string
  }
}

/** The costing footer (FR-1), computed server-side for the reason the
 *  checklist is: the margin a salesperson prices against must not depend on
 *  which screen they read it from. */
export interface CostTotals {
  buy: Money
  sell: Money
  margin: Money
  /** Margin over SELL, the convention the trade uses — a plain decimal string,
   *  not money, so it carries no currency. */
  marginPercent: string
  /** The costing's EFFECTIVE markup over cost (S-5, L9), computed by the
   *  server: the quote's stored markup while every line is at it, otherwise
   *  margin over cost at two places. ABSENT when the lines are mixed and the
   *  cost total is zero. */
  markupPercent?: string
  /** True when any line is not priced at the quote's markup — the figure
   *  above is then an average, and says «в среднем» (O6, L10). */
  markupMixed: boolean
}

/** What a cost-line write sends. The money stays STRINGS all the way through
 *  (AD-9): parsing to a float here is how 1234.55 becomes 1234.5499999999999.
 *
 *  `vendorName` is a plain string while it is a `Maybe` on CostLine, and that
 *  asymmetry is deliberate — suppression is a property of what the server
 *  SENDS, never of what a client submits (AD-6). */
export interface CostLinePayload {
  description: string
  vendorName?: string
  /** The counterparty the name was picked from, when it was picked at all.
   *
   *  Always SENT, `null` when there is none: the server reads an absent key as
   *  "this client does not know the field" and leaves the stored link alone,
   *  which would make clearing a picked vendor impossible from a form that
   *  omitted it. */
  vendorId?: string | null
  /** The unit COST, and the only price a line write carries (S-5, L3/L6): the
   *  server derives the line's sell from it at the quote's stored markup. There
   *  is no `sell` here on purpose — a client-set price is what put an edited
   *  line back at cost while its neighbours kept their markup (Q-11). */
  buy: string
  quantity: number
}

/** One routing offered on a quote (FR-3), as the wire serves it
 *  (routeoption_dto.go RouteOptionDTO).
 *
 *  The route is three structured fields, never a joined string (FR-19): the
 *  case conversion parses POL → border → POD from exactly these. The selected
 *  option is the one the cost lines price — its real total is the quote's
 *  costTotals, deliberately not repeated here — while the others carry the
 *  typed indicative figure, when anybody entered one. */
export interface RouteOption {
  id: string
  /** The slot (1–3), which is also the letter the offer prints: 1 is "A". */
  position: number
  origin: string
  destination: string
  /** The ordered places between the two ends (migration 0062). Always an
   *  array — `[]` is "no intermediate place", a statement the editor renders. */
  points: RouteOptionPoint[]
  /** Whether the cargo changes vehicle on the way; printed on the offer and
   *  copied onto the converted leg. */
  transshipment: boolean
  /** Planned days of the final hop, last point (or origin) → destination. */
  destinationDaysFromPrevious?: number
  transportModeId?: string
  /** The directory code ("RAIL"); map with modeFromCode at the seam. */
  transportModeCode?: string
  transitDaysMin?: number
  transitDaysMax?: number
  indicativeTotal?: Money
  /** Exactly one is true whenever any options exist — the server's
   *  invariant, rendered rather than re-derived. */
  selected: boolean
}

/** What a route point IS, spelled as the wire spells it (migration 0062).
 *  Shared by route options, case leg points and direction template points. */
export type RoutePointKind = 'border_crossing' | 'customs_terminal' | 'waypoint'

export const ROUTE_POINT_KINDS: readonly RoutePointKind[] = [
  'border_crossing',
  'customs_terminal',
  'waypoint',
]

/** One place between a route option's origin and destination. No id and no
 *  position: order is the array's (routeoption_dto.go RoutePointDTO). */
export interface RouteOptionPoint {
  name: string
  /** ISO 3166-1 alpha-2, absent when unset. */
  country?: string
  kind: RoutePointKind
  /** Planned days of the hop from the previous place. */
  daysFromPrevious?: number
}

/** What creating or amending a route option sends. Selection is deliberately
 *  not here: it moves only through the select endpoint, which owns the FR-3
 *  acknowledgement. */
export interface RouteOptionPayload {
  origin: string
  destination: string
  /** The whole ordered list, replacing what the option carried. */
  points: RouteOptionPoint[]
  transshipment: boolean
  destinationDaysFromPrevious?: number
  transportModeId?: string
  transitDaysMin?: number
  transitDaysMax?: number
  /** A decimal string (AD-9), USD like the costing. */
  indicativeTotal?: string
}

/** The three kinds of document block a quote carries (FR-60), spelled exactly
 *  as the wire and the settings templates spell them — a copy is a copy, not a
 *  translation.
 *
 *  `included` is the exclusion with the sign flipped (migration 0042): the same
 *  list of one-line items in an order somebody arranged, printed under its own
 *  heading. It carries no `priceText`, because a figure beside an inclusion
 *  would name a charge that is already inside the quoted total — the server's
 *  CHECK and BlockChange.Validate both refuse one, and a screen that offered
 *  the input would be offering a field the save rejects. */
export type QuoteBlockKind = 'not-included' | 'disclaimer' | 'included'

/** One not-included item or disclaimer on a quote (FR-60), as the wire serves
 *  it (block_dto.go QuoteBlockDTO).
 *
 *  A COPY of whatever a settings template said when somebody inserted it, not a
 *  reference to it: editing the template next month must not change what a
 *  quote sent last month printed. Nothing here is agent identity, so nothing is
 *  ever suppressed — these are words on paper handed to the client.
 *
 *  `body` is what prints; `label` is the editor's handle and stays on this
 *  screen. `priceText` is always a string, empty when there is none, so the
 *  input bound to it never flips from controlled to uncontrolled. */
export interface QuoteBlock {
  id: string
  kind: QuoteBlockKind
  label: string
  body: string
  /** FR-60's optional price or tariff note ("at cost", "as per tariff"). Text
   *  rather than money: it joins no total, and an empty one prints nothing. */
  priceText: string
  /** The order within the KIND, 1-based, assigned by the server. */
  position: number
}

/** What creating or amending a block sends.
 *
 *  `kind` is read on the create and ignored on the amend — it is immutable, and
 *  moving a disclaimer into the exclusions is a different item rather than an
 *  edit. `position` is the server's on a create and travels only from a
 *  reorder, so an absent one leaves the order alone. */
export interface QuoteBlockPayload {
  kind?: QuoteBlockKind
  label: string
  body: string
  priceText?: string
  position?: number
}

export interface QuoteRevision {
  id: string
  label: string
  issuedAt: string
  issuedByName: string
  sell: Money
  note?: string
}

/* One row of the quote's action history (the History tab), mirroring
 * quote/history.go's ActionEntryDTO. A different record from QuoteRevision
 * above: a revision is what the CLIENT was sent, an action is what the TEAM
 * did — created, edited, sent, answered, converted.
 *
 * `action` is the log's stable machine key ("quote.cargo.create"), translated
 * here exactly the way an audit entry's field name is — the server never
 * stores a sentence that would freeze in one language. */
export interface QuoteActionEntry {
  id: string
  occurredAt: string
  actorName: string
  action: string
  /** Sub-record ids and annotations — ids and statuses only, by the log's own
   *  rule, so nothing here needs suppression. Absent when the entry carries
   *  nothing beyond the act itself. */
  detail?: Record<string, string>
}

/* The offer preview (UX-DR-15), mirroring quote/offer_dto.go.
 *
 * The document arrives ALREADY FORMATTED and already in the client's language:
 * money is rendered by the server's one money rule (UX-DR-30) and the labels are
 * the offer's own, not the interface's. So nothing on this surface reformats a
 * cell or re-translates a label — a preview that did could disagree with the PDF,
 * which is the one thing a preview must never do.
 *
 * Field labels are therefore in the CLIENT's language while the sheet's own
 * chrome stays in the user's. That contrast is intentional: it is what shows the
 * sender the document as the recipient will read it. */
export interface OfferPreviewField {
  label: string
  value: string
}

export interface OfferPreviewColumn {
  header: string
  /** A relative weight, not a pixel width — the renderer scales it to the page. */
  width: number
  /** Right-aligns the cell and sets tabular figures, as the PDF does. */
  numeric?: boolean
}

export interface OfferPreviewRow {
  cells: string[]
  /** A total line, never decoration. */
  emphasis?: boolean
}

export interface OfferPreviewTable {
  title: string
  /** The line opposite the title — 'All amounts in USD'.
   *
   *  Load-bearing rather than decoration: a single-currency table states its
   *  currency ONCE here and its rows carry bare figures, so a surface that
   *  drops it shows a column of numbers with no currency anywhere. */
  caption?: string
  columns?: OfferPreviewColumn[]
  rows?: OfferPreviewRow[]
  note?: string
}

export interface OfferPreviewBlock {
  title: string
  body: string
}

export interface OfferPreview {
  /** Absent on a preview, always: minting belongs to the issuing transaction
   *  (AD-12), and its absence is what makes the sheet print DRAFT. */
  number?: string
  /** The revision letter a send WOULD issue as — read, never reserved. */
  label: string
  date: string
  recipient: { name: string; address?: string; reference?: string }
  fields?: OfferPreviewField[]
  tables?: OfferPreviewTable[]
  totals?: OfferPreviewField[]
  blocks?: OfferPreviewBlock[]
  signatureBlock: boolean
  /** Why a send would be refused right now, as a stable token (AD-27); absent
   *  when the offer is sendable. The document above it is accurate either way. */
  refusal?: string
}

/* Omit<…, 'cargo'> because the wire reuses the name for two different shapes:
 * the list serves the rounded aggregate (CargoSummaryDTO), the detail serves the
 * positions themselves (dto.go QuoteDetailDTO). Go expresses that by shadowing
 * the embedded struct's field; extends cannot, since a narrowed property must
 * stay assignable to the one it overrides. Dropping it before redeclaring keeps
 * both shapes exactly as the server sends them.
 *
 * revisionLabel is dropped for the simpler half of the same reason: the detail
 * serves the revisions themselves, never the list's shorthand, and a type that
 * offered it here would invite a screen to render a field that never arrives. */
export interface QuoteDetail extends Omit<QuoteSummary, 'cargo' | 'revisionLabel'> {
  incoterm?: string
  // paymentTerms, clientContactName and clientContactEmail are all inherited
  // from QuoteSummary — the list and the detail serve the same embedded
  // QuoteDTO, so declaring them twice would let the two drift.
  demurrageTerms?: string
  /* `notIncluded` and `disclaimer` used to sit here: a string array and a
     paragraph that no server ever sent, rendered read-only by the terms panel
     while the offer composer printed the real FR-60 rows from
     /quotes/:id/blocks. Dropped with the panel that read them — a type that
     offered them would invite a screen to render a field that never arrives,
     the reason revisionLabel is omitted above. See QuoteBlock. */

  /* The collections that hang off the header.
   *
   * Cargo and checklist are SERVED (Story 2.2): the detail endpoint computes
   * the checklist from the saved positions and sends both together, so they
   * always arrive. Still optional here, because a header read from a stale
   * cache or an older server has neither, and a required array is what made
   * this screen read .length off undefined once already.
   *
   * Revisions are SERVED too (Story 2.6): the detail endpoint reads the
   * history with the header. Cost lines and route options stay absent until
   * their tables exist in Stories 2.3 and 2.4, and those sections render empty
   * until then. */
  cargo?: CargoPosition[]
  checklist?: ChecklistIssue[]
  /** The server's answer to "may this go out" (FR-3), never the client's to
   *  compute: an empty checklist on a quote with no cargo is not readiness. */
  sendable?: boolean
  /** The same answer for the other button (FR-6, FR-7): may a new revision be
   *  opened from where this quote stands. Also the server's — the status rules
   *  live there, and a client deriving them would drift from the refusal. */
  reopenable?: boolean
  costLines?: CostLine[]
  /** The footer arithmetic, served with the lines (FR-1). Absent only on a
   *  response that did not read the costing at all. */
  costTotals?: CostTotals
  /** The quote's ONE stored markup over cost (S-5, L1/L9), as the exact
   *  decimal the server holds ("15", "12.5", "0"). Served on the detail, send
   *  and reopen shapes, always — a quote nobody has priced holds "0". Only
   *  `POST …/cost-lines/markup` sets it; every line's price follows from it. */
  markupPercent: string
  /** The header's cargo measures (FR-13), summed server-side on every read. */
  cargoTotals?: CargoTotals
  routeOptions?: RouteOption[]
  revisions?: QuoteRevision[]
  /** The case this quote became (FR-19) — the 1:1 link is navigation and
   *  provenance, read by both «Открыть работу» affordances (the header button
   *  and the conversion sheet's).
   *
   *  Served by `GET /api/quotes/:id` on a CONVERTED quote, and only when the
   *  caller can reach the case under its own casefile scope: a Pricing teammate
   *  on the quote who is not on the case, or a custom role without case scope,
   *  gets neither. Absent otherwise, so a converted quote without them is a
   *  legitimate production shape and the screen must still offer a way out. */
  caseId?: string
  caseNumber?: string
}

/** One INTERNAL colleague on a quote (migration 0028).
 *
 *  An account carrying the role identity gave it. The quote's EXTERNAL parties
 *  — a shipper, a consignee, a driver — were a separate table until S-22a
 *  dropped it (migration 0075); the separation was load-bearing while both
 *  existed, because collapsing them "would put a login on a truck driver".
 *
 *  Structurally identical to CaseTeamMember, and kept as its own type for the
 *  reason the server keeps two: one is a quote's, one is a case's, and a shared
 *  type would invite a shared component that then has to branch on which record
 *  it is rendering. */
export interface QuoteTeamMember {
  userId: string
  name: string
  role: Role
  /** The quote's own owner, seeded onto the team at create. Rendered
   *  differently and protected by the last-member rule, so the flag travels
   *  rather than being re-derived by comparing against the quote. */
  owner: boolean
}

/** One account as a PICKER sees it, from GET /api/users.
 *
 *  Deliberately narrower than DirectoryUser, which comes from the Super-admin's
 *  /admin/users and carries capabilities, grants and activation state. This is
 *  the roster ANY signed-in user may read, so it is a name, a role and an
 *  office — no permissions, no email, no active flag. The server filters the
 *  list to accounts that can actually be staffed. */
export interface Colleague {
  id: string
  name: string
  role: Role
  office: Office
}

/** The pipeline's win/loss figures over a date range (FR-15, Story 2.7).
 *
 *  Mirrors PipelineStatisticsDTO in the quote module's dto.go, field for field,
 *  including the two that no acceptance criterion asks the widget to print:
 *  `open` and `total` are the denominator's context, and the server's own note
 *  says why they travel — a rate over four decided quotes and a rate over four
 *  hundred are different claims.
 *
 *  `won` is Approved plus Converted and `expired` is COUNTED ON ITS OWN, never
 *  folded into `lost`: a quote nobody answered is a different failure from one
 *  the client refused, and adding them would delete which of the two a bad
 *  month was made of. The client does no arithmetic over these — the server
 *  decides every bucket, including the computed expiry the stored status cannot
 *  express (AD-24). */
export interface PipelineStatistics {
  /** The range actually counted, date-only (AD-19). ABSENT rather than empty
   *  on an unbounded end — "all time" has no first day, and a date invented for
   *  it would be a period on the screen that nobody asked for. */
  from?: string
  to?: string

  won: number
  lost: number
  expired: number
  open: number
  total: number

  /** What the won quotes are worth. Money is a decimal STRING (AD-9) — the
   *  note at the top of this file forbids widening it to a number. */
  wonSellTotal: Money

  /** A percent at one decimal, "58.3", or NULL when nothing in the range was
   *  decided. Null rather than 0, and the distinction is the point: zero is a
   *  real answer to a real denominator, and manufacturing it from an empty one
   *  would tell a manager the team lost everything in a month they were simply
   *  still waiting. */
  conversionRate: string | null

  /** Whose quotes these figures were counted over, as a token the client
   *  translates (AC 6). The server resolves it from the caller's actual scope
   *  level rather than from their role, so the caveat can never promise "your
   *  quotes only" over a set somebody's scope was widened to include. */
  scopeLabel: 'own' | 'assigned' | 'all'
}

/* ── Case ─────────────────────────────────────────────────────────────────── */

export type CaseStatus =
  | 'draft'
  | 'in-progress'
  | 'stuck'
  | 'delivered'
  | 'operationally-closed'
  | 'financially-closed'
  | 'cancelled'

/** How a case's client agreed to pay (FR-32).
 *
 *  A closed set, not a phrase. Until Story 4.3 the case carried whatever
 *  somebody typed — "50/50", "50-50", "net 30" — and each of those is clear to a
 *  person and opaque to a platform that has to CHASE the money. These five are
 *  the wire values the server accepts; anything else is refused rather than
 *  reinterpreted.
 *
 *  `in_advance` is the workbook's «IN ADVANCE PAYMENT»: one tranche for the
 *  whole amount, dated at registration. It is also what a quote's picked
 *  prepayment converts to (P0-2), beside 50/50 and full on delivery; a quote's
 *  credit or free-text terms convert to `other`, because the day count is not
 *  something the quote states. */
export type PaymentTermsKind =
  | 'in_advance'
  | 'fifty_fifty'
  | 'full_on_delivery'
  | 'credit'
  | 'other'

/** How the client pays (FR-32), and how a recorded payment moved (Story 4.5).
 *  ONE type for both: the distinction is operational rather than accounting —
 *  cash is collected by a person, a transfer is chased through a bank — and the
 *  two stories ask it from opposite ends of the same fact.
 *
 *  The wire spellings are the server's own; its `payment_method` SQL domain
 *  accepts exactly these two, and they are carried verbatim rather than
 *  kebab-cased for the UI. Translating a value in two directions is one more
 *  place a typo becomes a 422 whose field nobody can trace back. */
export type PaymentMethod = 'cash' | 'bank_transfer'

/** One case's position on its own route, as a list row carries it. */
export interface CurrentLeg {
  /** The leg's number and how many the route has — "2 of 3". */
  sequence: number
  total: number
  origin?: string
  destination?: string
  /** The two ISO 3166-1 alpha-2 codes (0046), so the place can wear its flag.
   *  Empty on a leg nobody gave a country — rendered exactly as it was before
   *  the columns existed: no flag, no reserved gap, no globe standing in. */
  originCountry?: string
  destinationCountry?: string
  /** Where THIS movement stands (FR-52) — the fact the board is asked for,
   *  because "waiting at border" is the difference between a shipment that is
   *  moving and one that has stopped. */
  status: LegStatus
  /** The DISPLAY mode, folded from the server's code exactly as `CaseLeg.mode`
   *  is, because the card draws the same `MODE_ICONS` disc from it. */
  mode: TransportMode
}

export interface CaseSummary {
  id: string
  /** Human-facing number, minted by the allocator and stored (AD-12). */
  number: string
  clientName: string
  /** Groups a client's shipments without matching on their NAME — two clients
   *  differing by a suffix must not merge into one recipient (FR-41). */
  clientId?: string
  status: CaseStatus
  origin: string
  destination: string
  /** How the shipment moves, or ABSENT when nobody has said — the case's
   *  transport mode is a nullable column, NULL whenever the quote it came from
   *  named none. Deliberately not `CaseLeg.mode`'s multimodal fallback: a leg's
   *  mode is NOT NULL, so folding an unknown code there only picks an icon,
   *  while folding an absent one here printed «Мультимодал» for a case nobody
   *  had called multimodal (walkthrough C-6). Every reader renders absence —
   *  no icon, and a dash where the surface has a slot for the fact. */
  mode?: TransportMode
  /** The same mode as its catalogue row (`casefile.CaseDTO.transportModeId`,
   *  `omitempty`), absent exactly when `mode`'s column is NULL. `mode` is a
   *  lossy display fold and cannot be turned back into an id, so a form that
   *  SEEDS a mode from the case — a new leg (QW-8) — reads this one. */
  transportModeId?: string
  /** The issuing entity the case is registered under
   *  (`casefile.CaseDTO.entityId`, always served by the real module; optional
   *  for the fixture rows). The default «Юрлицо» of a carrier payment (QW-8). */
  entityId?: string
  /** The shipment's planned DEPARTURE: the ETD of the case's first leg, derived
   *  server-side. There is no case-level ETD column — 0015 put the dates on the
   *  movements, where a case that leaves Yiwu by truck and Almaty by rail has
   *  two of them and the one a list is asked about is the first. */
  etd?: string
  eta?: string
  /* The money, from the COMPOSED list (cmd/asa/readmodel_case_list.go), not
     from casefile's own DTO: a case row's rate and profit are finance's figures
     (AD-3), joined to the scoped list at the composition root (AD-23).

     Absent — all of them — for a case carrying no money fact at all, which is
     the ordinary state of an FR-70 direct creation and of all 241 migrated
     cases. `rate` is additionally absent when the case has no agreed rate even
     though it has costs: a zero there would read as a free shipment. */
  rate?: Money
  /** The row's ONE profit figure (S-6): the forecast while the case runs, the
   *  cash result once it is financially closed or cancelled — `profitBasis`
   *  says which. The same figure as the right side of the case's money line.
   *  Absent — never a substituted forecast — when the case is unpriced, the
   *  reader has no money reach, or the final read failed. */
  profit?: Money
  profitBasis?: ProfitBasis
  /** Where the cargo IS on the route: the first leg that is not completed, with
   *  the route's length beside it.
   *
   *  ABSENT for a case with no legs planned and for one whose every leg is
   *  completed — the two states a position could not honestly describe, and
   *  both of which would otherwise print as a leg zero. The server decides
   *  which leg is current (`caseFrom`'s `legnow` lateral); no surface
   *  re-derives it, or the two would disagree the first time a leg was
   *  corrected backwards.
   *
   *  A SUMMARY rather than a `CaseLeg`: the full leg carries the carrier, whose
   *  identity FR-29 suppresses per audience, and a case record carries no
   *  suppressed field. The route section is where a leg is read in full. */
  currentLeg?: CurrentLeg
  /** Days without movement, PRESENT ONLY WHEN THE CASE IS STUCK (FR-52).
   *
   *  The server applies the threshold — the configured company setting, the
   *  same number the dashboard's stuck widget reads — and omits the field
   *  otherwise, so presence IS the flag. A raw count on every row would put an
   *  attention chip on every case older than a day, and a client-side threshold
   *  would be a second definition of "too long" to keep in step with the
   *  founders' own screen. Only an in-process case can carry it: a closed or
   *  cancelled case has stopped moving by design. */
  daysStalled?: number
  /** The owner's account id — `casefile.CaseDTO.ownerId`, always served.
   *
   *  Optional here and nowhere else: the server has sent it since 0007, but the
   *  fixtures predate this declaration. What reads it is `canSeeRecordChat`,
   *  which needs the owner's ID rather than their NAME, because the owner can be
   *  taken off their own case's team (casefile.Service.Unassign refuses only the
   *  removal that would EMPTY it) and still reads the conversation server-side. */
  ownerId?: string
  ownerName?: string
  /** Where a status update about this case would go (FR-41).
   *
   *  NOT SERVED BY `GET /cases`, and this note is the correction of a comment
   *  that claimed the opposite for long enough to cause a bug. It said the
   *  fields were on the summary "so the bulk send works from list rows without a
   *  detail fetch per case"; casefile.CaseDTO has never carried an address, only
   *  the composed detail does, and the consolidated-send sheet that trusted this
   *  declaration reported every client as having no contact email.
   *
   *  They stay declared because the FIXTURES set them and the mock branch of
   *  `useStatusRecipient` reads them, which is the only place they are ever
   *  populated. The real recipient comes from `GET /cases/status-recipient`,
   *  resolved through the port the send itself resolves through. */
  clientContactName?: string
  clientContactEmail?: string

  /* The FR-32 trio, on the SUMMARY because the edit sheet seeds from a summary
     row and a field it cannot read is a field the edit silently clears. The day
     count is present only for `credit` — absent is "this case has no credit
     term", which is not the same as zero days.

     These REPLACE the free-text `paymentTerms` this shape used to carry, which
     is exactly what its note said would happen once Story 4.3 landed.

     Optional on the type though mandatory on the record: rows built before this
     shape existed, and fixtures, still typecheck. */
  paymentTermsKind?: PaymentTermsKind
  paymentTermsCreditDays?: number
  paymentMethod?: PaymentMethod

  /** Whether the client's final packing list is in hand (FR-12). On the SUMMARY
   *  for `paymentTerms`' reason — the case form prefills from a list row, and a
   *  whole-record submit that could not see this flag would answer "no" on
   *  behalf of a case that has the list. Optional here and required on
   *  `CaseDetail`: the list rows the fixtures build do not all state it, and no
   *  list surface renders it. */
  finalPackingListReceived?: boolean
}

/** What creating or amending a case header sends (FR-19, FR-24, FR-70).
 *
 * The route travels as four separate places, never as one joined "Origin →
 * Destination" string: the server filters by corridor on the way back, and a
 * joined string cannot be filtered. The sheet collects the two ends the desk
 * actually types at creation; the crossing and the unloading place are added on
 * the case itself, where the legs are.
 *
 * `openedOn` is omitted rather than guessed when the desk did not state one —
 * the server defaults it to today, and a client-stamped date would be the
 * browser's day, which in the other office is a different one (AD-19). */
export interface CasePayload {
  clientId: string
  placeOfLoading?: string
  borderCrossing?: string
  placeOfDischarge?: string
  unloadingPlace?: string
  transportModeId?: string
  cargoDescription?: string
  /** Whether the client's final packing list is in hand (FR-12).
   *
   *  NOT optional the way the fields around it are, and that is the whole point:
   *  PUT /cases/:id takes the whole record, so an absent key is read as false
   *  and CLEARS a flag conversion copied over. Every caller has to state it,
   *  which is what stops an edit form from silently answering "no" on behalf of
   *  a case whose list has already arrived. */
  finalPackingListReceived: boolean
  /** The FR-32 trio, replacing the free-text `paymentTerms` this payload used
   *  to send. Kind and method are MANDATORY — the server refuses a case
   *  without them — and the day count is sent only for `credit`, where the
   *  server refuses it on any other kind. */
  paymentTermsKind: PaymentTermsKind
  paymentTermsCreditDays?: number
  paymentMethod: PaymentMethod
  notes?: string
  /** Date-only, "YYYY-MM-DD" (AD-19). */
  openedOn?: string
  eta?: string
  ata?: string
}

/** What a leg carries, kind by kind (S-18b, `casefile.UnitKind`): a truck, a
 *  container, a wagon, an air waybill, a bill of lading, or a free-labelled
 *  other. The server answers 422 `units.<i>.kind: invalid` for anything else;
 *  `lib/legUnits.ts` holds the order the leg page and the payment sheet list
 *  them in, and folds a kind this client does not know. */
export type UnitKind = 'truck' | 'container' | 'wagon' | 'awb' | 'bl' | 'other'

/** One unit as a leg write submits it (issue #35 story 5; every kind since
 *  S-18b).
 *
 * The whole row, because the leg PUT replaces the whole list: a save that sent
 * only the number would CLEAR the trailer, the CMR number and the driver on
 * every truck it touched. That is the same whole-record collapse `LegPayload`
 * itself makes, and the unit cards are the surface where forgetting it would be
 * most expensive — they save every unit on the leg at once.
 *
 * No `id` and no `position`. The server matches an existing row by its KIND
 * and NUMBER (`unitKey`: a truck's plate folded for case and spaces, every
 * other kind's number exact after trimming) and numbers the list from its
 * order, which is what lets the cards edit a saved unit and add three new ones
 * in one request. There is no "change kind": a unit moved to another kind is a
 * removal plus an insertion.
 *
 * Only the fields the row's kind carries (`UNIT_FIELDS` in
 * `features/cases/components/legs/unitKinds.ts`): any other non-empty field is
 * refused 422 `units.<i>.<field>: not-for-kind`.
 *
 * `carrierId`/`carrierName` and the two driver fields are plain strings here
 * although they are `Maybe` on `LegUnit`, and the asymmetry is AD-6's:
 * suppression is a property of what the server SENDS, never of what a client
 * submits. A form that may not read a value never writes one back — it submits
 * what it was shown, and what it was shown was nothing. */
export interface LegUnitPayload {
  kind: UnitKind
  /** The plate, the container or wagon number, the AWB, the B/L or booking
   *  number, the free label — as printed. */
  number: string
  trailerPlate?: string
  containerNumber?: string
  cmrNumber?: string
  sealNumber?: string
  unitType?: string
  carrierId?: string
  carrierName?: string
  driverName?: string
  driverPhone?: string
  /** Whole kilograms, a NUMBER rather than a decimal string: a weighbridge
   *  ticket is an integer and this is not money (AD-9 governs the latter). */
  weightKg?: number
}

/** What the leg sheet submits (POST/PUT /cases/:id/legs).
 *
 * `sequence` is deliberately absent: where a leg sits in the route is the
 * server's to decide, so that two forms submitting position 2 at once cannot
 * leave the route with two second legs and no first.
 *
 * The derived figures are absent too. `delayDays` and `stalledDays` are
 * computed by the server on every read (AD-9) — posting one would let a client
 * state a delay that disagreed with the dates beside it. */
export interface LegPayload {
  origin: string
  destination: string
  /** ISO 3166-1 alpha-2. Absent clears the country, which is the same
   *  whole-record collapse every other optional field here makes. */
  originCountry?: string
  destinationCountry?: string
  transportModeId: string
  /** THREE-STATE on the PUT (S-10), like `units`, `lastLocation` and
   *  `lastReportedAt`: absent keeps what is stored — which
   *  is what lets a desk that typed for twenty minutes save without writing
   *  back a status the 15-minute sweep has since moved. Always sent on the
   *  POST, which is unchanged. Never `""` (the server answers 422). */
  status?: LegStatus
  /** Whether the cargo changes vehicle on this leg (0062). */
  transshipment: boolean
  /** The directory counterparty carrying this leg. Absent unlinks it. */
  carrierId?: string
  /** Still submitted alongside the id, and not vestigial: a leg linked to
   *  nobody keeps the name off its waybill, and a sheet that re-saved a legacy
   *  leg without this would erase what somebody typed before the id existed. */
  carrierName?: string
  reference?: string
  voyageNumber?: string
  /** The whole list when present, replacing whatever the leg carried; `[]`
   *  clears it. ABSENT keeps the stored units on the PUT (S-10) — a save that
   *  did not touch the units must not be the save that reverts or deletes the
   *  ones a colleague entered since this page was read. On the POST it is the
   *  new leg's list.
   *
   *  Positions are absent from the elements: order is the order of the array
   *  across every kind, numbered by the server. A client that could submit
   *  positions could submit two firsts.
   *
   *  `vehicles`, `containers` and `vehiclesVersion` are RETIRED (S-18b): the
   *  server answers 422 `<key>: retired` to a body carrying any of them, so a
   *  tab opened before the deploy fails loudly instead of having its trucks
   *  silently ignored. This type has no such keys on purpose. */
  units?: LegUnitPayload[]
  /** The stored unit list's token, as the leg read or write last served it
   *  (`CaseLeg.unitsVersion`). Sent with `units` and only with it — and on the
   *  PUT REQUIRED with it (422 `unitsVersion: required`): a PUT whose version
   *  no longer matches is refused with 409 `leg-units-changed` and nothing is
   *  written. The POST carries none. */
  unitsVersion?: string
  /** Date-only, "YYYY-MM-DD" (AD-19). */
  etd?: string
  eta?: string
  atd?: string
  ata?: string
  stallNote?: string
  /** Three-state (S-10): absent keeps the stored position — the sweep's —
   *  and a string (`""` included) writes it. Sent with `lastReportedAt`. */
  lastLocation?: string
  /** When `lastLocation` was reported, RFC 3339. Three-state; the page sends
   *  it, stamped now, beside an edited `lastLocation` and never alone. */
  lastReportedAt?: string
}

/** What the route chain submits (PUT /cases/:id/legs/:legId/points).
 *
 * The WHOLE list, like every other list on a leg: an absent element is a point
 * removed, and the server allocates the positions from this order. Nothing here
 * carries an id, for that reason — a client that could submit positions could
 * submit two firsts.
 *
 * `reached` is a FLAG rather than the timestamp it produces. The client knows
 * that the cargo is at Sarahs; it does not know at which instant the server
 * will record that, and a client-stamped arrival would be one clock disagreeing
 * with the one every other row on the case is stamped by. Kept-true stays the
 * moment it was first recorded — the server preserves it — so re-submitting an
 * unchanged chain does not re-date the journey. */
export interface LegPointsPayload {
  points: {
    name: string
    /** ISO 3166-1 alpha-2, or absent for unset. */
    country?: string
    /** Absent is taken as `waypoint` by the server. */
    kind?: RoutePointKind
    /** Absent on the first point and only there — it has no inbound
     *  transition. The server enforces that correspondence both ways. */
    plannedDaysFromPrevious?: number
    /** The reached marks must form a PREFIX of the chain: cargo that has
     *  reached point 3 has passed points 1 and 2. A gap is refused with
     *  `points.i.reached: not-contiguous` rather than quietly closed. */
    reached: boolean
  }[]
}

/** What a stop IS on the four-part route (FR-24). A closed set, so a locale
 *  missing a caption fails to compile rather than showing a raw code. */
export type RouteStopRole = 'loading' | 'border' | 'discharge' | 'unloading'

/** Where the shipment stands against a stop. The server decides — the client
 *  never re-derives "current" from leg statuses it might read differently. */
export type RouteStopState = 'passed' | 'current' | 'ahead'

/** One stop of the case route: POL, border crossing, POD, unloading place. */
export interface RouteStop {
  id: string
  name: string
  role: RouteStopRole
  state: RouteStopState
  /** Date-only (AD-19): the actual for a passed stop, the estimate ahead.
   *  Absent on a current stop, which carries the dwell instead. */
  date?: string
  /** Days sitting at a `current` stop — the border dwell of FR-52. */
  dwellDays?: number
}

/** One cargo position on the case (FR-24). Measures are decimal strings
 *  (AD-9), like every measure on the wire. */
export interface CargoLine {
  id: string
  description: string
  weightKg?: string
  volumeCbm?: string
  /** IMO dangerous-goods class, e.g. "9". Presence is what flags the line. */
  imoClass?: string
}

/** The declared load, and what a control re-weigh said about it. The delta is
 *  SERVED, never subtracted here — the exact decimals live on the server. */
export interface CaseCargo {
  lines: CargoLine[]
  declaredWeightKg: string
  declaredVolumeCbm?: string
  measured?: {
    location: string
    weightKg: string
    /** Signed decimal string, positive when over the declaration. */
    deltaKg: string
  }
}

export type LegStatus =
  | 'planned'
  | 'in-transit'
  | 'waiting-border'
  | 'arrived'
  | 'completed'

/** One unit running a leg (FR-67): a truck, a container, a wagon, an air
 *  waybill, a bill of lading or another free-labelled unit (S-18b,
 *  `casefile.UnitDTO`), one table and one list across every kind.
 *
 *  An object rather than a bare plate string, which is what a truck used to
 *  be: the position is served rather than left to the array index, because the
 *  card renders "truck 2" and an index is one off-by-one away from disagreeing
 *  with what the desk says on the phone. Position is the array order across
 *  every kind; a migrated leg's trucks and containers may share numbers, which
 *  is why the server orders by `position, created_at, id`. */
export interface LegUnit {
  id: string
  position: number
  kind: UnitKind
  /** As printed — the plate, the container or wagon number, the AWB, the B/L
   *  or booking number, the free label. Verbatim: plate formats disagree about
   *  their separators country by country and the server does not normalise
   *  them. */
  number: string
  /** A TRUCK's trailer, the box and the carriage document it travels on
   *  (0055). Absent rather than empty when nobody recorded one — `omitempty`
   *  on every field below — which is the ordinary state of a unit being
   *  planned; the table renders the absence.
   *
   *  Unrestricted, like the number: all three are printed on the transport
   *  documents the client already holds and none of them names a counterparty.
   *  A truck's `containerNumber` stays a truck field (owner): it is not a
   *  container unit. */
  trailerPlate?: string
  containerNumber?: string
  cmrNumber?: string
  /** A container's or a wagon's seal (a wagon's ЗПУ) and equipment type —
   *  "40HC", "20DV". An absent seal means NOT YET SEALED or not yet known,
   *  never "unsealed". */
  sealNumber?: string
  unitType?: string
  /** Whole kilograms off a weighbridge ticket or a manifest. A movement fact,
   *  not money — what the unit COST is `LegMoney`'s, composed from finance
   *  (AD-3). */
  weightKg?: number
  /** The carrier running THIS unit and, on a truck, the person driving it —
   *  the FR-29 / NFR-3 half of the row (AD-6, 0055). All four are suppressed
   *  TOGETHER, for every kind, for an audience that may not see an agent, so a
   *  Sales caller reads the number, the weight and the money and learns
   *  nothing about who is owed it.
   *
   *  A row rendered here must therefore check each of them for presence rather
   *  than assume the four travel as a block on THIS payload: absent is what
   *  suppression looks like on the wire (AD-6), and it is also what "nobody
   *  filled it in" looks like. Neither is a value to print. */
  carrierId?: Maybe<string>
  carrierName?: Maybe<string>
  driverName?: Maybe<string>
  driverPhone?: Maybe<string>
}

/** One unit's money on a direction (issue #35, story 6; every kind since
 *  S-18b).
 *
 *  Every figure is USD and every one of them is DERIVED server-side (AD-11) —
 *  the client sums nothing here. It could not do it correctly anyway: these are
 *  decimal strings precisely because a float64 in JavaScript cannot hold a cent,
 *  and a carrier balance is exactly the number somebody notices being wrong by
 *  one. `finance.UnitMoneyDTO` is the shape this mirrors, field for field. */
export interface UnitMoney {
  unitId: string
  /** The agreed rate — the planned cost lines attributed to this unit. */
  agreed: Money
  /** What the haulier actually invoiced, zero until an invoice lands. */
  actual: Money
  /** The share of `paid` that moved BEFORE the leg's ATA. A server fact under
   *  the ATA rule (see `finance/legmoney.go`), never re-derived here. */
  advance: Money
  paid: Money
  /** What is still owed on this unit (S-18a), USD on `Outstanding`'s
   *  forecast-minus-paid basis — the actual, falling back to the agreed rate,
   *  minus what was paid — each plan/actual pair counted on its PLAN's unit.
   *  SIGNED like `LegMoney.outstanding`: below zero is an overpayment. The
   *  per-unit remainders plus `LegMoney.legLevel.remaining` are
   *  `outstanding`; the client adds none of them. The unit cards print it
   *  read-only under «Остаток» (S-18b). */
  remaining: Money
}

/** The leg's own money that belongs to no unit (S-18a) — the leg rate on a
 *  sea or air leg, a convoy-level charge — on the same basis and in the same
 *  USD as `UnitMoney` (`finance.LegLevelMoneyDTO`). */
export interface LegLevelMoney {
  agreed: Money
  actual: Money
  paid: Money
  /** Signed like `LegMoney.outstanding`. */
  remaining: Money
}

/** One payment's slice applied to one of the direction's costs — a row of the
 *  «Оплаты перевозчику» table (`finance.LegSettlementDTO`).
 *
 *  It IS an allocation seen from the leg's side, and it is shaped after
 *  `Allocation` deliberately: the amount in the payment's own currency with the
 *  booked USD share beside it. A second encoding of the same row is where the
 *  two come to disagree. */
export interface LegSettlement {
  id: string
  paymentId: string
  /** The ACTUAL cost line this settles — what a further allocation targets. */
  expenseLineId: string
  /** The unit it paid for, absent when the cost belongs to the leg and to no
   *  single unit (a convoy-level charge). */
  unitId?: string
  /** The cost line's own description — «Ставка фуры 63AJN05». Served to every
   *  audience; the vendor is NOT folded into it (see `legmoney_dto.go`). */
  item: string
  /** Whom we paid. AD-6 restricted and served as `''` when withheld — a bare
   *  string rather than a `Maybe`, because a payment's counterparty is required
   *  at the table and an empty one here has exactly one possible meaning. */
  counterpartyName: string
  /** What the payer typed as the payment's basis — «Счёт 418». Rendered as
   *  stored under «Основание» (S-12). */
  reference: string
  method: string
  /** Date-only (AD-19): the business day the money moved. */
  receivedOn: string
  amount: Money
  /** The settling PAYMENT's bank fee (S-12), absent when it had none. A split
   *  payment repeats it on each of its rows, so it is never summed — and the
   *  leg's paid totals never include it. Not suppressed for Sales. */
  fee?: Money
  /** The booked USD share of `amount`, so a row can be reconciled against the
   *  tiles without this side converting anything. */
  usd: string
  /** Carries the neutral «аванс» tag. A SERVER fact under the ATA rule — the
   *  client renders it and never re-derives it. */
  advance: boolean
  /** The STATUS of the payment this settlement came from (migration 0059).
   *
   *  On the settlement row rather than looked up, because the leg's money block
   *  is the only thing this card reads — there is no payment list beside it to
   *  join against, and a table that showed «не подтверждён» on some rows and
   *  nothing on others because a second read had not landed would be reporting
   *  a fact about its own network.
   *
   *  It moves NO figure above it (spec L4). A pending settlement is inside
   *  `totalPaid`, inside `advancePaid` and inside `outstanding` exactly like a
   *  confirmed one; the tag is a note about who has checked the record, not a
   *  qualifier on the money. */
  paymentStatus: PaymentStatus
}

/** One direction's money block (`finance.LegMoneyDTO`, issue #35 story 6).
 *
 *  SERVED TO EVERY CALLER who reaches the case, with carrier identity
 *  suppressed per field inside it — the rule the case's money block already
 *  follows (`cmd/asa/readmodel_case.go`). Figures are not the secret (AD-5);
 *  the haulier's name is (FR-29). A screen that hid the tiles from Sales would
 *  be hiding the wrong half. */
export interface LegMoney {
  legId: string
  /** Σ planned lines on the leg — what the desk agreed to pay for the trucks. */
  agreedCost: Money
  /** Σ actual lines: what the hauliers have invoiced so far. */
  actualCost: Money
  /** The share of `totalPaid` that moved before the leg arrived. */
  advancePaid: Money
  totalPaid: Money
  /** Actual (falling back to agreed for the trucks nobody has invoiced yet)
   *  minus what has been paid — «остаток перевозчику». Derived server-side
   *  where the decimals are exact. */
  outstanding: Money
  /** How many distinct payments the settlements came from — the «2 платежа»
   *  subline. A count, which no figure above can stand in for. */
  payments: number
  /** The leg's cost lines, folded plan-beside-actual by the same mapper the
   *  case's Money section reads, so both surfaces render one row one way. */
  expenses: ExpenseLine[]
  /** The per-unit rollup (`perUnit`, S-18b). A LIST and never a total: the
   *  totals are the four figures above, and it does not necessarily sum to
   *  them — a convoy-level cost belongs to the leg and to no unit. */
  perUnit: UnitMoney[]
  /** The leg-level bucket (S-18a): lines with this leg and no unit. Read
   *  as "nothing at the leg level" when absent — the composer may leave it out
   *  of a block that has no such line, and the fixtures predate it. */
  legLevel?: LegLevelMoney
  settlements: LegSettlement[]
}

/** One box in the CASE-level roll-up, carrying the leg it was on (FR-67).
 *
 *  Composed by the server from the leg units of kind `container` since S-18b
 *  (`unitType` served as `type`); the wire keys are unchanged — `id`, `number`,
 *  `sealNumber`, `type`, `legId`, `legSequence`, `legRoute`, and no `position`
 *  (the server's `composedCaseContainer`). */
export interface CaseContainer {
  id: string
  number: string
  /** Absent means NOT YET SEALED or not yet known — never "unsealed". A box
   *  awaiting stuffing has none, and the card says so rather than printing a
   *  blank beside the number. */
  sealNumber?: string
  /** The equipment type as the carrier states it — "40HC", "20DV". Absent when
   *  nobody recorded it, which is common; the card renders the absence rather
   *  than guessing. */
  type?: string
  legId: string
  legSequence: number
  /** "Khorgos → Türkmenabat", composed server-side so the row can name the
   *  movement in the one line it has. */
  legRoute?: string
}

/** One point on a leg's route chain (issue #35, story 2).
 *
 *  The chain is where the cargo IS; `status` is the movement report about it.
 *  The two are deliberately not merged — a leg can be `waiting-border` while
 *  the chain still says the last point reached is Almaty, and folding them
 *  would leave the desk with one field answering two questions.
 *
 *  Copied onto the leg from a settings `DirectionTemplate` (or typed by hand),
 *  never referenced: editing a template must not move a shipment already
 *  running, which is why there is no `templateId` here to reach back through. */
export interface LegRoutePoint {
  id: string
  /** 1-based, allocated by the server from the order the points were submitted
   *  in — the client sends a list, not positions it would have to renumber. */
  position: number
  name: string
  /** ISO 3166-1 alpha-2. The server sends `''` when nobody recorded one, and
   *  `LegPlace` renders that exactly as it renders an absent country: no flag,
   *  no reserved gap. */
  country?: string
  /** What the point is — border crossing, customs terminal or waypoint. */
  kind: RoutePointKind
  /** Normative days for the transition FROM THE PREVIOUS POINT. Absent on the
   *  first point, which has no inbound transition, and absent on any point
   *  whose transition nobody has put a norm on — the automation that advances
   *  the position skips exactly those. */
  plannedDaysFromPrevious?: number
  /** When the cargo was declared here. Its ABSENCE is what makes a point
   *  "ahead": there is no separate reached flag to disagree with it. */
  reachedAt?: string
  /** Whose word the arrival is on — an operator's report (`manual`) or the
   *  worker advancing the position on the normative days (`auto`). Shown under
   *  the point, because "the system assumed this" and "somebody confirmed it"
   *  are different degrees of certainty about where a client's cargo is. */
  reachedSource?: 'manual' | 'auto'
}

/** Why a case's or a leg's money block is absent — the composer's
 *  `moneyUnavailable` (cmd/asa/readmodel_case.go, `moneyReasonFor`).
 *
 *  Two failures and one decision. `not-configured` and `read-failed` are the
 *  server failing to assemble a block it would have served, and render with a
 *  retry. `forbidden` (story B3, fix round 1) is the caller reaching the case
 *  through `cases` but not its money through `case-money`: the server answers
 *  404 on every money route of that case, so the money surfaces render as
 *  ABSENT — no section, no retry, no request. The client decides from this
 *  per-case signal and never from the session's scope levels; seeded roles hold
 *  `case-money` equal to `cases` and never receive it. */
export type CaseMoneyUnavailable = 'not-configured' | 'read-failed' | 'forbidden'

export interface CaseLeg {
  id: string
  sequence: number
  /** The DISPLAY mode — lowercase, lossy, and what the icons and the labels key
   *  off. Never what a form submits: see `transportModeId`. */
  mode: TransportMode
  /** The catalogue row this leg's mode IS (`transport_refs.id`), carried
   *  through from the server rather than reconstructed from `mode` above.
   *
   *  The reconstruction is what broke: `mode` is a lowercase display value the
   *  wire mapper folds unknown codes into, so no lookup against it can find the
   *  catalogue row again — the edit sheet opened with an empty mode field and
   *  re-saving a leg after a status change cleared its mode. */
  transportModeId: string
  /** The catalogue's own UPPERCASE code, for the surfaces that key off it
   *  ("AIR" chooses the flight-number label). Absent on a leg whose mode row
   *  has been deleted from under it. */
  transportModeCode?: string
  origin: string
  destination: string
  /** ISO 3166-1 alpha-2, absent when nobody recorded one. BESIDE `origin` and
   *  `destination` rather than instead of them, which is where a leg differs
   *  from a quote's route option: the place is where the cargo physically is,
   *  the country is what a corridor report groups by. */
  originCountry?: string
  destinationCountry?: string
  status: LegStatus
  /** Whether the cargo changes vehicle on this leg (0062). */
  transshipment: boolean
  /** The counterparty moving this leg, when it is a directory row rather than a
   *  name off a waybill. Suppressed for Sales exactly as `carrierName` is, and
   *  by the same call on the server — the id identifies the same company. */
  carrierId?: Maybe<string>
  carrierName?: Maybe<string>
  /** The leg's transport document number — SMGS for rail, CMR for road.
   *  Absent until the document is issued, and the card says "not issued". */
  reference?: string
  /** The CONVEYANCE — a flight number on air, a voyage number on sea (FR-26).
   *  A separate fact from `reference`, and an air leg has both: AWB
   *  235-41190283 travelling on TK6021. One is the paper that consigns the
   *  cargo, the other is what you type into a tracking site. */
  voyageNumber?: string
  /** The units running this leg (FR-67, S-18b) — trucks, containers,
   *  wagons, air waybills, bills of lading, others — in their served order
   *  across every kind. Plural, because a consignment crossing on two trucks
   *  is one leg and two units.
   *
   *  Required and possibly empty, never absent. An empty list is a real answer
   *  — nobody has recorded a unit — and must not look like the `undefined`
   *  this field carried while the server had nothing to serve. */
  units: LegUnit[]
  /** A token for the stored unit list — a hash of each unit's id and last
   *  amendment, every kind included, computed by the server
   *  (`Leg.UnitsVersion`). Opaque here: compared by the server, never derived
   *  or edited on this side. The leg page sends back the token of the list its
   *  rows were seeded from, so a save over units a colleague changed since is
   *  refused (409 `leg-units-changed`) instead of reverting them. */
  unitsVersion: string
  etd?: string
  eta?: string
  /** Actual departure/arrival. Their presence is what flips the card's date
   *  facts from "ETD" to "ETD / ATD". */
  atd?: string
  ata?: string
  /** Days the arrival landed after the ETA, SERVED like every derived number
   *  (AD-9) — absent when on time or not yet arrived. */
  delayDays?: number
  /** How long this leg is PLANNED to take, ETD to ETA. Served for `delayDays`'
   *  reason and one more: a span computed here would be a second rule about
   *  which end counts, and it would disagree with the server's on this screen.
   *
   *  Zero is a real answer — a truck that loads and delivers the same day — and
   *  the card says so in words. Absent when the leg has only one of its two
   *  dates, which is the ordinary state of a rail booking. */
  transitDays?: number
  /** The leg's cost. Actual once the vendor invoice lands, planned before.
   *
   *  Both absent when the composer could not read the case's money at all —
   *  NOT when the caller may not see it, which is a state this server does not
   *  produce: figures go to everybody on the case and only the counterparty's
   *  identity is suppressed (AD-5 beside AD-6). */
  plannedExpense?: Money
  actualExpense?: Money
  /** What has actually been paid to whoever moves this leg, and what is still
   *  owed on it (issue #35 story 6). Composed per leg onto the CASE detail from
   *  the same money read the leg page's block comes from, so the card in the
   *  route section and the direction's own page cannot disagree.
   *
   *  Absent together with the two above, and for the same reason. */
  paidToCarrier?: Money
  carrierOutstanding?: Money
  /** The direction's own money block, served on the COMPOSED single-leg read
   *  (`GET /cases/:id/legs/:legId`) and on nothing else — the case detail's
   *  legs carry the four light figures above instead.
   *
   *  Absent on every leg that arrived through the case, which is why this is
   *  optional rather than required-and-possibly-empty: `undefined` here means
   *  "this leg was not read from the endpoint that composes money", and the
   *  finance card renders nothing at all rather than a zeroed one. */
  money?: LegMoney
  /** Why the block is absent, when the composer said. Exactly one of this and
   *  `money` is served by the composed read; both are absent on a leg that came
   *  through the case. */
  moneyUnavailable?: CaseMoneyUnavailable
  /** The FR-52 stall, on the leg it is happening to. The note is free text
   *  from operations — "loading slot confirmed, see chat". */
  stalledDays?: number
  stallNote?: string
  /** Last reported position, from the border agent's daily tracing. */
  lastLocation?: string
  lastReportedAt?: string
  /** The route chain (issue #35, story 2). The server never omits it — `[]` is
   *  a real answer, a leg nobody has given a chain — and `toCaseLeg` fills the
   *  empty list in, so a reader may map it without guarding.
   *
   *  Optional on the TYPE all the same, and not as a hedge against the wire:
   *  the fixtures in `src/mocks/` construct legs by hand and predate the chain,
   *  and a required field here would make them lie about having read one. */
  points?: LegRoutePoint[]
  /** Which point the cargo has reached, as the SERVER states it — the last one
   *  carrying a `reachedAt`. Never re-derived on this side: that is the rule
   *  `currentLeg` already states for the route, and two definitions of where a
   *  shipment is part company the first time one is corrected backwards. */
  currentPointId?: string
  /** When the NEXT point is due, computed server-side from the current point's
   *  arrival plus the next transition's normative days. Absent when there is no
   *  current point, no next one, or no norm on the transition — which is
   *  exactly when nothing can be late. */
  nextPointDueAt?: string
}

/** The seven participant types the directory record carried before S-22b
 *  (migration 0077) replaced them with a counterparty's `category`.
 *
 *  Kept for ONE reader: a bell row written before the change names a role
 *  through this vocabulary (`features/notifications/notificationMessages.ts`),
 *  and an old row must still be worded. No wire field carries it any more. */
export type ParticipantRole =
  | 'shipper'
  | 'consignee'
  | 'agent'
  | 'counter-agent'
  | 'vehicle-owner'
  | 'dispatcher'
  | 'driver'

export interface ExpenseLine {
  id: string
  description: string
  /** The movement this cost paid for, absent when it belongs to no single one
   *  — a clearance fee or a bank charge, which is the ordinary case (FR-67).
   *  The Money section links the row to that direction's page. */
  legId?: string
  /** The unit on that leg it paid for — a truck, a container, any kind
   *  (S-18b; `unit_id`, NOT the `unit` of measure beside it). Never served
   *  without `legId`: the server refuses the pair half-stated (0056's CHECK),
   *  so a row carrying a unit can always be attributed to a direction as
   *  well. */
  unitId?: string
  /** The generalized planned line this one details, absent for a top-level
   *  line. The Money table folds detail rows into a group under their parent;
   *  the parent's own plan no longer counts in the served totals. */
  detailOf?: string
  /** The operator's stored «не детализирует» (S-12, migration 0070): this
   *  per-truck PLAN deliberately details no plan line. Served only as `true`,
   *  only on a planned line with a leg (and, since S-18a, with or without a
   *  truck — the leg rate is a truck rate for S-12) and no `detailOf`; absent
   *  otherwise. Absent beside an absent `detailOf` is "nobody answered yet" —
   *  the state the double-count warning is about — not the same answer. */
  standsAlone?: boolean
  counterpartyName?: Maybe<string>
  /** The directory row the counterparty was picked from, or '' when the name
   *  was typed against a company with no record (FR-20).
   *
   *  A `Maybe` for the same reason the name is: the server suppresses it WITH
   *  the name and by the same call, because an id is a lookup key for the name
   *  it belongs to. The edit form reads it so that reopening a line cannot
   *  silently unlink a counterparty whose directory name has since changed. */
  vendorId?: Maybe<string>
  /** On an `unplanned` line this is a PLACEHOLDER — `0.00` in the actual's
   *  currency — not a plan anybody made; screens draw «—» from `unplanned`
   *  instead of printing it, and the expense sheet never sends it back. */
  planned: Money
  /** The actual row this line folded, when exactly one made it up. Absent on a
   *  plan with no actual yet, and on one folded from several — where an edit has
   *  to ask which. The server keeps planned and actual as separate rows (AD-4)
   *  and this is the id of the second one. On an `unplanned` line it equals
   *  `id`: there is no plan, and the actual's own id is the line's (S-4.1). */
  actualId?: string
  /** How many actual rows make up `actual`, present only when more than one —
   *  a plan settled by two vendors' invoices. */
  actualLines?: number
  /** A cost with no planned counterpart: legal, and shown as full variance.
   *  It STAYS unplanned — the folded PUT refuses a plan for it
   *  (`planned: unplanned-line`), because a plan typed after the fact is what
   *  hid the overrun (S-4.1). */
  unplanned?: boolean
  /** In the actual's own currency, which need not be the plan's. A line folded
   *  from several actuals in DIFFERENT currencies is served in USD from their
   *  booked equivalents — and has no `actualId`, so nothing edits it here. */
  actual?: Money
  /** Signed variance. A positive number means over plan. In the line's
   *  currency when plan and actuals share one; in USD, from the booked
   *  equivalents, when they do not (AD-9) — so it is never assumed to be in
   *  `planned.currency`. */
  variance?: Money
  /** How far the cost is PAID (S-4.2) — derived by the server from the
   *  payments allocated to the line's actuals, pending ones included, and
   *  never ticked by anybody: the manual `settled` flag it replaced was a box
   *  nothing kept true. Folded from several actuals it is `paid` only when
   *  every one is, `unpaid` when none has an allocation, and `partly-paid`
   *  otherwise; overpaid reads `paid`.
   *
   *  ABSENT on a plan with no actual (nothing has been invoiced, so there is no
   *  debt to be paid), and on a response that did not compute it. Absent is
   *  not `unpaid` — a screen says nothing rather than guess. Not suppressed for
   *  Sales: it names no counterparty. Read it, never re-derive it (see
   *  `Settlement`). */
  settlement?: Settlement
  /** What is still to pay on this line's ONE actual (S-18a), in that
   *  actual's own currency — the figure the carrier payment sheet defaults a
   *  row's amount to. Served ONLY on the leg money block's `expenses`
   *  (`LegMoney.expenses`), and only on a row with a single `actualId`;
   *  absent everywhere else, including the case's Money section. Zero once
   *  the line reads `paid`, so «оплачено» and the figure never disagree.
   *  Computed by the server from the allocations (`lineRemaining`), never
   *  here. Names no counterparty. */
  remaining?: Money
  /** Booking context read WITH the figure — "Booked in RMB · 120 120.00 CNY
   *  @ 7.15". Free text from the operator, rendered verbatim.
   *
   *  A `Maybe`: the server withholds it from a caller who may not see the
   *  vendor (AD-6, `finance/expense_dto.go`) and sends the marker in its
   *  place — the remark can name the counterparty. A screen prints it only
   *  when `!isSuppressed(note)`; printed as text the marker read
   *  «[object Object]». */
  note?: Maybe<string>
  /** The overrun is being passed through to the client and awaits Super-admin
   *  (FR-36). The table holds the row on an attention wash until decided. */
  pendingClientApproval?: boolean
}

/** Which figure a case's headline profit is (S-6). `forecast` while the case
 *  runs; `final` — the cash, received minus paid — once it is financially
 *  closed or cancelled. Decided by the server (`profitBasisOf`), never here. */
export type ProfitBasis = 'forecast' | 'final'

/** The terms every FR-30 figure is built from, all in USD and all from the
 *  one snapshot the three figures were computed from (S-6), so that
 *  expected = rate − plannedCost, forecast = rate + billedExtras +
 *  debitNoteRecoveries − cost, final = received − paid hold to the cent.
 *  PRINTED, never summed: the client states the server's arithmetic, it does
 *  not redo it (AD-9). */
export interface CaseProfitBreakdown {
  rate: Money
  plannedCost: Money
  billedExtras: Money
  debitNoteRecoveries: Money
  /** Actual costs, plus the planned cost of lines with no actual yet. */
  cost: Money
  /** Inbound money allocated to the case's tranches, at settlement rates. */
  received: Money
  /** Outbound payments on the case. */
  paid: Money
}

export interface CaseMoney {
  rate: Money
  /** Whether an agreed rate EXISTS. A case with none serves a zero rate, and
   *  the two are different facts: zero is a number a screen may state, and "no
   *  rate agreed" is a sentence it should say instead (Story 4.6). */
  hasRate?: boolean
  plannedCost: Money
  actualCost: Money
  /** FR-30's first figure: rate - planned costs. What the deal was signed to
   *  earn, known before a single invoice exists. */
  expectedProfit?: Money
  forecastProfit: Money
  /** The cash so far: received minus paid. Served on an open case too, where it
   *  is not yet the result — see `profitBasis`. */
  finalProfit?: Money
  /** The case's ONE profit figure (S-6), the right side of the money line and
   *  the header's «Прибыль»: `forecastProfit` while the case runs,
   *  `finalProfit` once it is financially closed or cancelled. Chosen by the
   *  server; absent only from a composer built without a finance service. */
  profit?: Money
  /** Which of the two `profit` is. Absent for a word this build does not
   *  know — the line then names no basis rather than guess one. */
  profitBasis?: ProfitBasis
  /** The terms behind the three figures, for «Как посчитано». */
  breakdown?: CaseProfitBreakdown
  /** Column sums for the plan-vs-actual table, SERVED not summed here (AD-9):
   *  actuals recorded so far, and the signed variance across them. Distinct
   *  from `actualCost`, which forecasts lines still waiting on a figure. */
  actualToDate?: Money
  costVariance?: Money
  /** What the client still owes on the rate after settled tranches (AD-9 —
   *  the subtraction happened where the decimals are exact). */
  clientReceivable?: Money
  /** What vendors are still owed on the ACTUAL basis (FR-30): actual cost
   *  lines net of the outbound payments allocated to them, vendorless lines
   *  dropped. A DIFFERENT figure from `suppliers.totals.outstanding` (forecast
   *  basis, vendorless lines included) — docs/llm-wiki/contracts/http-api.md. */
  payables?: Money
  expenses: ExpenseLine[]
  /** Expected tranches materialised at registration (AD-11, FR-33). */
  tranches: ReceivableTranche[]
  /** «Счета клиенту»'s tiles (S-11 revision, `finance.ClientTotalsDTO`): what
   *  the client was invoiced, what they paid, and the signed difference. Names
   *  nobody, so every money reader gets it. */
  clientTotals: CaseClientTotals
  /** «Затраты»' supplier ledger (S-11 revision, `finance.SuppliersDTO`): the
   *  forecast cost, the paid share and the payment slices per supplier for a
   *  reader who sees counter-agents, the same slices flat and nameless for one
   *  who does not, the totals for both. It REPLACED `payablesByCounterparty`. */
  suppliers: CaseSuppliers
  /** Legs whose per-truck plans neither detail a plan line nor stand alone,
   *  beside top-level plan lines nobody details (S-12) — the freight counted
   *  twice in the forecast. Decided by ONE SQL predicate on the server; absent
   *  when no leg is flagged and when the case's status does not accept money. */
  doubleCount?: CaseDoubleCount
  /** The case's outbound payments with an unallocated remainder (S-12, AD-11:
   *  never auto-applied, and no figure on the card moves because of them).
   *  Absent when there are none and when the status does not accept money. */
  unallocatedOutbound?: UnallocatedOutbound
}

/** `CaseMoney.doubleCount` (S-12). Both lists in the server's order: legs by
 *  their earliest-created flagged truck plan, and `planLineIds` resolving
 *  against `CaseMoney.expenses` of the same response. */
export interface CaseDoubleCount {
  legIds: string[]
  planLineIds: string[]
}

/** Outbound payments recorded but not (fully) allocated (S-12) — served on the
 *  case money block and at the top of `GET /finance/balances`. `count` is the
 *  number of PAYMENTS; `amounts` is one per-currency sum of their unallocated
 *  remainders, sorted by currency code, never converted or summed across
 *  currencies (AD-9). */
export interface UnallocatedOutbound {
  count: number
  amounts: Money[]
}

/** What `GET /finance/balances` leaves out of its rows on purpose (N-04,
 *  `finance.UninvoicedReceivableDTO`): instalments cut from a case's agreed
 *  rate that no issued invoice has billed yet. A tranche names nobody (AD-26),
 *  so it cannot be a balance row — the aging report and the dashboard still
 *  count it, and this is the figure that reconciles the three. USD at booked
 *  rates, a served decimal string (AD-9); follows the request's range and side;
 *  served even when the rows are withheld (it names no counterparty), and
 *  ABSENT when nothing is left out. */
export interface UninvoicedReceivable {
  amount: Money
  /** How many un-invoiced instalments the amount sums. */
  count: number
}

/** `CaseMoney.clientTotals` (S-11 revision, `finance.ClientTotalsDTO`).
 *
 *  USD except `receivedUnallocated`. Each figure is rounded once on the server
 *  and `debt` is `invoiced − received` of the rounded pair, so the three tiles
 *  subtract exactly and the sign of `debt` is judged at cents. PRINTED, never
 *  summed or subtracted here (AD-9). */
export interface CaseClientTotals {
  /** Issued invoices, plus issued debit notes addressed to the client (AD-26),
   *  in booked USD. */
  invoiced: Money
  /** The booked share of inbound allocations to tranches, invoices and
   *  client-owed debit notes, plus the USD of each inbound payment's
   *  unallocated remainder — NET of `refunded` (S-22f). */
  received: Money
  /** The inbound payments' unallocated remainders, one per currency in its own
   *  currency, sorted by code, never converted (AD-9) — each net of the
   *  refunds' unallocated remainder in the same currency, a currency netted to
   *  zero or below dropped (S-22f). `[]` when nothing is left unallocated. */
  receivedUnallocated: Money[]
  /** Money paid back to the client (S-22f): Σ over the case's refunds — an
   *  `out` payment naming the client — of each one's UNALLOCATED remainder, in
   *  USD. Always served ("0.00" when none). The client's figure, never a
   *  supplier's. */
  refunded: Money
  /** `invoiced − received`, SIGNED: negative is the client's advance. */
  debt: Money
}

/** `CaseMoney.suppliers` (S-11 revision, `finance.SuppliersDTO`).
 *
 *  ONE audience decision on the server, spent three ways: a reader who sees
 *  counter-agents gets `groups` and an EMPTY `slices`; one who does not gets an
 *  EMPTY `groups` and the same slices flat in `slices`, oldest first; both get
 *  `totals`, which name nobody. */
export interface CaseSuppliers {
  totals: SupplierTotals
  groups: SupplierGroup[]
  slices: SupplierSlice[]
}

/** «Оплачено» and «Остаток поставщикам», USD: Σ over every group, the
 *  unattributed one included. Plan and forecast are NOT repeated here —
 *  they are `plannedCost` and `actualCost` (the one-shape rule). */
export interface SupplierTotals {
  paid: Money
  outstanding: Money
}

/** One supplier's row and its slices (`finance.SupplierGroupDTO`). Served only
 *  to a reader who sees counter-agents. */
export interface SupplierGroup {
  /** "id:<uuid>", "name:<typed name>" or "unattributed" — stable across reads,
   *  the list key. */
  key: string
  /** `false` for «Без поставщика / не разнесено», the last group. */
  attributed: boolean
  /** The supplier's display name; absent on the unattributed group. */
  name?: string
  /** долг · оплачено · остаток, USD; `outstanding = debt − paid` on the wire,
   *  to the cent. */
  debt: Money
  paid: Money
  outstanding: Money
  slices: SupplierPaymentSlice[]
}

/** One slice of an outbound payment, or an outbound payment's unallocated
 *  remainder (`finance.SupplierSliceDTO`) — the row every audience may read.
 *  `LegSettlement`'s shape: `amount` in the payment's own currency, `usd` its
 *  booked share as a bare decimal string. */
export interface SupplierSlice {
  paymentId: string
  /** Absent on a remainder. */
  allocationId?: string
  /** The payment's unallocated remainder: it settles nothing and is in no
   *  figure (AD-11). */
  unallocated: boolean
  /** From the settled line; all three absent on a remainder. */
  expenseLineId?: string
  legId?: string
  unitId?: string
  reference: string
  /** Date-only (AD-19). */
  receivedOn: string
  paymentStatus: PaymentStatus
  amount: Money
  /** The booked USD share, absent on a remainder — which is never converted
   *  (S-12). */
  usd?: string
}

/** A slice inside a group (`finance.SupplierPaymentSliceDTO`): the shared row
 *  plus the payment's typed counterparty. AD-6 restricted — it exists on this
 *  type only, which reaches only a reader who sees counter-agents. */
export interface SupplierPaymentSlice extends SupplierSlice {
  counterpartyName: string
}

/** One expected receivable, as finance's TrancheDTO serves it (FR-32, FR-33).
 *
 *  Reshaped to the wire in Story 4.3's wiring pass. It used to carry `label` and
 *  `settled`, both of which were fixture prose: the server sends neither, and
 *  `settled` in particular cannot be filled — per-tranche settlement needs the
 *  allocations against that tranche, and the block serves only the case's total
 *  receivable. A field nothing can fill is the shape that made this section
 *  render an empty note for a series that existed. */
export interface ReceivableTranche {
  id: string
  /** 1-based, and the series' MEANING rather than a display index: "the first
   *  tranche" is what Operations chase. Never re-derived from array position,
   *  which is only correct while the list happens to be ordered. */
  sequence: number
  amount: Money
  /** Date-only (AD-19), or ABSENT for "no agreed date yet". Absent is a real
   *  answer and not overdue: the second half of a 50-50 case is created undated
   *  on purpose, and a date the platform invented would be chased. */
  expectedOn?: string
}

export interface CaseFile {
  id: string
  name: string
  sizeBytes: number
  /** The declared MIME type — how a browser should render the bytes.
   *
   *  NOT what kind of document this is: see `docKind`. The two were one field
   *  while the file area had nothing to group by, and `application/pdf` is the
   *  answer that made forty documents one heap. */
  kind: string
  /** What kind of document it is, as the uploader declared it (0045). A code
   *  from lib/fileKinds, rendered and grouped in the reader's language. */
  docKind: FileKind
  uploadedByName: string
  uploadedAt: string
  /** Queued uploads stay visible until the queue drains (AD-18, NFR-6). */
  pending?: boolean
}

/** One file carried by a chat message (FR-45, AD-18).
 *
 *  Metadata and an id — never the bytes and never the object key, exactly as
 *  CaseFile is. Rendering one means asking for a fresh signed URL, because a
 *  link minted when the panel opened is dead by the time somebody scrolls back
 *  to it.
 *
 *  `url` is therefore the EXCEPTION rather than the rule: it is set only for a
 *  local preview of something the user has just picked and not yet sent, where
 *  the "URL" is an object URL pointing at their own disk. A message that came
 *  back from the server never carries it. */
export interface ChatAttachment {
  id: string
  fileName: string
  /** The declared MIME type. Untrusted — what decides whether this renders
   *  inline is renderableKind(), not this string on its own. */
  contentType: string
  byteSize: number
  /** Local blob URL for a not-yet-sent attachment only. Absent once sent. */
  previewUrl?: string
}

export interface ChatMessage {
  id: string
  authorName: string
  authorId: string
  /** A short-lived signed link to the author's photo (FR-58, AD-18), absent
   *  when they have none — the ordinary case, which is why the bubble falls
   *  back to initials rather than treating this as missing data.
   *
   *  Absent on the fixture transcript, whose speakers have no account behind
   *  them. Own messages do not need it: the panel already holds the signed-in
   *  user's photo from the session, and prefers it over this. */
  authorAvatarUrl?: string
  body: string
  sentAt: string
  /** Files sent with this message. Absent on a text-only message rather than
   *  an empty array, so the common case costs nothing on the wire. */
  attachments?: ChatAttachment[]
  /** Optimistic messages render immediately and reconcile on ack. */
  pending?: boolean
  /** The permanent "edited" marker (0029).
   *
   *  A flag, not an instant: the panel renders "изменено" and never
   *  "изменено в 14:32". The server deliberately does not send the timestamp,
   *  because a time the client does not display is one somebody displays
   *  later, and then the transcript argues with itself about which time
   *  matters. */
  edited?: boolean
  /** A tombstone. The body is always empty when this is true — the server
   *  clears it in the database AND blanks it again at the mapper.
   *
   *  The row stays in the transcript with its author, because a hole where a
   *  coordination step was tells the next reader nothing, while "Aygül deleted
   *  a message" says a step was retracted. */
  deleted?: boolean
  /** Ids of the colleagues this message named (0030).
   *
   *  Ids rather than names, because the panel already holds the team's names
   *  and shipping them again would be a second copy of the roster that could
   *  disagree with the first. Absent on a message that named nobody. */
  mentions?: string[]
}

/** What a conversation hangs from (0029).
 *
 *  Mirrors the server's conversation_subject_kind domain. A union rather than
 *  a string so a typo is a compile error instead of a 404 nobody can explain. */
export type ChatSubjectKind = 'case' | 'quote'

/** One row of the chat page's list (0029).
 *
 *  Carries the SUBJECT rather than a route, so the client decides where the
 *  row links to — the same division of labour that has the server send an
 *  instant and the client render "4 minutes ago". */
export interface Conversation {
  id: string
  subjectKind: ChatSubjectKind
  subjectId: string
  /** A converted quote's thread: readable, not writable. */
  archived?: boolean
  /** Absent on a thread nobody has used yet. */
  lastMessageAt?: string
  /** This reader's count, capped at 99 by the server. Never another
   *  reader's — the watermark is per person and this is composed per
   *  request. */
  unread: number
}

/** One financial change, reverse-chronological and read-only (FR-37, AD-13).
 *
 *  The values are `Maybe`s rather than plain strings, and that is the whole of
 *  what AD-6 asks of this type: finance withholds an entry's transition from an
 *  audience that may not read it and sends the hidden marker in its place, as a
 *  PAIR, so neither half can be derived from the other. Typed as `string` the
 *  marker would arrive anyway and render as `[object Object]` or as nothing —
 *  and nothing is precisely the reading the marker exists to prevent, because a
 *  blank beside a colleague's name says "the vendor never changed".
 *
 *  The actor is never a `Maybe`: "who changed this" is the question FR-37
 *  exists to answer, and a colleague's name is not commercially sensitive. */
export interface AuditEntry {
  id: string
  actorName: string
  at: string
  /** What record the change sits on — finance's RecordKind (`payment`,
   *  `invoice`, `case`…), always sent. The field is worded by (kind, field):
   *  `status` means a different set of values on each kind
   *  (features/cases/lib/auditWording.ts). */
  recordKind: string
  /** The stable machine name; the client words it, never the server. */
  field: string
  oldValue?: Maybe<string>
  newValue: Maybe<string>
  /** Rate-change approvals carry their reason inline — the reason is the point.
   *  Suppressed with the values it explains: a reason routinely names the
   *  counter-agent in prose ("agreed lower rate with Hebei Cross"). */
  reason?: Maybe<string>
}

/** One recorded act on a case, from the global action log (FR-37).
 *
 *  The counterpart of AuditEntry above, and deliberately NOT merged with it:
 *  this one records what a PERSON did, at the grain of one act, and carries no
 *  values at all — the log stores ids and statuses only, which is exactly why
 *  it needs no suppression. A transition belongs in the trail, which knows how
 *  to withhold one.
 *
 *  `actorName` arrives denormalised at write time rather than as an id to
 *  resolve, so renaming an account cannot rewrite what history says happened. */
export interface CaseActionEntry {
  id: string
  occurredAt: string
  actorName: string
  /** The log's stable machine key ("case.status.change"); the client translates
   *  it, and prints it raw when it has no translation. */
  action: string
  /** Sub-record ids and annotations. Absent when the entry carries nothing
   *  beyond the act itself — the server omits an empty map rather than sending
   *  `{}`. */
  detail?: Record<string, string>
}

/** One line of the case history feed: an act, or a financial change (FR-37).
 *
 *  A union rather than one flattened row, because the two sources genuinely
 *  differ — one act versus one field transition — and flattening them would
 *  mean a row with an `action` and a `field` of which exactly one is ever set,
 *  which is a union spelled in a way the compiler cannot check.
 *
 *  `at` is hoisted out of both so the merge sorts ONE field rather than asking
 *  each entry which name its timestamp goes by. Underneath it each source keeps
 *  its own shape, unbent. */
export type CaseHistoryEvent =
  | { source: 'action'; at: string; entry: CaseActionEntry }
  | { source: 'audit'; at: string; entry: AuditEntry }

/** The client status email as the server would send it now (S-15) —
 *  `POST /api/cases/:id/status-update/preview`.
 *
 *  `html` is a COMPLETE document rendered by the same template and the same
 *  call the send uses, so what the sheet shows is what the client receives. It
 *  is shown only through `EmailFrame` (an empty `sandbox`), never joined to
 *  this application's DOM. */
export interface StatusEmailPreview {
  subject: string
  html: string
  /** The language the server rendered in — `ru` or `en`. */
  language: string
}

/** The consolidated status email's preview — `POST
 *  /api/cases/status-update/preview` — with the same covered/skipped partition
 *  the send answers with (`BulkStatusCase`, `BulkStatusSkip` in useCase). */
export interface BulkStatusEmailPreview extends StatusEmailPreview {
  covered: { caseId: string; caseNumber: string }[]
  skipped: { caseId: string; caseNumber: string; reason: string }[]
}

/** One status email sent on a case: a row of the send journal (S-15, migration
 *  0073), `GET /api/cases/:id/status-sends`, newest first.
 *
 *  The journal is the record of what the client was told; the status PDF it
 *  replaced is no longer filed. */
export interface StatusSendEntry {
  id: string
  sentAt: string
  /** The sender's name, joined from the accounts at read time. */
  sentByName: string
  /** The recipient's name as the email greeted them; empty when it had none. */
  recipientLabel: string
  /** The address it went to. ABSENT — the key, not a null — for a reader
   *  without `directory.contact.read` (NFR-3), the way `clientContactEmail` is
   *  absent on the case. */
  recipientEmail?: string
  /** The language the email was written in — `ru` or `en`. */
  language: string
  /** The case's status at the send, in the SERVER's spelling (`in_process`,
   *  `stuck`…), read through `statusFromWire`. */
  caseStatus: string
  /** Part of a consolidated send that covered several of the client's cases. */
  batch: boolean
}

/** One journal row with the email as it was sent — `GET
 *  /api/cases/:id/status-sends/:sendId`. The `html` is the stored body, shown
 *  through `EmailFrame` like the preview.
 *
 *  A CONSOLIDATED entry (`batch`) whose summary covered a case this reader
 *  cannot reach is served with `bodyWithheld: true` and NO `subject` and NO
 *  `html` keys: the summary prints every covered case, so its body would show
 *  shipments outside the reader's scope. The flag itself says only that at
 *  least one covered case is out of reach — not which, not how many. Every
 *  other entry is served with both keys and without the flag. A union rather
 *  than two optional fields, so a reader of `html` has to rule the withheld
 *  branch out first. */
export type StatusSendDetail = StatusSendEntry &
  ({ bodyWithheld?: false; subject: string; html: string } | { bodyWithheld: true })

/** One colleague assigned to a case (Story 3.6, FR-69).
 *
 *  Not suppressed for any role. These are COLLEAGUES — FR-29's rule is about
 *  the AGENT behind a rate, not about the people running the shipment — so
 *  every role that can see the case sees who is on it. A user who cannot tell
 *  whether the person they are about to phone is on the case cannot do their
 *  job. */
export interface CaseTeamMember {
  userId: string
  name: string
  role: Role
  /** The case's own owner, seeded onto the team when the case was created.
   *  Rendered differently, so the flag travels rather than being re-derived by
   *  comparing against the case.
   *
   *  It does NOT mean this row cannot be removed: the last-assignee rule
   *  (casefile.Service.Unassign) protects the last MEMBER, whoever that is, not
   *  the owner. An owner can be unassigned while a colleague remains, which is
   *  why `CaseSummary.ownerId` exists rather than the chat rule reading this
   *  flag. */
  owner: boolean
}

export interface CaseDetail extends CaseSummary {
  /** Who is assigned (FR-69). Absent on a response that did not read it, which
   *  is not the same as an empty team — a team is non-empty by the
   *  last-assignee rule, though its remaining member need not be the owner. */
  team?: CaseTeamMember[]
  /** The lifecycle moves this case may still make (FR-25, AD-24), computed
   *  server-side from the transition table — the SHAPE of the legal moves, and
   *  not a per-caller answer. It is NOT filtered by the viewer's capabilities,
   *  so a move only some desks may make is offered to all of them and refused
   *  at the write with a 403 (AD-21). A control worth hiding outright is gated
   *  at its own call site, the way the reopen action gates on `case.reopen`.
   *
   *  Optional, and the optionality carries meaning the way `team`'s does: an
   *  ABSENT list is a response that did not compute the moves, while an EMPTY
   *  list is the server saying there are none — a cancelled or financially
   *  closed case has nowhere left to go. The lifecycle surface renders nothing
   *  for either, but only one of the two would be a bug to fix here rather than
   *  on the server.
   *
   *  Read rather than re-derived from `status`: the client's own transition
   *  table (caseWire's CASE_TRANSITIONS) exists because a DRAG cannot wait for
   *  a round trip. A record screen can wait, and one copy of the rules beats
   *  two that drift apart. */
  nextStatuses?: CaseStatus[]
  /** Actual departure, once the cargo has moved; ETD stays the plan. The
   *  overview labels which of the two it is showing. */
  atd?: string
  /** The delivery term the quote stated, free text — "DAP" or "DAP Ashgabat" —
   *  copied onto the case at conversion (P0-2). Absent (`omitempty`) when the
   *  quote stated none. */
  incoterm?: string
  /** The transit time the quote promised, in days, either end optional
   *  (`casefile.CaseDTO.transitDaysMin/Max`). Served since the case columns
   *  existed and dropped by the wire mapper until P0-2. */
  transitDaysMin?: number
  transitDaysMax?: number
  /** Operations responsible. `ownerName` is the sales side — "who do I call"
   *  has two answers, and the card states both. */
  opsOwnerName?: string
  /** The ASA legal entity this case's documents issue from (FR-24, FR-64).
   *  Served by the real module, and absent (`omitempty`) when the entity row
   *  is gone; a reader dashes it with `||`, which also covers a blank. */
  entityName?: string
  /** Free text (FR-24), rendered verbatim. */
  notes?: string
  quoteId?: string
  quoteNumber?: string
  /** Whether the client's FINAL PACKING LIST is in hand (FR-12), carried over
   *  from the quote at conversion and editable afterwards.
   *
   *  Required rather than optional, because "no flag" is not one of the states:
   *  every case either has the client's list or is running on our estimate, and
   *  the overview says which. It is the first thing to check when the arrived
   *  volume disagrees with the booked one — a gap against the client's own list
   *  is a conversation about their packing, one against our estimate is a
   *  conversation about our estimating. */
  finalPackingListReceived: boolean
  /** The route as the four values the desk typed (FR-19, FR-24), served by
   *  `casefile/dto.go` and `omitempty` there. `route` below is the richer stop
   *  list with states, which only the showcase fixture serves today; the
   *  overview falls back to these when it is absent. */
  placeOfLoading?: string
  borderCrossing?: string
  placeOfDischarge?: string
  unloadingPlace?: string
  /** The cargo as the desk typed it — the overview's fallback when the
   *  structured `cargo` below is not served. */
  cargoDescription?: string
  /** The load the case carries as figures (`casefile.CaseDTO`; decimal strings,
   *  each `omitempty`, so absent means nobody stated it). The overview prints
   *  them beside `cargoDescription` when the structured `cargo` below is not
   *  served. */
  declaredWeightKg?: string
  measuredWeightKg?: string
  declaredVolumeM3?: string
  measuredVolumeM3?: string
  /** The server sends both flags on every read; they stay optional here only
   *  because the showcase fixtures speak `cargo` instead. */
  weightDiffers?: boolean
  volumeDiffers?: boolean
  route?: RouteStop[]
  cargo?: CaseCargo
  /** Every box on the shipment, rolled up from the legs (FR-67).
   *
   *  A UNION over `legs` rather than a list of its own, composed server-side —
   *  a container belongs to a MOVEMENT, and on a transhipped case the box that
   *  crossed the water is not the one that reached the consignee. Deliberately
   *  NOT de-duplicated by number: a box on two legs appears twice, because it
   *  was, and its seal legitimately differs between them once customs has
   *  opened and re-sealed it.
   *
   *  `legSequence` is what makes the flat list readable — two rows of container
   *  numbers mean nothing until you know which leg each was on. */
  containers: CaseContainer[]
  legs: CaseLeg[]
  /** The money block, or absent with `moneyUnavailable` naming why.
   *
   *  It used to be documented as "absent for roles without commercial access",
   *  and that state does not exist: finance answers not-found for a case a
   *  caller cannot reach, and AD-5 gives the money to the deal's own team. What
   *  absence actually meant was four different things, three of them real —
   *  which is what made an unpriced case render as a permission refusal.
   *
   *  One permission state came back with runtime scopes (story B3, fix round
   *  1): a custom role whose `case-money` is narrower than its `cases` reaches
   *  the case and not its money, and is told so as `forbidden` — a stated
   *  reason, never a bare absence, so it cannot be mistaken for the other two.
   *
   *  Present now whenever the server could compute it, empty figures included.
   *  A case with nothing recorded is a block of zeros beside `hasRate: false`,
   *  which the section states as "nothing recorded yet" (UX-DR-17). */
  money?: CaseMoney
  /** Why there is no money block (`CaseMoneyUnavailable`). Exactly one of this
   *  and `money` arrives; an absence with no reason is an older server, and
   *  reads as a failure because that is what it is. On `forbidden` every leg in
   *  `legs` also lacks its four money figures. */
  moneyUnavailable?: CaseMoneyUnavailable
  files: CaseFile[]
  /* No `history` here, and its absence is the point rather than an omission.
     The case's past is TWO reads — the action log and the financial audit
     trail — merged on the client (see features/cases/api/useCaseHistory). It
     was a field on this type for as long as the server answered it with a
     permanently empty array, which made a case with a year of changes on it
     render as "nothing has happened yet". */
}

/* ── Finance ──────────────────────────────────────────────────────────────── */

/** Which age group an outstanding item falls in (FR-34, AD-19).
 *
 * FIVE states, not three, and the two extra ones are the point:
 *
 *   * `not-yet-due` — expected in the future. Outstanding is not overdue, and
 *     a founder chasing what is merely outstanding burns the relationship on
 *     money that is not late.
 *   * `no-date` — no expected date was ever recorded. An undated receivable is
 *     a DATA PROBLEM to surface, not a number to default into `0-30`; it is
 *     also not chaseable, because nobody can say what it is late against.
 *
 * The keys are the server's own wire values, so the label comes from the
 * catalogue by key and the boundary arithmetic stays server-side (AD-19: the
 * comparison is date-only, or a bucket boundary moves between offices). */
export type AgingBucketKey = 'not-yet-due' | '0-30' | '31-60' | '60-plus' | 'no-date'

/** One bar of the aging summary — `finance.AgingBucketDTO`, served as `items`
 *  on GET /finance/aging.
 *
 *  `id`, NOT `key`. This surface was an `internal/showcase` fixture until Story
 *  6.4's real read replaced it, and the real read spells the field `id`. The
 *  rename is carried here rather than papered over with an alias, because a
 *  client reading `bucket.key` off the live response gets `undefined` — five
 *  bars keyed on nothing, with an untranslated label under each — and a shared
 *  type that hid that would be the fixture outliving the fixture.
 *
 *  No `label`: the id names the bucket and the client translates it. A
 *  server-sent label would arrive in one language and print in the other. */
export interface AgingBucket {
  id: AgingBucketKey
  amount: Money
  /** A decimal STRING like every other computed figure — share of the whole
   *  report, not of the visible buckets. One decimal place on the wire; the
   *  server keeps four in the arithmetic so a hundred small rows do not round
   *  themselves into visibly wrong shares. */
  sharePercent: string
  /** Whether this bucket is money that is LATE. `not-yet-due` and `no-date`
   *  are false, and that is what stops "outstanding" being read as "overdue".
   *
   *  The bucket's own answer, so the client styles from data rather than from a
   *  hard-coded list of which keys are red. The fixture flagged only `60-plus`,
   *  which was a styling hint wearing a definition's name. */
  overdue: boolean
}

/** One outstanding item behind the aging summary — the row a bucket total is
 *  made of, and what a founder lands on after clicking a bucket (UX-DR-10).
 *
 *  Served as `rows` on the SAME response as the buckets, and the two travel
 *  together on purpose: a bucket total fetched separately from the rows under it
 *  is exactly the disagreement AD-9's shared-basis rule exists to prevent.
 *
 *  `source` names the paper it came from, because the four age differently and
 *  are chased by different conversations. `debit-note` is the arm that was
 *  missing while Story 5.6 owned no table — migration 0047 gave the record one
 *  and `Repo.IssuedDebitNotesCompanyWide` reads it, so the note is now an
 *  ordinary fourth source rather than a kind nothing could produce. Hyphenated,
 *  like `expense-line` and unlike `AllocationTargetKind`'s underscored
 *  `debit_note`: two vocabularies, and converting between them is a mapping.
 *
 *  There is no `href`. The fixture served one; the real read serves `caseId`,
 *  and the route is the client's to build — a server that emitted client routes
 *  would be a second place the router lives. */
export interface AgedItem {
  /** The SOURCE row's id — a tranche, an invoice, a debit note, an expense
   *  line — never synthesised, so a founder reporting a wrong row can name it. */
  id: string
  source: 'tranche' | 'invoice' | 'debit-note' | 'expense-line'
  side: 'receivable' | 'payable'
  caseId: string
  /** '' for a case that has never had paper raised on it: finance labels a case
   *  from its OWN frozen documents, and a number nobody froze is a gap in what
   *  finance can say rather than a reason to hide the money. */
  caseNumber: string
  /** '' when suppressed for the reader's role (AD-6), as an expense line's
   *  vendor already is — the amount stays visible, the identity does not.
   *
   *  Also '' for a TRANCHE, which names nobody at all: an instalment is a slice
   *  of the case's agreed rate and no counterparty was ever written on it. A
   *  plain string rather than `Maybe` because here the two cases genuinely
   *  collapse — withheld and unattributed both render as a row with no name. */
  counterpartyName: string
  /** One of OUR OWN legal entities (FR-24), never the counterparty's, and ''
   *  for a source no entity raised. */
  entityName: string
  /** What is STILL OUTSTANDING on this item — the document's booked figure less
   *  what has been allocated against it — never a face value the reader would
   *  have to adjust in their head. USD at booked rates (AD-9, FR-54). */
  amount: Money
  /** Date-only (AD-19), and the EMPTY STRING rather than absent when nobody
   *  agreed one — which is precisely what puts the item in the `no-date`
   *  bucket. The key is always sent, so `expectedOn === ''` is the test and
   *  `expectedOn === undefined` is a response that did not come from here.
   *
   *  An INVOICE's date is the date of the tranche it bills, because an invoice
   *  has no due date of its own; one outside the tranche series therefore lands
   *  in `no-date` rather than being given a deadline nobody agreed. */
  expectedOn: string
  bucket: AgingBucketKey
}

/** One party's netted position — `finance.CounterpartyBalanceDTO`, served as
 *  `items` on GET /finance/balances (FR-35).
 *
 *  The whole LIST is the AD-6 decision here, not a field on it: a caller
 *  without counter-agent visibility gets `[]`, because a row reading "42 800
 *  payable, 3 open cases, 41 days" with the name removed is still a row about a
 *  company the reader was not supposed to know we deal with. Nothing on this
 *  shape is `Maybe`, and that is why. */
export interface CounterpartyBalance {
  /** An OPAQUE handle — the AD-11 counterparty key rendered `kind:uuid`. Stable
   *  across requests and unique within a response, so it is safe as a list key.
   *
   *  NOT an id to fetch anything by: for an unlinked party the uuid is derived
   *  from its NAME. `directoryId` is the field for that. */
  id: string
  /** The record behind this party (S-22f: every row is keyed by it) — the
   *  participant's id for a `participant` row, the CLIENT's id for a `client`
   *  row — and '' only for a party typed by name and linked to no record. The
   *  row opens `/counterparties/:id` or `/clients/:id` by it; an unlinked row
   *  opens nothing. */
  directoryId: string
  name: string
  /** TWO kinds, not four. Finance keys money under `client` or `participant`
   *  because `case_debit_notes.counterparty_kind` is the only place in its own
   *  tables a counterparty's TYPE is recorded, and this module may not learn the
   *  directory's vocabulary to add up money (AD-2).
   *
   *  `agent`, `counter-agent` and `carrier` were the FIXTURE's words and the
   *  live read never sends them. A facet offering all four now filters three
   *  values nothing can match; what sort of participant a party is remains the
   *  directory's question, not this report's. */
  kind: 'client' | 'participant'
  /** Which of OUR OWN entities holds the position, and '' when the party's
   *  documents span more than one — blank rather than the first one found,
   *  because naming one of two would be a statement the data does not support.
   *
   *  There is no `entityId`. The fixture served one and the real read does not,
   *  so a screen grouping cards per entity is grouping on DISPLAY TEXT: two
   *  entities renamed to read alike fold into one card whose figures net across
   *  both, and every row with a blank entity folds together as well. */
  entityName: string
  receivable: Money
  payable: Money
  net: Money
  openCases: number
  /** Age of the oldest unsettled item, date-only (AD-19). ALWAYS PRESENT, and
   *  SIGNED.
   *
   *  Negative means the earliest thing this party owes has not fallen due yet —
   *  the distinction the `not-yet-due` bucket draws, which an unsigned number
   *  cannot make. The fixture's figure was optional and always positive, and
   *  neither is true of the live read: "none" will never render again, and a
   *  cell that reddens on `> 60` must not redden on `-72` under an absolute
   *  value somebody adds to make the column look tidy. */
  oldestDays: number
}

/** One rate-change request as the cross-case HISTORY shows it (FR-53, FR-36,
 *  FR-37, Story 6.5 AC 5).
 *
 *  A different question from `ApprovalRequest`, which is the decision inbox:
 *  that one is pending-only and carries an action, this one is every entry
 *  regardless of outcome and carries none. Both are real surfaces and neither
 *  replaces the other.
 *
 *  `reason` is `Maybe` because the reason is restricted free text (AD-6): it
 *  can name a counter-agent, so a role that may not see that gets the explicit
 *  marker rather than a blank cell. */
export interface RateChangeHistoryEntry {
  id: string
  caseId: string
  caseNumber: string
  requesterName: string
  /** RFC 3339 — a request is an instant, not a date. */
  requestedAt: string
  reason: Maybe<string>
  current: Money
  requested: Money
  /** Signed: requested − current. A negative delta is a discount. */
  delta: Money
  status: 'pending' | 'approved' | 'rejected'
  /** '' until somebody has decided. The server sends the key rather than
   *  omitting it (approval_dto.go: an absent key and an empty string would
   *  mean the same thing, and `status` already answers "decided?"). */
  deciderName: string
  /** Date-only, absent while the request is still pending. */
  decidedOn?: string
  /** The case the request was made on. */
  href: string
}

/** The kinds of document a payment may be allocated against (AD-11).
 *
 * ALL THREE RESOLVE. `main.go` chains `WithTrancheAllocations()`,
 * `WithInvoiceAllocations()` and `WithDebitNoteAllocations()`, which closes the
 * registry: no kind a caller can name is unresolvable in a correctly wired
 * binary, and the allocation sheet may offer all three.
 *
 * What each kind OFFERS is not symmetric, and the asymmetry is the point rather
 * than an unfinished edge:
 *
 *   * `invoice` — ISSUED ones only. A draft creates no receivable (FR-62,
 *     AD-24), so there is nothing on it to settle.
 *   * `tranche` — only an instalment no issued invoice bills. An issued invoice
 *     naming a tranche DISPLACES it: the invoice takes its place in the registry
 *     and the tranche stops being a target. Without that exclusion a case with
 *     one 11 950.00 instalment and the invoice for it would report 23 900.00
 *     outstanding — the same money demanded twice — and FINANCIALLY CLOSED
 *     (FR-25) would be unreachable, because settling both requires paying twice.
 *   * `debit_note` — ISSUED ones only, on the same rule, and displacing nothing.
 *     Nobody quoted the detention, so no tranche anticipated it.
 *
 * The first three are RECEIVABLE-side and only an inbound payment settles one.
 *
 *   * `expense` — an ACTUAL cost line, and the only PAYABLE kind (0056, story
 *     5). It is what an outbound payment to a haulier settles, which is how the
 *     direction page can say «Оплачено 3 000 · Остаток 2 000» about a truck
 *     rather than about a case. A PLANNED line is refused by the server
 *     (`ErrExpenseLineIsNotADebt`): a plan is what the case expects to spend,
 *     nobody has invoiced it, and cash allocated against a forecast would leave
 *     the real cost reading as entirely unsettled beside it.
 *
 *     This comment used to end "a vendor cost is not a target: it is settled by
 *     marking the line paid, so a payables allocation sheet has nothing to offer
 *     and should not exist". That was true of the server it described and is not
 *     true of this one — `WithExpenseAllocations` registers the source. The
 *     manual `settled` flag it names is retired (S-4.2): a line's paid status
 *     is now derived from these allocations and served as `settlement`.
 *
 * UNDERSCORED. `debit_note` is the column's own domain value and it does NOT
 * match `DocumentKind`'s `'debit-note'` or `AgedItem['source']`'s `'debit-note'`.
 * Three spellings of one document is not a mistake to normalise away here —
 * each is what its own surface stores — but converting between them is a
 * mapping, never a cast. */
export type AllocationTargetKind = 'tranche' | 'invoice' | 'debit_note' | 'expense'

/** One receivable tranche as `GET /cases/:id/tranches` serves it (FR-32, FR-33).
 *
 *  NOT `ReceivableTranche`, and the two are deliberately separate shapes for two
 *  different questions:
 *
 *   * `ReceivableTranche` is the composed case detail's SUMMARY — a resolved
 *     `label` and a `settled` flag, for the sentence under the receivable figure.
 *     It is a read-model projection and carries no booking.
 *   * this is the finance module's own ROW: the id a `PATCH` addresses, the
 *     1-based `sequence` that is the series' meaning rather than an array index,
 *     the five AD-9 booking facts, and the nullable expected date.
 *
 *  `expectedDate` is `null` as a VALUE, never absent: null says "no agreed date
 *  yet, so not yet chaseable", which is a state the screen renders rather than a
 *  field the response forgot (see `tranches_dto.go`).
 *
 *  There is no outstanding or settled figure here, and that is the shape the
 *  server sends rather than an omission on this side — which is why the
 *  allocation picker can offer a tranche but cannot grey out one already paid.
 *  The server refuses the over-allocation either way (AD-11); what is missing is
 *  the discoverability, not the control. */
export interface CaseTrancheRow {
  id: string
  sequence: number
  amount: string
  currency: string
  appliedRate: string
  rateDate: string
  usd: string
  expectedDate: string | null
}

/** One split of a payment against one document (AD-11).
 *
 * Allocation is EXPLICIT: never inferred by date, never FIFO. This row exists
 * because a human said "this money settles that paper", and it is the only
 * thing that makes a receivable go down.
 *
 * The target is a `(kind, id)` pair with NO foreign key behind it and no join
 * the client can make: three kinds point at three different tables, so there is
 * one endpoint per kind and none of them is keyed on this pair. That is why the
 * server resolves `targetLabel` through its target registry, and why the label
 * is the only thing here a screen can render without another request. */
export interface Allocation {
  id: string
  paymentId: string
  targetKind: AllocationTargetKind
  targetId: string
  /** Human wording of what was settled — "tranche 2 of 2", "invoice INV-0001".
   *  Resolved server-side, because the client has no table to join against. */
  targetLabel: string
  /** In the payment's own currency, like the payment's `allocated` figure. */
  amount: Money
  /** The SETTLEMENT booking (AD-9): the rate this allocation actually settled
   *  at, which need not be the rate its target was booked at. */
  appliedRate: string
  rateDate: string
  usd: string
  /** Signed, four decimal places, USD. The gap between settling at
   *  `appliedRate` and the target's own booked rate, recorded EXPLICITLY —
   *  because AD-9 forbids rewriting the target's booked USD value to make the
   *  two agree. This is the one place two rates for one economic event coexist
   *  by design, and Story 4.6's final profit reads this column. */
  fxSettlementDifference: string
  allocatedAt: string
  /** The target's own facts (S-21), so a screen words it in the reader's
   *  language — «Счёт AG-INV-17», «Транш 2 из 2» — rather than printing the
   *  server's English `targetLabel`. `targetNumber` is an invoice's number, or
   *  a debit note's when the note is billed to the case's own client;
   *  `targetSequence` and `targetOf` a tranche's place in its whole series.
   *  Served to every case-money reader, Sales included (the owner's recorded
   *  exception: they name documents of the case's own client relationship).
   *  All ABSENT on a cost line, which keeps its machine `targetLabel`. */
  targetNumber?: string
  targetSequence?: number
  targetOf?: number
}

/** Whether a recorded payment has had its second pair of eyes (0059, L3).
 *
 * TWO states and there is no third. «Отклонён» is not one of them: a payment
 * Finance judges to be wrong is DELETED with a reason into the audit trail
 * (`DELETE /payments/:id`, L8), because a rejected row that stayed on the ledger
 * would be a figure every sum then has to remember to exclude.
 *
 * IT PARTICIPATES IN NO SUM (L4). A pending payment counts in «Оплачено», in
 * the balances, in the aging and in a leg's «Остаток» exactly as a confirmed one
 * does — the desk records money that moved, and confirmation is quality control
 * over the record rather than a gate in front of the money. Nothing on this side
 * may filter a list by status before adding it up. */
export type PaymentStatus = 'pending' | 'confirmed'

export interface Payment {
  id: string
  /** A payment always belongs to a case: it is money against one deal's
   *  paper, and a cross-case log is a view over case-scoped rows. */
  caseId: string
  reference: string
  /** '' when suppressed for the reader's role (AD-6), exactly as an expense
   *  line's vendor is — the amount stays visible, the identity does not. */
  counterpartyName: string
  /** The directory counterparty the payment names (migration 0080) — every
   *  outbound payment recorded since S-22d2 names one, or `clientId` below.
   *  ABSENT when the payment names no counterparty record (an inbound one, a
   *  refund to the client, an outbound one typed before the link existed), and
   *  absent too for a reader without `counterparty.read`: the payment's party
   *  is withheld WHOLE, name and ids together (AD-6). */
  counterpartyId?: string
  /** The case's client, when the payment names it by record — an inbound
   *  payment from the client, or a refund to it (0080). At most one of the two
   *  ids is ever present (`payments_one_party`). Absent on a typed payer, and
   *  withheld with the name. */
  clientId?: string
  direction: 'in' | 'out'
  method: PaymentMethod
  /** OUR OWN legal entity — which of the company's issuing entities received or
   *  paid the money. NOT the counterparty, who is `counterpartyName`. The two
   *  readings of "issuing entity" produce different foreign keys, and this is
   *  the one the server took. */
  issuingEntityId: string
  issuingEntityName: string
  amount: Money
  /** The AD-9 money facts, stored at booking and never recomputed for display:
   *  the rate applied, the date it was the rate FOR, and the USD figure that
   *  came out. A client that divided `amount` by `appliedRate` itself would be
   *  producing a second answer to a question the ledger has already answered. */
  appliedRate: string
  rateDate: string
  usd: string
  /** What the bank charged to move this money (migration 0058), with the rest
   *  of its own five AD-9 facts beside it — the fee is charged in its own
   *  currency, so it carries its own rate and its own USD figure.
   *
   *  ABSENT means no fee was recorded. It never means a fee of zero, and the
   *  four keys are optional together for exactly that reason: a `0.00` on this
   *  row would read on a report as a transfer that cost the company nothing,
   *  which is a claim nobody made.
   *
   *  IT IS NOT SETTLEMENT MONEY. `allocated` and `unallocated` below are about
   *  the payment AMOUNT alone (AD-11): a 5 000 transfer with a 25 fee settles
   *  5 000 of debt and cost 5 025, and adding the two would report a supplier's
   *  debt as settled by money they never received. Nothing on a screen may sum
   *  `amount` and `fee` into an allocatable figure. */
  fee?: Money
  feeAppliedRate?: string
  feeRateDate?: string
  feeUsd?: string
  receivedOn: string
  /** Unallocated cash is legal and visible; it is never auto-applied (AD-11).
   *  Both figures are in the payment's OWN currency, not USD. */
  allocated: Money
  unallocated: Money
  /** Always present, `[]` when none — an absent array and an empty one would
   *  read the same on screen and mean different things on the wire. */
  allocations: Allocation[]
  /** Whether Finance has confirmed this record (0059). Always present: the
   *  column is NOT NULL and the service states it on every insert, so an absent
   *  status here would be a client reading a server older than this feature.
   *
   *  A payment recorded BY Finance is born `confirmed`; one recorded by the
   *  logist through «Оплатить перевозчику» is born `pending` and waits. */
  status: PaymentStatus
  /** When the confirmation happened. Absent on a pending payment, together with
   *  the actor the audit trail carries — the two travel as a pair server-side
   *  (`payment_confirmation_is_whole`), and no screen in v1 asks for the name,
   *  so only the timestamp is on the wire. */
  confirmedAt?: string
  /** The SERVER's own verdicts on what THIS reader may do to this payment
   *  (S-21), computed by the predicates its writes ask (`payment_rights.go`):
   *  allocate it, reverse one of its receivable allocations, amend it, delete
   *  it. Always present. They name nobody — no recorder identity reaches the
   *  wire — and the acts on a payment are gated on them rather than on a
   *  capability re-derived here: the recorder of a pending client payment
   *  holds rights no key states. They do not read the case's lifecycle; a
   *  closed case still answers the act with its 409. */
  canAllocate: boolean
  canReverseAllocations: boolean
  canAmend: boolean
  canDelete: boolean
}

/** A payment as POST /finance/payments takes it.
 *
 * No `allocated` or `unallocated`: the server sets both, because cash lands
 * unallocated and is never auto-applied (AD-11). A client that sent them would
 * be stating the one number Finance has to decide for itself.
 *
 * No `appliedRate`, `rateDate` or `usd` either, for the neighbouring reason:
 * the booking is the server's (AD-9), taken from the FX cache at the case's
 * registration-date basis, and a rate the client offered would be a second
 * source for a figure that must have exactly one.
 *
 * `caseId` is carried in the body rather than the path because the Money page
 * records payments across cases; the per-case route takes the same shape minus
 * this field, from the path. */
export interface PaymentPayload {
  caseId: string
  reference: string
  /** The party's display name. On a payment that names a record (either id
   *  below) the server stores the RECORD's name and ignores this one; it is the
   *  payer only on an inbound payment with no `clientId`. */
  counterpartyName: string
  /** The party by record (S-22d2), at most one of the two — both is 422
   *  `clientId: with-counterparty`. An OUTBOUND payment needs exactly one:
   *  a directory counterparty (`counterpartyId`; unknown → `invalid`), or the
   *  case's own client for a refund (`clientId`; any other client →
   *  `invalid`); neither is `counterpartyId: required`, because a typed name
   *  is not a party. An INBOUND one may name the case's client, or neither
   *  and the typed payer above; a `counterpartyId` on it is
   *  `counterpartyId: direction`. */
  counterpartyId?: string
  clientId?: string
  direction: Payment['direction']
  method: PaymentMethod
  issuingEntityId: string
  amount: Money
  /** The bank's charge for moving the money (0058) — OPTIONAL, and omitted
   *  rather than sent as a zero when nobody recorded one. The server refuses a
   *  half-answered pair by field (`feeAmount`, `feeCurrency`), so a form that
   *  collected one half must send both or neither.
   *
   *  No rate here, for the reason the payment's own booking is absent: the
   *  server books it at the date the money moved (AD-20). */
  fee?: Money
  receivedOn: string
}

/** A client payment as `POST /cases/:id/payments` takes it since S-21: the
 *  payment (its case is the path's), the optional splits it settles in the
 *  SAME act, and the optional idempotency key the sheet generated when it
 *  opened.
 *
 *  The payment, every split, its audit and the pending notice are one
 *  transaction: a split over its document refuses the whole request and no
 *  payment is left behind. A retry carrying the same `clientKey` and the same
 *  payment fields answers `200` with the payment the first attempt recorded;
 *  different fields are 409 `idempotency-conflict`.
 *
 *  For a caller holding only `finance.payment.client` (Sales) the server
 *  forces `direction: 'in'` and the case's client as the party, discarding
 *  whatever the body named; a `finance.payment.record` holder's body is read
 *  as it stands, so the sheet names the case's client by id. At most 100
 *  splits (`splits: too-many`), each in the payment's currency. */
export interface CasePaymentPayload extends Omit<PaymentPayload, 'caseId'> {
  splits?: AllocationSplit[]
  clientKey?: string
}

/** One requested split, as POST /payments/:id/allocations takes it. */
export interface AllocationSplit {
  targetKind: AllocationTargetKind
  targetId: string
  amount: Money
}

/** Every split of one allocation request, sent as ONE call.
 *
 * The batch is not a convenience. The server takes the conservation checks
 * under `FOR UPDATE` inside a single transaction and commits only if every
 * split passes (AD-11), so splitting one request into three would let two land
 * and the third be refused — a payment half-applied to paper the user meant to
 * settle together. */
export interface AllocationPayload {
  splits: AllocationSplit[]
}

/** Which receivable a client payment can settle (S-21) — the allocation
 *  target kinds of money RECEIVED. */
export type ReceivableKind = Extract<AllocationTargetKind, 'invoice' | 'debit_note' | 'tranche'>

/** Where one receivable stands, judged by the server at cents: nothing
 *  allocated, part of it, all of it (a remainder under one cent reads paid),
 *  or more than its total. */
export type ReceivableStatus = 'open' | 'partial' | 'paid' | 'overpaid'

/** The figures every receivable row carries. Decimal strings throughout
 *  (AD-9); the pairs in the receivable's own currency, the bare strings USD.
 *  `outstanding` and `outstandingUsd` are SIGNED: an overpaid row's remainder
 *  is below zero. */
interface ReceivableFigures {
  id: string
  /** An invoice's GROSS total, VAT included. */
  total: Money
  totalUsd: string
  paid: Money
  paidUsd: string
  outstanding: Money
  outstandingUsd: string
  status: ReceivableStatus
  /** Whether a client payment may be split onto it now: the case takes money,
   *  the row is not displaced, and it is `open` or `partial`. */
  allocatable: boolean
  /** The most of the ASKED payment currency the server accepts against this
   *  row — present only when the read was asked with `payIn`, never negative,
   *  and absent on another-currency row when the payment date's rate is
   *  missing (`rateMissing`). */
  outstandingInPayment?: Money
}

/** One row of `GET /cases/:id/receivables` (S-21): an ISSUED invoice, an
 *  issued debit note billed to the case's own client, or a tranche — every
 *  tranche, those an issued invoice has replaced included (`replacedBy`).
 *
 *  It names documents and amounts and nobody: no client or counterparty name
 *  or id is on the wire, so the same payload is served to Finance and to the
 *  Sales user who runs the deal. A participant-billed debit note is not a row
 *  at all. */
export type Receivable = ReceivableFigures &
  (
    | {
        kind: 'invoice' | 'debit_note'
        /** The minted number — an issued document always has one. */
        number: string
        /** The document's own date, `YYYY-MM-DD`. */
        documentDate?: string
      }
    | {
        kind: 'tranche'
        /** The tranche's place in its WHOLE series, 1-based. */
        sequence: number
        of: number
        /** The agreed date, `null` while none is agreed — a value, never
         *  absent on a tranche. */
        expectedDate: string | null
        /** The issued invoice that replaced this tranche. A displaced tranche
         *  is never `allocatable`; the money it held before S-21 stays on it. */
        replacedBy?: { id: string; number: string }
      }
  )

/** `GET /cases/:id/receivables[?payIn=<ccy>&on=<YYYY-MM-DD>]` as a whole. */
export interface CaseReceivables {
  /** Whether the case's lifecycle still takes money; when false no row is
   *  allocatable. */
  acceptsMoney: boolean
  /** Whether the case names a client — a client payment needs one. */
  hasClient: boolean
  /** True when `payIn` was asked and no rate is cached for `on`. */
  rateMissing: boolean
  /** Invoices, then debit notes (by document date), then tranches by
   *  sequence. Always present, `[]` when the case owes nothing. */
  items: Receivable[]
}

export interface ApprovalRequest {
  id: string
  caseId: string
  caseNumber: string
  requesterName: string
  requestedAt: string
  reason: string
  currentRate: Money
  requestedRate: Money
  delta: Money
  status: 'pending' | 'approved' | 'rejected'
  deciderName?: string
}

/* ── Client invoices (FR-61, Story 5.4/5.5) ───────────────────────────────── */

/** Where an invoice STANDS — `finance.InvoiceState`, and the domain of
 *  `case_invoices.status` since migration 0048.
 *
 *  TWO stored states, and the missing third is the whole content of this type.
 *  AD-24 describes the lifecycle as Draft → Issued → Paid; `paid` is deliberately
 *  absent because being paid is not a move anybody performs — it is what the
 *  allocations against the invoice already say, and it is served separately as
 *  `settlement`. A union that admitted `'paid'` would be a second place one fact
 *  lives, and two places for one fact disagree on exactly the invoice somebody
 *  is arguing about.
 *
 *  Draft → Issued is ONE-WAY. There is no void: a correction is a new document,
 *  which is how the paper world does it anyway. So a client that renders an
 *  "unissue" affordance is offering an act with no route behind it. */
export type InvoiceStatus = 'draft' | 'issued'

/** How much of a demand has been PAID — `finance.Settlement`.
 *
 *  DERIVED server-side from the allocations against the document (AD-11, AD-24),
 *  never stored and never a third value of `InvoiceStatus`. On a document it is
 *  always present, including on a draft, where it is honestly `unpaid`: a
 *  document nobody has sent has been settled by nobody. A cost line carries the
 *  same three words for what has been paid against its actuals (S-4.2,
 *  `ExpenseLine.settlement`), absent on a line with no actual.
 *
 *  Do NOT re-derive this in the browser from `allocatedUsd` and `totalUsd`. That
 *  would be a second copy of `SettlementOf`'s three edge cases — a zero total, an
 *  over-allocation, a negative figure — written in a language with no decimals. */
export type Settlement = 'unpaid' | 'partly-paid' | 'paid'

/** The shipment block printed on an invoice (AC-2) — `finance.ShipmentDTO`.
 *
 *  An absent date prints NOTHING rather than a guess: a date the platform
 *  invented on a client's invoice is a date they can hold us to.
 *  `carriageDocuments` is always an array, `[]` when none, because an absent
 *  array and an empty one read the same on screen and mean different things on
 *  the wire. */
export interface InvoiceShipment {
  route: string
  carriageDocuments: string[]
  /** Date-only (AD-19), and `null` rather than absent when the shipment has no
   *  such date.
   *
   *  The distinction is the WIRE's, not a preference: `finance.ShipmentDTO`
   *  declares both as `*string` with no `omitempty`, so the keys are always
   *  present and carry JSON null. Typing them `etd?: string` said the opposite —
   *  that the key may be missing and can never be null — which type-checks every
   *  `?? ''` fallback while leaving `etd === null` looking impossible to the
   *  compiler. */
  etd: string | null
  eta: string | null
}

/** One charge on an invoice — `finance.InvoiceLineDTO`.
 *
 *  The AMOUNT is served and is not derived here: `quantity × unitPrice` rounded
 *  the server's way is the figure that prints, and a client that multiplied it
 *  itself would be a second implementation of the rounding step, free to
 *  disagree with the paper in front of the person being asked to pay.
 *
 *  `quantity` and `unitPrice` are '' rather than '0.00' when absent — for these
 *  two, zero and absent are the same thing (see migration 0036), and an editor
 *  pre-filled with 0.00 invites somebody to leave it there. `amount` is always a
 *  figure. `currency` is repeated per line even though an invoice has exactly
 *  one, because that is what the printed table shows per row. */
export interface InvoiceLine {
  id: string
  /** 1-based print order, the document's own meaning rather than an array
   *  index. */
  position: number
  description: string
  quantity: string
  unitPrice: string
  amount: string
  currency: string
}

/** One client invoice as the list serves it — `finance.InvoiceSummaryDTO`,
 *  returned under `invoices` by GET /cases/:id/invoices.
 *
 *  The same object WITHOUT its lines, and a distinct shape rather than
 *  `Invoice` with an optional array: a case with six tranches would otherwise
 *  ship six line tables to a screen that renders six rows.
 *
 *  There is NO suppression anywhere on this shape and none on `Invoice` either,
 *  and the absence is load-bearing rather than an oversight. An invoice names
 *  the CLIENT, who is not a protected identity — FR-29 protects the
 *  counter-agents behind a rate, not the party we are billing — and it must name
 *  no agent, vendor or supplier at all. A field withheld from Sales here would
 *  be a field that could be printed on the client's own copy: if it may not
 *  reach Sales it may not be here. Contrast `DebitNote`, which inverts this
 *  exactly.
 *
 *  Every money figure is a decimal STRING (AD-9). */
export interface InvoiceSummary {
  id: string
  caseId: string

  /** The denormalised header (AC-2): the invoice's OWN copies, not a live
   *  lookup. A renamed client does not rewrite an invoice already sent, which is
   *  why these are frozen text and not ids to join on. */
  caseNumber: string
  quoteNumber: string | null
  clientName: string
  clientCode: string | null
  customerReference: string | null

  /** `null` while draft, and the minted number from the moment it is issued
   *  (AD-12). Null as a VALUE rather than an absent key, because the client
   *  renders "no number yet" and "the server did not send the field"
   *  differently — and rendering a draft six times spends no numbers. */
  invoiceNumber: string | null

  /** Dates as YYYY-MM-DD (AD-19). An invoice dated the 3rd is dated the 3rd in
   *  Ashgabat and in Yiwu. */
  invoiceDate: string
  economicDate: string

  /** The AD-9 booking as served: the currency chosen, the rate it was booked at
   *  and the date that rate was FOR. Frozen at creation and never rewritten —
   *  served at all so the screen can explain the USD figure beside them. The
   *  rate keeps its stored precision rather than being rounded to money, because
   *  it is a ratio and a rounded one would fail to reproduce the USD total. */
  currency: string
  fxRate: string
  fxRateDate: string

  /** In the document's currency, and its booked USD equivalent. BOTH served
   *  rather than summed in the browser (AD-9). */
  totalAmount: string
  totalUsd: string

  /** The FR-10 tax block. `vatRate` and `vatAmount` are `null` when tax does not
   *  apply, so the screen cannot render "VAT 0.00" — which is a statement about
   *  a zero-rated supply rather than the absence of one. */
  vatApplies: boolean
  vatRate: string | null
  vatAmount: string | null

  shipment: InvoiceShipment

  /** Resolved from the CASE's payment terms (the entity states none) and
   *  READ-ONLY (AC-5, FR-64). Served so the screen can show what will print,
   *  never so it can be edited: there is no write path that accepts either. */
  paymentTerms: string
  bankDetails: string

  /** The two AD-10 figures, so the client composes its own refusal message from
   *  numbers it already holds rather than from numbers travelling inside an
   *  error. `approvedRateUsd` is `null` on a case with no agreed rate, which
   *  means there is no ceiling — not that the ceiling is zero. */
  approvedRateUsd: string | null
  billedUsd: string

  /** The STORED state. Never 'paid' — see `settlement`. */
  status: InvoiceStatus

  /** When the demand became binding, `null` on a draft (AD-12).
   *
   *  A DATE, not an instant, unlike `DebitNote.issuedAt` next door. The
   *  asymmetry is the server's and it is deliberate: what a client and a court
   *  care about is the DAY an invoice was issued, and the same instant is two
   *  different days in Ashgabat and Yiwu (AD-19). Do not feed this to a
   *  timestamp formatter. */
  issuedAt: string | null

  /** The instalment this invoice bills, `null` for one outside the series.
   *
   *  Served because the DISPLACEMENT is otherwise invisible: an issued invoice
   *  naming a tranche removes that tranche from the allocation registry, so a
   *  screen showing a case's tranches beside its invoices must be able to say
   *  WHICH tranche a demand replaced — or the row that quietly stopped being
   *  owed looks like data that went missing.
   *
   *  Read-only on this shape; the link is set on the write shape and frozen once
   *  issued. At most ONE issued invoice per tranche, which a partial unique
   *  index makes impossible rather than merely unlikely — two demands for one
   *  instalment is the failure it exists to refuse. */
  trancheId: string | null

  /** The DERIVED Paid state. Always present, `unpaid` on a draft. */
  settlement: Settlement

  /** Σ what has been paid against this invoice, at booked USD.
   *
   *  Served BESIDE `settlement` rather than instead of it, because the two
   *  answer different questions: 'partly-paid' is what a status chip renders,
   *  and 4 000.00 of 11 950.00 is what a person chasing the balance needs. */
  allocatedUsd: string

  /** The archive row this invoice was last rendered into. ABSENT — not null —
   *  until somebody renders it, because a client that has never seen the key
   *  behaves identically to one that reads null. */
  documentId?: string
}

/** One client invoice with its charges — `finance.InvoiceDTO`, served by
 *  GET/POST/PUT on /cases/:id/invoices/:invoiceId and by the line, render and
 *  issue routes.
 *
 *  Every route in the block answers this same shape, so a client refreshes from
 *  the response it already has rather than re-fetching after each edit. */
export interface Invoice extends InvoiceSummary {
  /** Always an array, `[]` when none — the state every invoice on a quote-less
   *  case starts in. */
  lines: InvoiceLine[]
}

/** An invoice HEADER as POST/PUT /cases/:id/invoices takes it.
 *
 *  Note what a client may not state. No rate, no rate date and no USD: the
 *  booking is resolved server-side from the FX cache (AD-9, AD-20), and a body
 *  that could carry a rate could book a demand at a rate of its own choosing. No
 *  client, codes or case number either — the printed header is copied from the
 *  case (AC-2), and a body that could state them could raise an invoice against
 *  a client the case does not have. No number, no status and no `issuedAt`:
 *  minting and the flip are one server-side transaction (AD-12), and a minted
 *  number on a record that stayed draft is a spent number nobody can find.
 *
 *  The lines are NOT here. An invoice is created as a header and its charges
 *  arrive one at a time on the line routes, so the AD-10 ceiling is checked
 *  against the figure being added rather than against a batch that could meet it
 *  at the end. `DebitNotePayload` is the deliberate opposite. */
export interface InvoicePayload {
  /** Required on create, and ABSENT from the edit path entirely: the document's
   *  currency is what its booking was resolved against, and changing it after
   *  the fact would rewrite a frozen rate. */
  currency?: string
  /** Date-only (AD-19). Both optional; the server defaults them. */
  invoiceDate?: string
  economicDate?: string
  customerReference?: string
  /** '' for an invoice outside the tranche series — a re-billing, a correction,
   *  a case with no series at all. Such an invoice displaces nothing and is a
   *  target in its own right. */
  trancheId?: string
  vatApplies: boolean
  /** Required when `vatApplies`, and DROPPED rather than refused when it is
   *  false: a form that remembers last week's figure with the box off is
   *  ordinary. */
  vatRate?: string
}

/** One charge as the invoice line routes take it.
 *
 *  The amount IS accepted here — an invoice line is a stated figure, and the
 *  quantity and unit price are how it was arrived at rather than the inputs it
 *  is computed from. That is the opposite of `DebitNoteLinePayload`, where the
 *  amount is derived and refusing to accept one is the whole point.
 *
 *  A decimal string, never a number: the server's binder REFUSES a JSON number
 *  (AD-9), so a client that computed money in the browser lands in a bind error
 *  rather than silently billing a float. */
export interface InvoiceLinePayload {
  description: string
  quantity?: string
  unitPrice?: string
  amount: string
}

/* ── Debit notes (FR-66, Story 5.6) ───────────────────────────────────────── */

/** Where a debit note STANDS — `finance.DebitNoteState`.
 *
 *  The same two words as `InvoiceStatus` and a SEPARATE type on purpose, exactly
 *  as the server keeps them separate. The two lifecycles agree today and they
 *  are not the same lifecycle: an invoice bills a tranche and an issued one
 *  displaces it, a debit note displaces nothing. A shared alias would make the
 *  next divergence a change to both documents at once. */
export type DebitNoteStatus = 'draft' | 'issued'

/** Which sort of directory record owes a note — `finance.DebitNoteCounterpartyKind`.
 *
 *  Exactly one id is set beside it, never both and never neither, which the
 *  table's own CHECK enforces rather than the client having to believe it.
 *
 *  A NEW note is billed to the case's client only (S-22a): the create refuses
 *  anything else with `counterparty: client-only`. 'participant' stays because
 *  notes raised before that still read, print, issue and take payments as
 *  stored — it is a word old rows carry, never one this client sends.
 *
 *  This kind is RESTRICTED (AD-6) and arrives as a `Maybe`, which is the
 *  non-obvious member of the suppressed group: 'participant' is the word that
 *  says this demand is owed by somebody other than the client — the
 *  counter-agent relationship itself, stated without naming anyone — and serving
 *  it beside a withheld name would give away the one thing the suppression
 *  exists to withhold. */
export type DebitNoteCounterpartyKind = 'client' | 'participant'

/** One charge on a debit note — `finance.DebitNoteLineDTO`.
 *
 *  The AMOUNT is served and is NOT writable: it is `unitPrice × quantity`,
 *  derived once on the server. A typed amount that disagrees with its own
 *  multiplication is a document that contradicts itself in front of the person
 *  being asked to pay it.
 *
 *  `unitPrice` and `quantity` keep FULL stored precision rather than money's two
 *  places, and both are always a figure rather than '': detention is billed in
 *  part-days, a rate of 12.5 per part-day rendered as 12.50 would still multiply
 *  correctly while a finer one would not, and a blank on this document would be
 *  a hole where the arithmetic goes. */
export interface DebitNoteLine {
  id: string
  position: number
  description: string
  /** The qualifying line under the description — "4 Containers (Open-top)".
   *  ALWAYS PRESENT, '' when there is none: the client renders a row either way,
   *  and a missing key on half the rows is a shape the editor would special-case
   *  into a bug. */
  note: string
  unitPrice: string
  quantity: string
  amount: string
  currency: string
}

/** One debit note — `finance.DebitNoteDTO`, returned under `debitNotes` by
 *  GET /cases/:id/debit-notes and bare by the other eight routes in the block.
 *
 *  ONE shape, lines included, served by every route INCLUDING the list — where
 *  the invoice serves a summary without its charges. The difference follows from
 *  what the documents are: an invoice can be summarised by its total, and a
 *  debit note that says only "USD 2,453.00" is precisely the document its
 *  recipient refuses, because which charge and on what authority is the entire
 *  question they are asking. One shape is also one anonymity boundary rather
 *  than two that can drift.
 *
 *  ── The AD-6 group, and why it is four fields ─────────────────────────────
 *
 *  An invoice names the CLIENT and carries no `Maybe` at all. A debit note names
 *  whoever owes it, and the ordinary shape of this document is that they are NOT
 *  the client — FR-66's own example bills a partner logistics company. That
 *  party is exactly what FR-29 protects, so `counterpartyKind`, `counterpartyId`,
 *  `recipientName` and `recipientAddress` are `Maybe` and travel together off ONE
 *  server-side decision. Test them with `isSuppressed` as a group; a screen that
 *  checked one and printed another is the leak the grouping exists to prevent.
 *
 *  The ID is in the group because a restricted field's id is restricted with it:
 *  an id is a lookup key for the name it belongs to, and served alone it hands
 *  over the directory row one request later.
 *
 *  The marker form (`{hidden:true}`) rather than absence, and that choice is
 *  itself the decision: a debit note with no visible counterparty is a demand
 *  whose recipient block reads as MISSING DATA, which sends a Sales user to ask
 *  an operator to fix a document that is perfectly fine.
 *
 *  What is NOT suppressed is the MONEY — every amount, the currency, the booked
 *  rate, both totals. That asymmetry is the entire point of FR-29: a deal's own
 *  team sees that deal's money, and what is withheld is who is on the other side
 *  of it. */
export interface DebitNote {
  id: string
  caseId: string
  /** The note's OWN copy, not a live lookup — a case renumbered does not rewrite
   *  a note already sent. */
  caseNumber: string

  counterpartyKind: Maybe<DebitNoteCounterpartyKind>
  counterpartyId: Maybe<string>
  recipientName: Maybe<string>
  recipientAddress: Maybe<string>

  /** "Your Ref." — and deliberately NOT in the group above. It is the
   *  recipient's own reference for the JOB (a purchase order, a booking number),
   *  so it identifies a shipment rather than a company, and it is typed by us
   *  onto paper we send them rather than read out of the directory. Suppressing
   *  it would withhold a string the deal team put there. */
  recipientReference: string | null

  /** `null` while draft; minted at issue from the entity's DEBIT-NOTE series,
   *  which AD-25 keeps separate from the invoice series precisely so a company
   *  can number the two apart (AD-12). */
  noteNumber: string | null

  /** Dates as YYYY-MM-DD (AD-19) — what the document says about itself. */
  noteDate: string
  economicDate: string

  /** When it happened. An RFC 3339 INSTANT, `null` while draft — and NOT the
   *  date-only shape `Invoice.issuedAt` uses. The difference is the server's and
   *  it is deliberate: the two above are what the document says about itself and
   *  this is an ORDERING fact, because two notes issued on one afternoon are
   *  distinguished by nothing else. */
  issuedAt: string | null

  /** The AD-9 booking, frozen at creation and never rewritten. */
  currency: string
  fxRate: string
  fxRateDate: string

  /** In the note's currency and at booked USD. Both SERVED (AD-9). */
  totalAmount: string
  totalUsd: string

  /** Resolved from the issuing entity and READ-ONLY (FR-64). Served so the
   *  screen can show what will print, never so it can be edited — a demand with
   *  a typeable bank account is one somebody can misdirect.
   *
   *  There is no payment-terms line, and its absence is a decision: the terms
   *  were agreed for the freight an invoice bills, and printing them beside a
   *  detention charge would state a due date nobody agreed to. */
  bankDetails: string

  status: DebitNoteStatus

  /** Absent until somebody renders it, for `InvoiceSummary.documentId`'s
   *  reason. */
  documentId?: string

  /** Always an array. A stored note has at least one charge — the create route
   *  refuses an empty one, so there is no moment at which a debit note with no
   *  stated reason exists. */
  lines: DebitNoteLine[]

  /* Note what is NOT here: `settlement` and `allocatedUsd`. An issued debit note
     IS an allocation target and money can be applied to it, but this DTO serves
     no derived Paid state the way the invoice does — so a screen that wants to
     show a note as settled has to read it from the payment's allocations, and
     one that renders a chip from `note.settlement` is reading `undefined`. */
}

/** A debit note as POST/PUT /cases/:id/debit-notes takes it.
 *
 *  The CHARGES travel with the create, where `InvoicePayload` carries a header
 *  alone. That is not a convenience: a debit note with no charges is a demand
 *  for money with no stated reason, so there is no useful moment at which an
 *  empty one exists, and creating it in one request is what stops that moment
 *  existing at all. The edit path ignores `lines` — the three line routes amend
 *  a record that always has at least one.
 *
 *  Neither the AD-9 booking nor the printed recipient is here. The name and
 *  address are RESOLVED from the party the counterparty pair names (AD-26), and
 *  a body that could state them could address a demand to a company nobody
 *  picked — the failure the whole counterparty design exists to prevent,
 *  arriving through the front door. */
export interface DebitNotePayload {
  /** The AD-26 pair. On the create it is always `client` and the case's own
   *  client id (S-22a) — the server answers `counterparty: client-only` to any
   *  other pair. The header amend sends none (`DebitNoteHeaderPayload`). */
  counterpartyKind: DebitNoteCounterpartyKind
  counterpartyId: string
  currency: string
  /** Date-only (AD-19); both optional, the server defaults them. */
  noteDate?: string
  economicDate?: string
  recipientReference?: string
  /** Sent on create, ignored on edit. */
  lines?: DebitNoteLinePayload[]
}

/** A draft debit note's header as `PUT /cases/:id/debit-notes/:noteId` takes it.
 *
 *  NO counterparty pair (S-22a). The amend keeps the stored payer and the
 *  printed recipient it was frozen with — the server ignores a pair in the body
 *  — so an old note billed to a participant keeps that party while its date and
 *  reference stay editable, and a reader whose role hides the payer can still
 *  save it: nothing here names anybody. The currency is echoed from the record
 *  and ignored by the route; the charges travel on the three line routes. */
export type DebitNoteHeaderPayload = Omit<
  DebitNotePayload,
  'counterpartyKind' | 'counterpartyId' | 'lines'
>

/** One charge as the debit-note create and line routes take it.
 *
 *  NO AMOUNT, and that is the point: it is derived from the unit price and the
 *  quantity, once, on the server. A field here would be a fourth number able to
 *  disagree with the other three on the face of the document, and the
 *  disagreement would be discovered by the person being asked to pay.
 *
 *  Both figures are decimal STRINGS. The server's binder refuses a JSON number
 *  (AD-9). */
export interface DebitNoteLinePayload {
  description: string
  /** '' when there is none, rather than omitted — the field the printed row
   *  always has. */
  note?: string
  unitPrice: string
  quantity: string
}

/* ── The Documents surface ────────────────────────────────────────────────── */

/* What the case's Documents section and the Money page's Documents tab render.
   Both read the SAME rows the two blocks above describe — client invoices and
   debit notes — through one view type assembled on this side, so the two screens
   cannot grow different vocabularies for one piece of paper. */

export type DocumentKind = 'invoice' | 'debit-note' | 'offer'

/** Where a document STANDS, as a reader of the screen understands it.
 *
 *  The server stores TWO states — see `InvoiceStatus` and `DebitNoteStatus`,
 *  both of which are `draft | issued` and both of which are deliberately kept
 *  apart there. `part-paid` and `paid` have no stored counterpart at all and are
 *  DERIVED here from the served figures by `documentStatusOf` below, which is
 *  the same stance `invoice_lifecycle.go` takes on the server: a stored column
 *  able to hold "paid" would be a second place the answer lives, and the two
 *  places disagree the first time an allocation is written without it.
 *
 *  There is NO `void`, and its absence is a decision rather than an omission.
 *  AD-24 makes Draft → Issued one-way and issue #26 deliberately added no route
 *  back, because a correction to a document that has been sent is a NEW document
 *  — which is how the paper world does it and how a numbering scheme stays
 *  auditable. A `void` member would therefore be an affordance for a state
 *  nothing in this system can produce: the pill would exist, the tone table
 *  would have a row for it, and no record would ever wear it. That is the same
 *  defect the money section had when it offered "money is not shown for your
 *  role" to a founder who held every role. */
export type DocumentStatus = 'draft' | 'issued' | 'part-paid' | 'paid'

/** The status pill's word, derived from what the server served.
 *
 *  ONE helper with two call sites — the case's Documents section and the Money
 *  page's — rather than the same four-branch `if` inlined in both. Two copies of
 *  a derivation is two places to fix when the rule moves, and the failure mode
 *  is not a crash: it is one screen calling a document part-paid while the other
 *  still calls it issued, which is the sort of disagreement a person resolves by
 *  distrusting both.
 *
 *  `Number()` on the decimal strings is a COMPARISON, not a recomputation, and
 *  the distinction is exactly the one `CaseDocuments.tsx` draws about `overdue`:
 *  AD-9 governs money ARITHMETIC — no figure printed, sent or stored may come
 *  out of a float — and nothing here is printed. The figures being compared were
 *  both computed on the server, and asking "is this one zero" of a value that
 *  has been through a double cannot move a cent, because there is no cent on the
 *  other side of it. A helper that parsed these to decimals to answer a
 *  yes/no question would be precision spent on a question that has no
 *  precision. */
export function documentStatusOf(doc: {
  issued: boolean
  paid: Money
  outstanding: Money
}): DocumentStatus {
  // Asked first, because an unissued document is a draft whatever its figures
  // say — and a draft's figures are what it WOULD demand, not what is owed.
  if (!doc.issued) return 'draft'

  const paid = Number(doc.paid.amount)
  const outstanding = Number(doc.outstanding.amount)

  // Nothing has been allocated against it yet, which is every document's state
  // for the whole interval between issuing it and being paid.
  if (paid <= 0) return 'issued'

  // Zero outstanding WITH something paid is settled. The `paid > 0` half is
  // load-bearing: a zero-value document would otherwise read as paid the
  // instant it was issued, having demanded nothing.
  return outstanding <= 0 ? 'paid' : 'part-paid'
}

/** One piece of paper as both Documents surfaces render it.
 *
 *  A VIEW, assembled on this side from an `InvoiceSummary` or a `DebitNote` —
 *  NOT a wire type. Nothing on the server serves this shape and no endpoint
 *  should be expected to: `GET /cases/:id/invoices` and
 *  `GET /cases/:id/debit-notes` are two reads of two tables with two different
 *  anonymity boundaries, and the screen that lists them together is the only
 *  place they are one thing. Keeping the assembly here rather than in the
 *  section's own component is what lets the Money page render the identical row.
 *
 *  It REPLACES `FinanceDocument`, the shape the `internal/showcase` fixture
 *  invented: five statuses for a system with two stored states, a `number` that
 *  was the string "DRAFT" on an unissued document, and no notion at all of
 *  whether the record had been rendered. Every field below is either served or
 *  derived from something served.
 *
 *  ── Why `paid` and `outstanding` are USD and `total` is not ────────────────
 *
 *  `total` is the FACE of the document, in the currency it prints in: an
 *  invoice raised in CNY says CNY on the paper, and rendering it as USD would
 *  show the client a figure their copy does not contain. The other two are the
 *  settlement side, and allocations are booked in USD at the rate the server
 *  resolved (AD-9, AD-11) — `InvoiceSummary.allocatedUsd` is USD and there is
 *  no per-currency counterpart to it. Subtracting a USD allocation from a CNY
 *  face value to get a third figure would be arithmetic across currencies, and
 *  the answer is a number nobody can read: not CNY, not USD, and wrong in both.
 *  So the two sides are carried in the units they were booked in, and the screen
 *  labels each with its own currency rather than pretending they share one. */
export interface CaseDocument {
  id: string
  kind: 'invoice' | 'debit-note'
  caseId: string
  caseNumber: string
  /** null while draft; the minted number from the moment it is issued (AD-12). */
  number: string | null
  /** Maybe<string> because a debit note's recipient is suppressed for a caller
   *  without agent-identity visibility (AD-6) and an invoice's client name is not. */
  counterpartyName: Maybe<string>
  entityName: string
  issued: boolean
  /** Date-only; null while draft. */
  issuedOn: string | null
  documentDate: string
  /** The face of the document, in ITS currency. */
  total: Money
  /** What has been allocated against it, at booked USD. */
  paid: Money
  /** total minus paid, at booked USD. */
  outstanding: Money
  /** The archive row this was LAST rendered into. Absent when there is no
   *  current render — the server clears it on every draft write. */
  documentId?: string
}

/** One row of the company-wide register of ISSUED paper
 *  (`GET /api/finance/issued-documents`, N-30, `finance.IssuedDocumentDTO`).
 *
 *  Not a `CaseDocument`: the register holds issued paper only (a draft raises
 *  no receivable), so there is no `issued` flag, and `paid` / `outstanding` are
 *  both booked USD whatever currency `total` is in. `counterpartyName` is a
 *  `Maybe` — withheld without `counterparty.read`. `documentId` is `omitempty`
 *  on the wire, so optional here and never `| null`: absent when there is no
 *  archive row to open. */
export interface IssuedDocument {
  id: string
  kind: 'invoice' | 'debit-note'
  caseId: string
  caseNumber: string
  number: string
  counterpartyName: Maybe<string>
  entityName: string
  /** Date-only, as printed on the paper. */
  documentDate: string
  /** RFC 3339 instant. */
  issuedAt: string
  /** The face, in the document's own currency (an invoice's gross of tax). */
  total: Money
  /** What has been allocated against it, booked USD. */
  paid: Money
  /** Still owed, booked USD, SIGNED (an over-allocation is negative). */
  outstanding: Money
  documentId?: string
}

/** `GET /api/finance/issued-documents`: newest first, at most 500 rows, with
 *  `total` the number that matched the period. */
export interface IssuedDocumentsPage {
  items: IssuedDocument[]
  total: number
}

/** One row of the document ARCHIVE, as `GET /api/documents?sourceKind=…&sourceId=…`
 *  serves it — `document.ArchivedDocumentDTO` (backend/internal/document/dto.go),
 *  under `{ items }`.
 *
 *  Not a `CaseDocument`, and the difference is the audience: that one is
 *  Finance's (amounts, recipient, outstanding), joined from two reads only a
 *  render key opens. This is the archive's existence record, open to every
 *  holder of `document.archive.read` — kind, number, dates, the issuing
 *  entity's trading name, and nothing that prints money or a counterparty.
 *
 *  Only the fields the read-only «Бумаги работы» view renders are mirrored
 *  (the DTO also carries source ids, entity id, size, content type, checksum
 *  and the renderer's user id, which this client never shows). `number` and
 *  `issuedAt` are `omitempty` on the wire, so they are optional here and never
 *  `| null`: absent until the document is issued. */
export interface ArchivedDocument {
  id: string
  /** The wire's string, not a closed union: `invoice`, `debit-note`, `offer`
   *  and `status-update` today (platform/docs `Kind`), and a kind the server
   *  adds later must render with a fallback label rather than fail a type. */
  kind: string
  entityName: string
  /** Absent until issued. */
  number?: string
  /** Date-only — the date printed on the document. */
  documentDate: string
  issued: boolean
  /** RFC 3339, absent until issued. */
  issuedAt?: string
  /** RFC 3339 — when these bytes were archived. */
  renderedAt: string
}

/** An invoice summary as the Documents surfaces render it.
 *
 *  `outstanding` is the one figure computed here rather than served, and it is
 *  the narrow exception AD-9 tolerates for the same reason `rateCeilingBreach`
 *  and the editor's running total do: it is DISPLAY arithmetic on two figures
 *  the server already booked, in one currency, read and never sent back. The
 *  server has no `outstandingUsd` field to serve — `totalUsd` and
 *  `allocatedUsd` are what it publishes — so the alternative is not a more
 *  precise number, it is no number, and the person chasing a balance is the one
 *  who needs it most.
 *
 *  `counterpartyName` is wrapped as a `Maybe` holding a plain string rather than
 *  being widened at the call site, because an invoice's client name is NEVER
 *  suppressed (see `InvoiceSummary`) — the honest statement is "this value is
 *  present", not "this field has no suppression". A screen tests it with
 *  `isSuppressed` either way and gets the right answer for both kinds. */
export function toCaseDocumentFromInvoice(inv: InvoiceSummary): CaseDocument {
  return {
    id: inv.id,
    kind: 'invoice',
    caseId: inv.caseId,
    caseNumber: inv.caseNumber,
    number: inv.invoiceNumber,
    counterpartyName: inv.clientName,
    // NOT served. `InvoiceDTO` carries the issuing entity's paymentTerms and
    // bankDetails — what will PRINT — and never the entity's name, so there is
    // nothing here to read. Left empty rather than filled from the case's own
    // entity picker: an invoice's issuing entity is frozen onto the invoice at
    // creation, and showing today's answer beside a document raised under a
    // different one would be a caption that quietly lies.
    entityName: '',
    issued: inv.status === 'issued',
    issuedOn: inv.issuedAt,
    documentDate: inv.invoiceDate,
    total: { amount: inv.totalAmount, currency: inv.currency },
    paid: { amount: inv.allocatedUsd, currency: 'USD' },
    outstanding: {
      amount: (Number(inv.totalUsd) - Number(inv.allocatedUsd)).toFixed(2),
      currency: 'USD',
    },
    ...(inv.documentId === undefined ? {} : { documentId: inv.documentId }),
  }
}

/** A debit note as the Documents surfaces render it.
 *
 *  `paid` is 0.00 and `outstanding` is the whole face value, ALWAYS, and that is
 *  the truth rather than a gap papered over. `DebitNoteDTO` serves no
 *  `settlement` and no `allocatedUsd` — its own comment in this file says so and
 *  explains why: an issued note IS an allocation target, but what has been
 *  applied to it lives on the PAYMENT's allocations, and this DTO does not join
 *  them. So `documentStatusOf` only ever answers 'draft' or 'issued' for a debit
 *  note. That is exactly what the server knows about it.
 *
 *  The alternative — omitting the two fields, or leaving them undefined — would
 *  force every renderer to branch on kind before it could show a column, which
 *  is the fork this view type exists to prevent. Stating "nothing recorded here"
 *  in the note's own currency is the honest zero: it is not a claim that nobody
 *  has paid, it is the figure this read carries.
 *
 *  `recipientName` travels through as the `Maybe` the server sent (AD-6). It is
 *  NOT unwrapped here: the whole point of the marker form is that a suppressed
 *  recipient renders as withheld rather than as blank, and flattening it to ''
 *  in a mapper is precisely how "we may not tell you" becomes "nobody filled
 *  this in". */
export function toCaseDocumentFromDebitNote(note: DebitNote): CaseDocument {
  return {
    id: note.id,
    kind: 'debit-note',
    caseId: note.caseId,
    caseNumber: note.caseNumber,
    number: note.noteNumber,
    counterpartyName: note.recipientName,
    // Not served here either — a note carries the entity's bankDetails and not
    // its name, for `toCaseDocumentFromInvoice`'s reason.
    entityName: '',
    issued: note.status === 'issued',
    // `issuedAt` is an RFC 3339 INSTANT on a debit note, not the date-only shape
    // an invoice's is — the server's own asymmetry, because two notes issued in
    // one afternoon are ordered by nothing else. Sliced to the day here because
    // this view is date-only throughout (AD-19) and a column that formatted one
    // kind as a timestamp and the other as a date would be two answers to one
    // question. The ordering fact stays available on the note itself.
    issuedOn: note.issuedAt === null ? null : note.issuedAt.slice(0, 10),
    documentDate: note.noteDate,
    total: { amount: note.totalAmount, currency: note.currency },
    paid: { amount: '0.00', currency: note.currency },
    outstanding: { amount: note.totalAmount, currency: note.currency },
    ...(note.documentId === undefined ? {} : { documentId: note.documentId }),
  }
}

/* The `internal/showcase` Documents fixture is GONE from this file.

   `DocumentLine`, `DocumentPayload` and `FinanceDocument` described
   GET/POST /api/finance/documents — a fixture held in the server process's
   memory, whose vocabulary was its own: five statuses for a system with two
   stored states, a `number` that was the string "DRAFT" on an unissued
   document, a due date neither real record carries, and no notion at all of
   whether the paper had been rendered. Nothing raised through it reached
   `case_invoices` or `case_debit_notes`.

   `CaseDocument` above replaces all three, and every field on it is either
   served or derived from something served. The write shapes are the two real
   ones — `InvoicePayload` and `DebitNotePayload` — which is why there is no
   third: the two creates differ (a header against a header WITH its charges),
   and one merged payload is what made "invoice with lines" expressible in the
   type when the server refuses it (issue #27). */

/* ── Directories ──────────────────────────────────────────────────────────── */

/** One contact at a client or participant — `directory.ContactDTO`.
 *
 * Every field below the label is PII (NFR-3) and suppressed by the same
 * mechanism as agent identity: a role without permission receives the marker,
 * never an empty string, so "withheld" is never read as "nobody filled it in".
 *
 * The messenger handles are contact information only. The platform does not
 * send to them (FR-41) — messaging stays a manual channel outside it. */
export interface DirectoryContact {
  id: string
  /** Who this is, e.g. "Sun Di". Not PII; visible to every permitted role. */
  label: string
  email: Maybe<string>
  phone: Maybe<string>
  wechat: Maybe<string>
  telegram: Maybe<string>
  whatsapp: Maybe<string>
  isPrimary: boolean
}

/** One client company — `directory.ClientDTO`.
 *
 * Rows are scoped server-side (AD-5): a Sales user's list holds the clients
 * they own, and the common pool is not browsable by them. Nothing here reports
 * that scope, deliberately — the absence of a row is the whole mechanism. */
export interface Client {
  id: string
  name: string
  /** The company-wide registry number, e.g. `CL-0042` — minted server-side at
   *  registration from the one `client` numbering series, never editable and
   *  never reused. Always present: the migration numbered every older client. */
  number: string
  /** Optional short code, usable as a numbering token (FR-39, FR-68). */
  code?: string
  taxId?: string
  registrationId?: string
  country: string
  address?: string
  /** The SALES CONTACT: the colleague responsible for this client. Starts as
   *  whoever registered it and is reassignable by a holder of
   *  `directory.client.manage`. Drives the server's row scope (AD-5). */
  ownerId: string
  /** Who registered the client. Unlike `ownerId`, it never changes. */
  createdById: string
  isActive: boolean
  contacts: DirectoryContact[]
  createdAt: string
  updatedAt: string
}

/* ── Clients page: the overview and the yearly statement ────────────────────
 * Composed at the root (cmd/asa/readmodel_clients.go), not served by the
 * directory module: the money is finance's and the cases are casefile's.
 * Money is present only for a caller who may read cross-case figures; the
 * server says why it is absent in `moneyUnavailable`. */

/** The year selector's value. `'all'` asks for every year. */
export type YearFilter = number | 'all'

export interface ClientManager {
  id: string
  name: string
}

/** Billed / received / refunded / balance / cost, USD at booked rates (AD-9). */
export interface ClientMoneySummary {
  billed: Money
  received: Money
  /** Money paid back to the client (S-22f) — the refunds' unallocated
   *  remainder. Always served. `received` and `balance` do not move by it: an
   *  overpayment never reaches the statement's «Получено», so a refund is not
   *  subtracted there. */
  refunded: Money
  balance: Money
  cost: Money
}

/** Why the money is missing: silently (no right) or with a banner (a read
 *  failed — typically a missing FX rate, AD-20). */
export type MoneyUnavailable = 'forbidden' | 'unavailable'

export interface ClientOverviewRow {
  id: string
  name: string
  code?: string
  taxId?: string
  country: string
  isActive: boolean
  /** The registry number — see `Client.number`. */
  number: string
  /** The sales contact (`Client.ownerId`), not a case manager. */
  ownerId: string
  /** The sales contact's name. Absent when the server could not resolve it. */
  ownerName?: string
  createdById: string
  /** Who registered the client. Absent when unresolved. */
  createdByName?: string
  /** Distinct owners of the year's CASES, current manager first — a different
   *  set from the client's sales contact above. Empty when the client has no
   *  case in the year. */
  managers: ClientManager[]
  /** Cases opened in the selected year. */
  cases: number
  /** Σ weight over the year's cases, decimal string in kg. */
  weightKg?: string
  /** Absent without the right, and for a client with no money fact at all. */
  money?: ClientMoneySummary
}

export interface ClientsOverview {
  /** `null` is "all years". */
  year: number | null
  /** Selectable years, newest first — from the oldest visible case to today. */
  years: number[]
  items: ClientOverviewRow[]
  totals?: ClientMoneySummary
  moneyUnavailable?: MoneyUnavailable
}

export interface StatementRowMoney extends ClientMoneySummary {
  /** The balance carried down the page: opening + Σ balance through this row. */
  running: Money
  lastReceivedOn?: string
}

export interface StatementRow {
  caseId: string
  number: string
  status: CaseStatus
  /** The receipt date — the case's `openedOn` (AD-19 date-only). */
  openedOn: string
  route: { loading: string; border: string; discharge: string; unloading: string }
  transportModeCode: string
  cargoDescription: string
  weightKg?: string
  volumeM3?: string
  /** Absent when the case names no method, or names one this build's
   *  `cases.method.*` catalogue does not know. */
  paymentMethod?: PaymentMethod
  eta?: string
  ata?: string
  /** ATA − openedOn in whole days; absent until arrival. */
  transitDays?: number
  ownerName: string
  /** Absent for a case with no money fact (a shipment nobody has priced). */
  money?: StatementRowMoney
}

export interface ClientStatement {
  client: Client
  /** The names behind `client.ownerId` and `client.createdById`, resolved at
   *  the root because the directory module has no people of its own. Absent
   *  when unresolved. */
  ownerName?: string
  createdByName?: string
  year: number | null
  years: number[]
  /** The balance of every case opened before the year (an explicit zero for
   *  "all years"). Absent without the right. */
  opening?: { balance: Money }
  /** Oldest first. */
  rows: StatementRow[]
  totals?: ClientMoneySummary & { weightKg?: string }
  moneyUnavailable?: MoneyUnavailable
}

/** What a counterparty is to us — chosen at creation, editable afterwards
 *  (S-22b, migration 0077's `counterparty_category`). A fixed set in code: the
 *  wire carries the CODE and the words are composed here
 *  (`counterpartyCategory.*`). */
export type CounterpartyCategory =
  | 'carrier'
  | 'forwarder'
  | 'customs-broker'
  | 'driver'
  | 'agent'
  | 'other'

/** One row of `GET /api/counterparties` — every company or person that
 *  provides us a service (S-22b).
 *
 * There is deliberately no `contacts` member: the list read carries none, not
 * even `[]`, so a list row cannot seed an edit form. Seeding one from the list
 * is how the old directory wiped a record's contacts on every save — the list
 * said "no contacts" and the save turned that into deletions. An edit seeds
 * from `Counterparty`, the single read.
 *
 * A caller without `counterparty.read` gets an empty list rather than a 403,
 * so a picker it mounts still renders (and keeps its stored value). */
export interface CounterpartyRow {
  id: string
  name: string
  category: CounterpartyCategory
  /** An ISO alpha-2 code, or free text stored before the field was a picker. */
  country: string
  /** «БИН» on the record page. Omitted when empty. */
  taxId?: string
  registrationId?: string
  isActive: boolean
}

/** One counterparty — `GET /api/counterparties/:id`: the row plus what only
 *  the single read serves. Without `counterparty.read` this read is a 404. */
export interface Counterparty extends CounterpartyRow {
  address?: string
  /** Who created the record. Read by no rule: the directory is the company's.
   *  '' for a record created by the system (S-22f: migration 0082's backfill
   *  creates counterparties with no owner). */
  ownerId: string
  contacts: DirectoryContact[]
}

/* ── Counterparty money: the statement and the list figures (S-22e) ────────
 * Composed at the root (cmd/asa/readmodel_counterparties.go), not served by
 * the directory: the works are casefile's and every figure is finance's.
 * Counted by the directory id only — a cost line or a payment that names the
 * counterparty by typed name alone is in none of these figures. USD at the
 * booked rate. Money is bounded by the reader's case-money reach (`scoped`)
 * and is absent with `moneyUnavailable` saying why. Nothing here is summed in
 * the browser: every total, the running balance and the average are served. */

/** A counterparty's figures over a set of works. «Остаток» (`balance`) is
 *  billed − paid; the advance — an outbound payment to it not yet allocated —
 *  is reported BESIDE them and is in neither (AD-11). */
export interface CounterpartyMoneyFigures {
  billed: Money
  paid: Money
  balance: Money
  advance: Money
}

/** One line of a work's expansion. A union on `kind`:
 *
 *  - `unit` — a unit that is the counterparty's: it carries it (`carried`, the
 *    supplier ledger's carrier rule) or one of its actual cost lines sits on it.
 *    `billed` is "0.00" and `settlement` absent when it has no actual there;
 *    both are absent when the money was not served.
 *  - `leg` — its actuals on a leg and on no unit, one per leg; the client says
 *    «Плечо N целиком».
 *  - `other` — its actuals on no leg and no unit.
 *  - `advance` — the work's unallocated outbound payments to it.
 *
 *  `leg`, `other` and `advance` exist only when the money was served. */
export type CounterpartyStatementItem =
  | {
      kind: 'unit'
      unitId: string
      legId: string
      /** The leg's 1-based place in the route. */
      legPosition: number
      unitKind: UnitKind
      /** The unit's number, else its trailer plate, else its container number;
       *  `''` when it has none. */
      number: string
      carried: boolean
      billed?: Money
      settlement?: Settlement
    }
  | { kind: 'leg'; legId: string; legPosition: number; billed: Money; settlement?: Settlement }
  | { kind: 'other'; billed: Money; settlement?: Settlement }
  | { kind: 'advance'; amount: Money }

/** One work of the counterparty's statement — the money is the row's, the
 *  running balance is the opening plus every row's «Остаток» through this one. */
export interface CounterpartyStatementRow {
  caseId: string
  number: string
  status: CaseStatus
  /** The case's opening date (AD-19 date-only) — the year it counts in. */
  openedOn: string
  route: { loading: string; border: string; discharge: string; unloading: string }
  /** How many of the counterparty's units the work has. */
  units: number
  money?: CounterpartyMoneyFigures & { running: Money }
  /** Units first in route order, then `leg`, `other`, `advance`. */
  items: CounterpartyStatementItem[]
}

/** The year's works (`totals`) and the works before it (`opening`). */
export interface CounterpartyStatementTotals extends CounterpartyMoneyFigures {
  units: number
  /** The CLOSING running balance — opening plus the year's «Остаток»; the
   *  «Остаток к оплате» tile. */
  balance: Money
  /** «Средняя ставка»: the counterparty's actuals on units over the units that
   *  carry one. Absent when no unit carries one. */
  averageRate?: Money
}

/** `GET /api/counterparties/:id/statement?year=`. */
export interface CounterpartyStatement {
  counterpartyId: string
  /** `null` is "every year". */
  year: number | null
  /** Years with a work, newest first; `[]` when the money is forbidden. */
  years: number[]
  /** True when the reader's case-money reach is narrower than the company's —
   *  the figures are over their own works («Цифры по вашим работам»). */
  scoped: boolean
  moneyUnavailable?: MoneyUnavailable
  /** «Остаток прошлого периода» — an explicit zero for every year. */
  opening?: { balance: Money }
  totals?: CounterpartyStatementTotals
  /** Oldest first. `[]` when the money is forbidden. */
  rows: CounterpartyStatementRow[]
}

/** One counterparty's all-time figures on the list. */
export interface CounterpartyMoneyItem extends CounterpartyMoneyFigures {
  counterpartyId: string
  units: number
}

/** `GET /api/counterparties/money`: an item per counterparty with a work in
 *  the reader's reach. A counterparty with none has no item. */
export interface CounterpartiesMoney {
  scoped: boolean
  moneyUnavailable?: MoneyUnavailable
  items: CounterpartyMoneyItem[]
}

/** The two offices. Recorded on every account because it defaults a record's
 *  issuing entity later, which is why it is a closed set rather than free text. */
export type Office = 'Ashgabat' | 'Yiwu'

/** Directory-ownership grants (FR-44).
 *
 *  All three keys are always present, false included: the UI indexes them
 *  directly, so an absent key would read as undefined rather than as "no".
 *
 *  These were columns until migration 0021 turned them into ordinary
 *  capabilities. The object is now DERIVED from the capability set on the
 *  server, so it cannot disagree with what is enforced. */
export interface Grants {
  manageClients: boolean
  manageParticipants: boolean
  manageTransport: boolean
}

/** An account as the Super-admin sees it — `identity.AdminUser` on the wire.
 *
 * Wider than CurrentUser because user management is the screen where office,
 * active state and the audit timestamps are the point. There is no password
 * field in any direction: the server generates credentials and returns one
 * exactly once, at creation or reset (AD-6). */
export interface DirectoryUser {
  id: string
  name: string
  email: string
  role: Role
  office: Office
  language: 'en' | 'ru'
  grants: Grants
  /** Everything the account may do: its role's capabilities and its own. */
  capabilities: Capability[]
  /** What was granted to THIS ACCOUNT specifically, which is the subset the
   *  edit form owns. Separate from `capabilities` so the form never presents a
   *  role-derived permission as the account's own — ticking a box that is
   *  already true by role would silently pin it if the role later lost it. */
  userCapabilities: Capability[]
  /** Whether THIS CALLER may act on the account — edit it, reset its password,
   *  (de)activate it (S-7, O7). False when the account outranks the caller: a
   *  superuser account, or capabilities or row scopes the caller does not hold.
   *  The server refuses those acts 403 `forbidden` (held_by `super-admin`)
   *  whatever this says; the list uses it to explain the refusal up front. */
  manageable: boolean
  isActive: boolean
  /** True while the account still holds a credential somebody else generated. */
  mustChangePassword: boolean
  createdAt: string
  updatedAt: string
}

/** The answer to creating an account or resetting a password.
 *
 * `generatedPassword` is readable exactly once, here. It is not stored in
 * recoverable form and no read endpoint reproduces it — losing it before
 * handing it over means issuing a reset, not looking it up. */
export interface CreatedUser {
  user: DirectoryUser
  generatedPassword: string
  /** True only when the credential mail was QUEUED to the owner's own address
   *  through a live sender. False for no mailer, the log sender, a failed
   *  enqueue, or a non-production redirect to the test address — in every one
   *  of those the owner receives nothing, so the dialog must say to hand the
   *  password over in person. Queued, not delivered: true never means "arrived". */
  credentialMailed: boolean
}

/** One entry in the modes list or the equipment list — `directory.TransportRefDTO`.
 *
 * Two lists, not one: the source workbook conflated "sea" with "40ft HC", so
 * choosing a mode and choosing a container were the same dropdown (FR-43).
 *
 * A deactivated entry no longer appears when choosing transport on a new case,
 * while records that already reference it keep displaying it unchanged —
 * deactivation hides an option going forward and never rewrites history. */
export interface TransportRef {
  id: string
  kind: 'mode' | 'equipment'
  label: string
  code: string
  isActive: boolean
  sortOrder: number
}

/* ── Settings ─────────────────────────────────────────────────────────────── */

/** One legal entity the company issues paper from — `settings.EntityAdmin`.
 *
 * `bankDetails` is present only on this Super-admin shape. Every other surface
 * receives `EntityRef`, which omits it (AD-6). */
export interface IssuingEntity {
  id: string
  name: string
  /** Expands `{INITIALS}` in a numbering pattern, e.g. `AB`. */
  initials: string
  /** Expands `{OFFICE}`, e.g. `YW`. */
  officeCode: string
  /** The office whose records default to this entity (FR-24); '' if none. */
  defaultOffice: Office | ''
  legalFooter: string
  bankDetails: string
  emailSignature: string
  /** The COLOURWAY of the wordmark, not an asset reference — `settings/logo.go`
   *  states the division of labour between this and `logo` below. It selects how
   *  the entity's name is drawn for an entity that has uploaded no mark, which is
   *  every entity until somebody does. */
  logoVariant: string
  /** The uploaded mark (FR-64, migration 0041).
   *
   *  Always present, never optional, even when there is no logo: `{has: false}`
   *  is a fact the screen can branch on, where a missing key would be
   *  indistinguishable from an older server that does not know about logos. */
  logo: EntityLogo
  isActive: boolean
  createdAt: string
  updatedAt: string
}

/** An issuing entity's uploaded logo — `settings.LogoView`.
 *
 * `has` rather than inferring presence from a non-empty `url`, because the two
 * differ in exactly the case worth distinguishing: an entity that HAS a logo whose
 * short-lived URL could not be signed. The screen then shows the wordmark — which
 * is what the document will print — instead of an upload prompt for a mark that is
 * already there.
 *
 * The object KEY is deliberately absent. It is carried on the upload ticket, which
 * is minted for one entity's own prefix and must make the round trip so the commit
 * can re-verify it; a key on a read shape would be a key to probe a bucket with
 * (AD-18). A settings anonymity gate asserts the omission. */
export interface EntityLogo {
  has: boolean
  /** Short-lived, signed per request (five minutes). Absent when there is no
   *  logo, and absent when signing failed — see `has`. */
  url?: string
  contentType?: string
  byteSize?: number
}

/** An entity as every non-admin surface sees it. No bank details, by omission
 *  rather than by blanking. */
export interface IssuingEntityRef {
  id: string
  name: string
  initials: string
  officeCode: string
}

/** The five things that carry a minted number (AD-12).
 *
 * Deliberately NOT `DocumentKind`, which is finance's set of issued papers
 * (invoice, debit note, offer). The two overlap without matching: a quote and a
 * case are numbered but are not documents, and an offer is a document that
 * carries its quote's number rather than one of its own (AD-25).
 *
 * `client` is the odd one: its series belongs to the whole company rather than
 * to an issuing entity — see `NumberingScope`. */
export type NumberedRecordKind = 'quote' | 'case' | 'invoice' | 'debit-note' | 'client'

/** Whose series a scheme numbers. `entity` is one per issuing entity (quote,
 *  case, invoice, debit note); `company` is one series for the whole company,
 *  which today is the client registry alone. */
export type NumberingScope = 'entity' | 'company'

/** One row of the numbering screen — `settings.SchemeView`.
 *
 * Every (entity, kind) pair appears whether or not a pattern has been set, and
 * so does every company-wide kind: a screen listing only what is already
 * configured cannot be used to set the rest, and the defaults are in force
 * either way. */
export interface NumberingScheme {
  scope: NumberingScope
  /** `null` for a company-wide scheme, which has no issuing entity. */
  entityId: string | null
  /** Empty for a company-wide scheme. */
  entityName: string
  documentKind: NumberedRecordKind
  /** Token pattern expanded at mint time; the expansion is stored (AD-12). */
  pattern: string
  /** False when no row exists and the kind's default is in force. */
  configured: boolean
  /** What the next number would be if one were minted now. Empty when a stored
   *  pattern no longer parses — the screen that fixes it must still render. */
  example: string
  nextValue: number
}

/** One block a new quote is seeded with, as `GET /settings/templates/standing-defaults`
 *  serves it (`settings.StandingDefaultDTO`, N-19).
 *
 *  These are NOT rows of the template library above: the wording is built into
 *  the product (`quote.DefaultBlocks`), one set per language, and a new quote
 *  receives the set for its creator's interface language. `source` says where it
 *  lives (`built-in` today) and `editable` whether Settings may change it
 *  (`false` today) — both are sent so the screen reads facts rather than
 *  hard-coding them. Never `cost-item`: that kind is a costing preset, not text
 *  a quote prints. */
export interface StandingDefault {
  kind: 'included' | 'not-included' | 'disclaimer'
  language: 'ru' | 'en'
  /** 1-based, within its kind and language. */
  position: number
  body: string
  source: string
  editable: boolean
}

export interface DocumentTemplate {
  id: string
  /* Four kinds, and only three of them ever print (migration 0042).
   *
   * `not-included`, `disclaimer` and `included` are the reusable text behind a
   * quote's own blocks — same strings as QuoteBlockKind above, because
   * inserting one COPIES it across.
   *
   * `cost-item` is the odd one and deliberately so: a standing charge-line
   * label a costing starts from ("China mainland transportation"), maintained
   * on the same Settings screen because a maintained label with an optional
   * remark is exactly what this table already is. It is never printed on an
   * offer and carries no money — a rate stored here would be read next quarter
   * as though it were current — which is also why the server accepts a blank
   * `body` for this kind alone. */
  kind: 'not-included' | 'disclaimer' | 'included' | 'cost-item'
  label: string
  body: string
  /** The issuing entity this template belongs to, or `null` for company-wide.
   *  Optional on the type because the quote's insertion picker reads the same
   *  rows and does not care whose they are. */
  entityId?: string | null
}

/** The three thresholds Story 2.9 requires to be editable in company settings
 *  rather than hard-coded (FR-16's two reminder days, FR-52's stuck days).
 *
 *  The defaults travel with the values so the panel can show what a number
 *  WOULD be, and `configured` is the server's answer to "has anybody saved
 *  these" — deliberately not inferred by comparing the two, because a founder
 *  who saves the default values has still configured them. */
export interface CompanyThresholds {
  followUpDays: number
  validityWarningDays: number
  stuckShipmentDays: number
  configured: boolean
  defaultFollowUpDays: number
  defaultValidityWarningDays: number
  defaultStuckShipmentDays: number
}

/** A named route kept in Settings — «Хоргос → Ашхабад (через Сарахс)» — as an
 *  ordered list of points, each with a country and a normative transit time.
 *
 *  Applied to a case leg BY COPY, the same rule `DocumentTemplate` follows and
 *  for the same reason: editing a template must never move a shipment that is
 *  already running. Nothing on a case will hold a `directionTemplateId`, so
 *  there is deliberately no field here to reach one from either. */
export interface DirectionTemplate {
  id: string
  name: string
  /** A switched-off direction stays maintainable in Settings and disappears
   *  from the picker — deactivation rather than deletion, because a retired
   *  corridor is still the route a finished case took. */
  active: boolean
  points: DirectionPoint[]
}

export interface DirectionPoint {
  id: string
  /** 1-based and allocated by the SERVER from the order the points were
   *  submitted in, the same way a leg's units are — the client sends a
   *  list, not positions it would have to renumber after every removal. */
  position: number
  name: string
  /** ISO 3166-1 alpha-2, or `''` when unset. */
  country: string
  kind: RoutePointKind
  /** Normative days for the transition FROM THE PREVIOUS POINT. `null` on the
   *  first point, which has no inbound transition — the server enforces that
   *  correspondence in both directions. */
  daysFromPrevious: number | null
}

/* ── Dashboard ────────────────────────────────────────────────────────────── */

export interface AttentionItem {
  id: string
  /** The server computes `minus` and `stuck` rows only (S-2b.2): the rate
   *  approvals and the payment-confirmation queue moved to «Мои задачи», in
   *  the strip above the dashboard. A rolled-back server may still send
   *  `approval` or `payment-confirmation`; `AttentionList` skips any kind it
   *  has no tone for rather than drawing it unlabelled. The other three kinds
   *  are the mockup's and nothing serves them yet. */
  kind: 'stuck' | 'minus' | 'overdue' | 'checklist' | 'quote-followup'
  recordNumber: string
  recordKind: 'case' | 'quote' | 'invoice'
  title: string
  detail: string
  amount?: Money
  href: string
}

export interface ActivityEntry {
  id: string
  label: string
  meta: string
  state: 'done' | 'current' | 'pending'
}

/** A breakdown row under a KPI tile — a route, an entity, an aging bucket. */
export interface BreakdownRow {
  id: string
  label: string
  icon?: string
  value: string
  changePercent?: string
  sharePercent?: string
  tone?: 'default' | 'positive' | 'negative' | 'attention'
}

export interface KpiTile {
  label: string
  value: string
  currency?: string
  deltaLabel?: string
  deltaTone?: 'positive' | 'negative' | 'attention' | 'neutral'
  series?: number[]
  breakdown?: BreakdownRow[]
  href?: string
  hrefLabel?: string
}

/** A money tile the server cannot compute yet, and the story that unblocks it.
 *
 *  This type exists so the screen can say "not yet" instead of implying a
 *  number. The two alternatives are both dishonest: omitting a tile makes the
 *  dashboard look finished, and rendering a zero gives the founder a FIGURE to
 *  act on. The server names what it withheld and why (readmodel.go). */
export interface PendingFigure {
  key: 'turnover' | 'profit' | 'forecastProfit' | 'toCollect' | 'toPay'
  story: string
}

/** The keys `pending` can name, as a value the screen can iterate. Derived from
 *  the type so the two cannot drift.
 *
 *  KEYS STAY IN THE UNION AFTER THEIR TILE TURNS ON, and the two are not in
 *  conflict: the union is what the server MAY name, and it no longer names
 *  `forecastProfit`, `toCollect`, `toPay` or — since Story B7 — `turnover`.
 *  Narrowing it as each figure landed
 *  would make a rolled-back deployment's honest "pending" a type error on the
 *  client that has to render it — and the client renders any key it finds here as
 *  unavailable, which is exactly the right behaviour for a server that has gone
 *  backwards. `profit` joined the union when it became the tile FR-51 AC 2
 *  describes; it is served rather than pending today. */
export const PENDING_FIGURE_KEYS = [
  'turnover',
  'profit',
  'forecastProfit',
  'toCollect',
  'toPay',
] as const satisfies readonly PendingFigure['key'][]

export interface DashboardSummary {
  counters: {
    activeCases: number
    shipmentsThisMonth: number
    /** Against the previous month. ABSENT when that month had none — a delta
     *  against zero is arithmetic nobody can do, and rendering it as 0 says
     *  business is flat when it has just started. Signed: it can be negative. */
    shipmentsDelta?: number
    /** FR-51's profit tile, composed per AC 2: an OPEN case contributes its
     *  forecast figure and a FINANCIALLY CLOSED one contributes its final
     *  figure, so the tile respects the two-stage close.
     *
     *  This is the figure the tile renders. `forecastProfit` below is its
     *  open-cases component, kept for a reader who wants the estimate without
     *  subtracting — the two are different numbers whenever any case has been
     *  financially closed. */
    profit?: Money
    /** Σ forecast profit over every case carrying a money fact (FR-30, Story
     *  4.6). ABSENT rather than zero when the server could not compute it —
     *  the same distinction `shipmentsDelta` draws, and for the sharper
     *  reason: a founder reading "$0 forecast profit" acts on it. */
    forecastProfit?: Money
    /** Σ receivable still owed, booked (AD-9, Story 6.3's basis).
     *
     *  Optional for the same reason as the two above, and it stays optional
     *  after the tile is turned on: the server names what it could not compute
     *  in `pending`, and a field typed as always-present is a tile the screen
     *  renders unconditionally. Where this is present, `toCollect` is gone
     *  from `pending` — the two never both speak about one figure. */
    toCollect?: Money
    /** Σ payable still owed, booked. Same basis, same predicate as the
     *  payables list it drills through to (AD-9, UX-DR-10) — a tile and its
     *  destination that disagree is worse than no tile. */
    toPay?: Money
    /** What the company BILLED this calendar month to date (FR-51, Story B7):
     *  every issued client invoice and every issued debit note owed by a
     *  client, dated by the document's own date, over the caller's reachable
     *  cases, at booked USD (AD-9).
     *
     *  NET OF VAT — the receivable behind `toCollect` is gross, so the two do
     *  not reconcile and must not be made to. ABSENT, never zero, when the
     *  server's read failed (readmodel.go `turnoverTile`); the definition is
     *  docs/llm-wiki/components/money-and-fx.md#turnover. */
    turnover?: Money
  }
  attention: AttentionItem[]

  /** The FR-52 movement threshold this response was computed with, echoed so
   *  the widget can say "no movement for 3 days" without the client hard-coding
   *  a number the server owns. */
  stuckDays: number

  /** When the COMPOSER RAN, generated server-side (NFR-2, UX-DR-16).
   *
   *  Never stamped from the browser's clock. A laptop resumed from sleep
   *  resolves a cached response and would stamp it "now" — the stale tab
   *  mistaken for a live one, which is the precise failure the criterion
   *  names. */
  readAt: string

  /** Money tiles the server could not compute. Always an array; EMPTY since
   *  Story B7 took `turnover` out of it, and empty means the dashboard is
   *  complete. The client still renders any key it finds here as unavailable —
   *  a rolled-back server's honest answer (PENDING_FIGURE_KEYS). */
  pending: PendingFigure[]

  /* Note what is NOT here: activity. It and turnover were always-present
     fields when this surface was served from fixtures, and removing them was
     deliberate — a tile typed as always-present is a tile the screen renders
     unconditionally, which is how a fixture becomes a number somebody trusts.

     `profit`, `forecastProfit`, `toCollect`, `toPay` and `turnover` have since
     come back on `counters`, OPTIONAL rather than required, as each story
     landed the read behind it. Optional is the shape that survives a server
     that computed some of them and named the rest in `pending` — or, for
     `turnover`, left it absent because its read failed. `turnover` was the
     last to return (Story B7, 2026-10-09): billed revenue at booked values,
     read over issued invoices and client debit notes once migration 0048 let
     an invoice be issued. The approved rate was never a substitute — that is
     contracted value, and serving it under this name would be a different
     number wearing it.

     `activity` comes back with a cross-record read of the action log
     (migration 0026) if the dashboard ever asks for one — FR-51 lists six
     figures and three needs-action lists, and a feed is none of them. */
}

/* ── Notifications ────────────────────────────────────────────────────────── */

/** What happened. Drives the row's icon and label, so the event type is never
 *  carried by colour alone. */
export type NotificationKind =
  | 'message'
  | 'status'
  | 'approval'
  | 'payment'
  /** A cost line recorded, amended or removed on a case — the case's costs
   *  moved, no money did (N-28). */
  | 'expense'
  /** The price request's life on a quote: asked, answered, asked again. */
  | 'pricing'
  | 'document'
  | 'file'
  | 'assignment'
  | 'stuck'

export interface Notification {
  id: string
  kind: NotificationKind
  /** The outbox topic this row came from (`case.assigned`), kept beside the
   *  icon `kind` it maps to because `kind` folds several topics into one
   *  icon. The drawer translates a title by topic AND text (QW-1).
   *
   *  NOT a wire field: the server sends the topic as `kind`, and
   *  `notificationFromWire` copies it here. Every real row carries one. */
  topic: string
  recordKind: 'case' | 'quote' | 'invoice'
  recordNumber: string
  /** The record's own title, repeated on every event so a group can name
   *  itself from any one of its notifications. */
  recordTitle: string
  /** The server's English, composed at publish time — the FALLBACK since story
   *  B5, printed only when `code` is empty or unknown to this build. */
  title: string
  detail: string
  /** The message code (story B5): a key of `backend/platform/outbox/inapp_codes.go`,
   *  worded by `features/notifications/notificationMessages.ts`. Always sent;
   *  `""` for a row written before codes existed. */
  code: string
  /** The values `code` is worded from — status codes, a role key, an ISO date,
   *  a decimal amount with its currency, a count, a name. Strings only; always
   *  an object, `{}` when the code has none. */
  params: Record<string, string>
  /** An event instant, not a date-only value — rendered in the viewer's zone. */
  at: string
  read: boolean
  href: string
}

/** Events grouped by the record they concern (FR-49). The bell panel never
 *  shows a flat chronological list: a case with six updates is ONE thing to
 *  read, not six. */
export interface NotificationGroup {
  recordKind: Notification['recordKind']
  recordNumber: string
  recordTitle: string
  href: string
  /** Reverse-chronological within the group. */
  items: Notification[]
  unreadCount: number
  /** The most recent event's instant — what the group sorts on. */
  latestAt: string
}

export interface NotificationFeed {
  items: Notification[]
  unreadCount: number
}

/** A role as the roles screen reads it — `identity.RoleDTO` on the wire. */
export interface RoleRecord {
  key: Role
  name: string
  description: string
  /** True for the five the product ships with. They are fully editable, but
   *  cannot be deleted or re-keyed: the product's own documentation, fixtures
   *  and 403 bodies name them. */
  isSystem: boolean
  /** True for the one role that passes every check. Its capability list is not
   *  editable — the flag is what grants access, so an edit would be a no-op
   *  that looked like it worked. */
  isSuperuser: boolean
  capabilities: Capability[]
  /** Row visibility per surface. A surface ABSENT from this map means no access
   *  to it at all — never "own". `own` is a real, narrower level, and treating
   *  a missing key as `own` would hand a filtered view of a surface to a role
   *  that should not reach it. */
  scopes: Record<string, ScopeLevel>
  /** How many accounts hold this role, so the screen can explain a refused
   *  delete before the founder tries it. */
  userCount: number
  /** Whether THIS CALLER may give the role to an account (S-7, O7): every
   *  capability and row scope it carries is one the caller holds. The superuser
   *  role is assignable by the superuser alone. The server refuses an
   *  unassignable pick with `exceeds-authority` on `role` regardless. */
  assignable: boolean
}

/** How much of a surface a role may see.
 *
 * `assigned` is strictly wider than `own` and never narrower: somebody who owns
 * a record they are not on the team of must still see it. */
export type ScopeLevel = 'own' | 'assigned' | 'all'

/** One capability the server registers, with the text the role editor shows. */
export interface CapabilityDescriptor {
  key: Capability
  description: string
}

/** The role editor's vocabulary — GET /api/admin/capabilities.
 *
 * Served by the server rather than hard-coded here, because the server's
 * registry is what actually gets checked: a capability this client invented
 * would be a permission that does nothing. */
export interface CapabilityCatalogue {
  items: CapabilityDescriptor[]
  surfaces: string[]
  levels: ScopeLevel[]
}

/** A role as every signed-in user may read it — GET /api/roles (story B3).
 *
 *  Mirrors identity.RoleName in backend/internal/identity/dto.go: the key and
 *  the display name somebody typed, and deliberately nothing about what the
 *  role may do or see. Exists so a role created on the roles screen is named
 *  rather than keyed on every team card. */
export interface RoleNameEntry {
  key: Role
  name: string
}

/** The write shape for creating or amending a role.
 *
 * `capabilities` and `scopes` are whole sets, never deltas: the form submits
 * the entire record, so a missing key is a REVOCATION. An add/remove language
 * would make "revoke this" and "leave it alone" indistinguishable, which is the
 * bug that silently re-grants a permission somebody just removed. */
export interface RoleChange {
  key: Role
  name: string
  description: string
  capabilities: Capability[]
  scopes: Record<string, ScopeLevel>
}

/* «Мои задачи» — GET /api/my-tasks (S-2b.1, S-2b.2).
 *
 * Mirrors the composer view in backend/cmd/asa/readmodel_tasks.go. A section is
 * PRESENT when the caller holds its gate (capability AND scope AND "mine", all
 * decided in SQL; for the two finance sections, two capabilities), even with no
 * items, and ABSENT when they do not — so the page renders exactly what
 * arrives, in the order it arrives, and decides nothing about who sees what. */

/** The eight record sections: each item is a quote or a case the caller
 *  works. `expiring-soon` (FR-16) is a sent quote whose validity ends within
 *  the warning window; its items carry `validUntil` and no `since`. */
export type MyTaskRecordSectionKey =
  | 'pricing-requested'
  | 'pricing-ready'
  | 'awaiting-client'
  | 'expiring-soon'
  | 'case-no-legs'
  | 'case-no-vehicle'
  | 'case-no-actuals'
  | 'case-truck-double-count'

/** The section keys this client knows how to title. The wire may carry a key
 *  a later server added; `myTasksFromWire` drops it rather than render a
 *  card with no name. The two finance sections (S-2b.2) are served after the
 *  seven record ones. */
export type MyTaskSectionKey = MyTaskRecordSectionKey | 'rate-approvals' | 'payments-to-confirm'

/** One record waiting on the caller. Exactly the fields the server's per-kind
 *  allowlist pins for `quote` and `case` (`TestAnonMyTasksItemCarriesOnlyItsDeclaredFields`):
 *  no carrier, vendor or plate, and no free text. The wording is composed here
 *  from the section key (FR-2). */
export interface MyTaskRecordItem {
  kind: 'quote' | 'case'
  /** The quote's or the case's id — the case's even on a leg item. */
  id: string
  number: string
  /** Always served on a record item (the wire field is `omitempty` only
   *  because an approval item carries none). */
  clientName: string
  /** Since when it has waited: an RFC 3339 instant. Absent when the server
   *  has no date for it (a leg with no creation stamp), and never served on
   *  `expiring-soon`, whose date is the end rather than the start. On
   *  `pricing-ready` it is the LATER of when the price was ready and when the
   *  quote was last sent, so a reopened quote reads from that send. */
  since?: string
  /** Only on `expiring-soon`: the last day the offer stands, date-only
   *  (`YYYY-MM-DD`, AD-19) — formatted with `format.date`, never as an
   *  instant, or west of UTC it reads a day early. */
  validUntil?: string
  /** On `case-no-vehicle`: the ROAD leg that has no truck. On
   *  `case-truck-double-count` (S-12): the case's first flagged leg. */
  legId?: string
}

/** One rate-change request waiting on the caller's decision (S-2b.2), from the
 *  same queue the Finance page's approvals tab reads. Exactly the six fields
 *  the server's allowlist pins for `approval`: no `reason` (restricted free
 *  text, AD-6, which stays on the approvals tab), no client and no case id. */
export interface MyTaskApprovalItem {
  kind: 'approval'
  /** The REQUEST's id, not the case's. */
  id: string
  /** The case's number. */
  number: string
  /** When it was requested: an RFC 3339 instant; the server sorts oldest first. */
  since: string
  /** Absent when the server has no name for the requester. */
  requesterName?: string
  /** The requested change, signed, in the request's own currency — one
   *  request's figure, never a sum across requests (AD-9). */
  amount: Money
}

export type MyTaskItem = MyTaskRecordItem | MyTaskApprovalItem

/** The common shape of every section: `count` is the real total, and `items`
 *  stops at 50 — the difference is «и ещё N». Never null on the wire. */
interface MyTaskSectionOf<K extends MyTaskSectionKey, I> {
  key: K
  count: number
  items: I[]
}

/** A section, keyed by `key`: the key decides which item kind it may hold, and
 *  `myTasksFromWire` drops an item that does not match. Record sections list
 *  oldest `since` first, undated last by number. `payments-to-confirm` is a
 *  COUNT ONLY — present at 0, never an item (no counterparty, no amount). */
export type MyTaskSection =
  | MyTaskSectionOf<MyTaskRecordSectionKey, MyTaskRecordItem>
  | MyTaskSectionOf<'rate-approvals', MyTaskApprovalItem>
  | MyTaskSectionOf<'payments-to-confirm', never>

export interface MyTasks {
  /** The one instant the response was stamped with, server-side; the date
   *  cut-offs (awaiting the client's answer) were computed from its day. */
  readAt: string
  sections: MyTaskSection[]
}
